/**
 * ⚠️ AGENDA PAGE IS GEANKERD
 * ============================================
 * Dit bestand is structureel vastgezet op 2026-01-21.
 * Wijzigingen alleen na expliciete review.
 * 
 * ANKER-REGELS:
 * 1. GEEN imports van: focus/, journey/, capabilities/, analysis/
 * 2. GEEN automatische writes - alleen expliciete user actions
 * 3. GEEN onbekende drag payloads verwerken
 * 4. Types gedefinieerd in: src/types/agenda.types.ts
 * 
 * @stable
 * @anchored 2026-01-21
 */

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { db } from '../firebase';
import { useGoalContext } from '../hooks/useGoalContext';
import PageHeader from './ui/PageHeader';
import ScrollIndicator from './ui/ScrollIndicator';
import ExpertStatusWidget from './agenda/ExpertStatusWidget';
import ConnectionSuggestions from './agenda/ConnectionSuggestions';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  getDocs, 
  addDoc, 
  updateDoc, 
  doc, 
  deleteDoc,
  onSnapshot,
  deleteField,
  serverTimestamp,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { User } from 'firebase/auth';
import { UserProfile } from '../types';
import { connectGoogleCalendar, loadGoogleIdentityServices } from '../utils/googleCalendar';
import { connectOutlookCalendar, syncOutlookCalendar, disconnectOutlookCalendar } from '../utils/outlookCalendar';
import { 
  ChevronLeft, 
  ChevronRight, 
  MessageSquare, 
  Minimize2, 
  Maximize2, 
  Edit3, 
  Trash2, 
  Save, 
  X, 
  Plus,
  Calendar,
  Mic,
  Users,
  Settings,
  RefreshCw,
  Mail,
  Eye,
  EyeOff,
  Clock,
  MapPin,
  UserPlus,
  CheckCircle,
  Circle,
  Target,
  GoalIcon,
  DocumentIcon,
  AnalyticsIcon,
  ChatIcon,
  EditIcon,
  SuccessIcon,
  FireIcon,
  TipIcon,
  TimeIcon,
  PlayIcon,
  PauseIcon,
  FolderIcon,
} from './icons';
import { DoelstellingenPage } from './Doelstellingen/Milestone_organizer';
import TeamPage from './TeamPage';
import { IkigaiPage } from './Doelstellingen/purpose';
import { 
  ACTION_PRIORITY_CONFIG, 
  ACTION_TYPE_CONFIG,
  ActionPriority,
  ActionType as ActionTypeEnum
} from './Doelstellingen/Milestone_organizer/types';
import { sortTasks, getSmartSortMode, SortMode } from '../services/taskSortingService';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Fashion Mint Sage Theme — verwijder deze import + class op root div om te reverten
import '../styles/mintSageTheme.css';

// Geankerde types en validators
import {
  parseDragData,
  isDoelstellingenPayload,
  isAgendaTaskPayload,
  DRAG_TYPE_DOELSTELLINGEN,
  DRAG_TYPE_AGENDA_TASK,
  type DragPayload,
} from '../types/agenda.types';

interface CalendarEvent {
  id: string;
  title: string;
  description?: string;
  startTime: Date;
  endTime: Date;
  type: 'podcast' | 'meeting' | 'call' | 'personal' | 'planning' | 'google' | 'networking';
  category?: 'work' | 'friends' | 'family' | 'activity' | 'action';
  domain?: 'work' | 'private';
  // Bron van het event voor multi-calendar kleurindicatie
  // 'superkonnected' (intern), 'google', 'outlook', 'ics', etc.
  source?: string;
  participants: string[];
  participantNames: string[] | { [uid: string]: string }; // Array (legacy) or object (new format)
  location?: string;
  meetingUrl?: string;
  status: 'scheduled' | 'confirmed' | 'cancelled' | 'completed';
  createdBy: string;
  createdAt: Date;
  // Reminder settings
  reminderEnabled?: boolean;
  reminderMinutesBefore?: number;
  reminderAt?: Date | null;
  reminderSent: boolean;
  color?: string;
  recurring?: {
    frequency: 'daily' | 'weekly' | 'biweekly' | 'monthly';
    endDate?: Date;
    excludedDates?: Date[]; // specifieke dagen uit de reeks verwijderen
  };
  // Networking settings
  autoAcceptNetworking?: boolean; // Automatisch planning vullen voor "Vrij voor netwerken"
  // Meeting change requests
  pendingChangeRequest?: {
    id: string;
    fromUserId: string;
    newStartTime: Date;
    newEndTime: Date;
    reason: string;
    status: 'pending' | 'accepted' | 'declined';
  };
  // Connection flow linkage
  connectionId?: string;
  phase?: 'call' | 'video' | 'physical';
  lockedToConnectionFlow?: boolean;
  // Doelstellingen linkage
  doelstellingenMeta?: {
    actionId: string;
    goalId: string;
    milestoneId?: string;
    ghostDates?: string[];
    lastShiftedDate?: string;
  };
  // Task linkage (NEW)
  linkedTaskId?: string;
  linkedTaskMeta?: {
    listId: string;
    listName: string;
    balanceCategory?: string;
  };
}

const isLockedConnectionEvent = (e: CalendarEvent) =>
  !!e.connectionId || e.lockedToConnectionFlow === true;

const isVisibleEvent = (e: CalendarEvent, showCompleted: boolean, showCancelled: boolean) => {
  if (e.status === 'completed') return showCompleted;
  if (e.status === 'cancelled') return showCancelled;
  return true;
};

type ActionOriginBadge = {
  label: React.ReactNode;
  color: string;
  bg: string;
};

const ACTION_ORIGIN_BADGES: Record<string, ActionOriginBadge> = {
  sparring: { label: <ChatIcon size={10} />, color: 'text-purple-600', bg: 'bg-purple-100' },
  capability: { label: <AnalyticsIcon size={10} />, color: 'text-green-600', bg: 'bg-green-100' },
  milestone_generated: { label: <GoalIcon size={10} />, color: 'text-[#4F6660]', bg: 'bg-stone-100' },
  intake: { label: <DocumentIcon size={10} />, color: 'text-blue-600', bg: 'bg-blue-100' },
  manual: { label: <EditIcon size={10} />, color: 'text-gray-600', bg: 'bg-gray-100' }
};

const toLocalDateString = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const parseRecurring = (raw: any): CalendarEvent['recurring'] | undefined => {
  const toDate = (value: any): Date | undefined => {
    if (!value) return undefined;
    return value.toDate ? value.toDate() : new Date(value);
  };

  if (raw?.recurring?.frequency) {
    const excludedRaw = Array.isArray(raw.recurring.excludedDates)
      ? raw.recurring.excludedDates
      : undefined;
    return {
      frequency: raw.recurring.frequency,
      endDate: toDate(raw.recurring.endDate),
      excludedDates: excludedRaw
        ? excludedRaw.map((d: any) => toDate(d)).filter(Boolean) as Date[]
        : undefined
    };
  }

  const legacyFrequency = raw?.repeat;
  if (legacyFrequency && legacyFrequency !== 'none') {
    const excludedRaw = Array.isArray(raw?.excludedDates) ? raw.excludedDates : undefined;
    return {
      frequency: legacyFrequency,
      endDate: toDate(raw?.repeatEndDate),
      excludedDates: excludedRaw
        ? excludedRaw.map((d: any) => toDate(d)).filter(Boolean) as Date[]
        : undefined
    };
  }

  return undefined;
};

// Helper: Get event display title with correct participant name
const getEventDisplayTitle = (event: CalendarEvent, currentUserId?: string) => {
  // For networking events with participantNames object, show the OTHER person's name
  if (event.type === 'networking' && event.participantNames && typeof event.participantNames === 'object' && !Array.isArray(event.participantNames)) {
    if (currentUserId && event.participants?.includes(currentUserId)) {
      const otherUid = event.participants.find((uid: string) => uid !== currentUserId);
      if (otherUid && event.participantNames[otherUid]) {
        return `Netwerken met @${event.participantNames[otherUid]}`;
      }
    }
  }
  // Default: use stored title
  return event.title;
};

const openInConnectieFlow = (event: CalendarEvent) => {
  if (!event.connectionId) return;
  window.location.hash = '#connecties';
  window.dispatchEvent(
    new CustomEvent('openConnectionFlowForConnection', {
      detail: { connectionId: event.connectionId, mode: 'agenda' }
    })
  );
};

type BalanceCategory = 'work' | 'personal' | 'self' | 'essential' | 'none';

export interface AgendaTaskList {
  id: string;
  name: string;
  isSystem?: boolean; // 'Klaar' mag niet verwijderd worden
  balanceCategory?: BalanceCategory; // Voor integratie met levensbalans
}

export interface AgendaTask {
  id: string;
  title: string;
  note?: string;
  listId: string;
  originalListId?: string; // Behoudt oorspronkelijke categorie na voltooien
  completed: boolean;
  completedAt?: Date | null;
  createdAt: Date;
  // Timer tracking
  activeTime?: number; // Totale actieve tijd in milliseconden
  timerStartedAt?: Date | null; // Wanneer timer gestart
  isTimerRunning?: boolean; // Timer actief?
}

interface AvailabilitySlot {
  id: string;
  userId: string;
  dayOfWeek: number; // 0 = Sunday, 1 = Monday, etc.
  startTime: string; // "09:00"
  endTime: string; // "17:00"
  isAvailable: boolean;
  timezone: string;
}

interface PodcastSession {
  id: string;
  title: string;
  description: string;
  host: string;
  guests: string[];
  scheduledDate: Date;
  duration: number; // minutes
  category: string;
  isPublic: boolean;
  maxParticipants: number;
  currentParticipants: number;
  status: 'planned' | 'live' | 'completed' | 'cancelled';
}

interface PlanningTask {
  id: string;
  name: string;
  title?: string; // For backward compatibility
  category: string;
  color: string;
  status: 'todo' | 'in-progress' | 'completed';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  assignee: string;
  deadline: string;
  description: string;
  createdAt?: Date;
  updatedAt?: Date;
}

interface MeetingRequest {
  id: string;
  fromUserId: string;
  toUserId: string;
  type: 'chat' | 'call' | 'video' | 'meeting';
  status: 'pending' | 'accepted' | 'declined';
  title: string;
  description: string;
  proposedDate: Date;
  proposedTime: string;
  duration: number;
  location?: string;
  meetingLink?: string;
  createdAt: Date;
  updatedAt: Date;
}

interface AgendaPageProps {
  userProfile: UserProfile | null;
  mobileTabIndex?: number;
}

const AgendaPage: React.FC<AgendaPageProps> = ({ userProfile, mobileTabIndex = 0 }) => {
  // Goal context for team collaboration
  const { activeGoalId, isInTeamContext } = useGoalContext(userProfile?.uid);
  
  // Main navigation state
  const [activeTab, setActiveTab] = useState<'kalender' | 'planning' | 'podcast-sessies' | 'calendar-sync' | 'doelstellingen' | 'purpose' | 'team'>('kalender');
  
  // Auto-switch to doelstellingen tab when sparring session closes
  useEffect(() => {
    const handleSparringClosed = () => {
      console.log('🎯 Sparring session closed, switching to Doelstellingen tab');
      setActiveTab('doelstellingen');
    };
    
    window.addEventListener('sparringSessionClosed', handleSparringClosed);
    return () => window.removeEventListener('sparringSessionClosed', handleSparringClosed);
  }, []);
  
  // Calendar states
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<'week' | 'biweek' | 'month'>('month');
  const [mobileCalendarMode, setMobileCalendarMode] = useState<'day' | 'week' | 'month'>('week');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedDayDetails, setSelectedDayDetails] = useState<Date | null>(new Date());
  
  // Day view tabs state (NEW)
  const [dayViewTab, setDayViewTab] = useState<'events' | 'tasks'>('events');

  useEffect(() => {
    const tabMap: Array<typeof activeTab> = ['kalender', 'doelstellingen', 'kalender', 'team', 'calendar-sync'];
    const nextTab = tabMap[mobileTabIndex] || 'kalender';
    setActiveTab(nextTab);
    if (mobileTabIndex === 2) {
      setDayViewTab('tasks');
      setSelectedDayDetails(new Date());
    } else if (mobileTabIndex === 0) {
      setDayViewTab('events');
    }
  }, [mobileTabIndex]);
  
  // Drag state voor pulse animatie op Afspraken vak
  const [isDraggingOverAfspraken, setIsDraggingOverAfspraken] = useState(false);
  // Drag state voor pulse animatie op Taken vak
  const [isDraggingOverTaken, setIsDraggingOverTaken] = useState(false);
  
  // Task sorting state (NEW)
  const [taskSortMode, setTaskSortMode] = useState<SortMode>(getSmartSortMode());
  const [customTaskOrder, setCustomTaskOrder] = useState<Record<string, string[]>>({});
  
  // Drag & Drop sensors (NEW)
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  
  // Calendar visibility filters
  const [calendarVisibility, setCalendarVisibility] = useState({
    google: true,
    outlook: true,
    apple: true,
    planning: true,
    podcast: true,
    personal: true,
    meeting: true,
    call: true,
    networking: true,
    meetings: true
  });
  const [showCompleted, setShowCompleted] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('agendaShowCompleted') === 'true';
  });
  const [showCancelled, setShowCancelled] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('agendaShowCancelled') === 'true';
  });
  
  // Loading states
  const [loading, setLoading] = useState({
    events: false,
    planningTasks: false,
    agendaTasks: false,
    meetingRequests: false,
    podcastSessions: false
  });

  // Error states
  const [errors, setErrors] = useState<{[key: string]: string | null}>({});

  // Toast notification state
  const [toast, setToast] = useState<{message: string, type: 'success' | 'error' | 'info'} | null>(null);
  const [connectionContext, setConnectionContext] = useState<{ connectionId: string; name?: string; otherUserId?: string } | null>(null);

  // Google Calendar sync state
  const [googleCalendarStatus, setGoogleCalendarStatus] = useState<{
    connected: boolean;
    lastSync: Date | null;
    syncing: boolean;
    eventCount: number;
  }>({
    connected: false,
    lastSync: null,
    syncing: false,
    eventCount: 0
  });
  const [googleReconnectNeeded, setGoogleReconnectNeeded] = useState<string | null>(null);

  // Outlook Calendar sync state
  const [outlookCalendarStatus, setOutlookCalendarStatus] = useState<{
    connected: boolean;
    lastSync: Date | null;
    syncing: boolean;
    eventCount: number;
  }>({
    connected: false,
    lastSync: null,
    syncing: false,
    eventCount: 0
  });
  const [outlookReconnectNeeded, setOutlookReconnectNeeded] = useState<string | null>(null);

  // Apple Calendar states
  const [appleCalendarStatus, setAppleCalendarStatus] = useState({
    connected: false,
    lastSync: null as Date | null,
    syncing: false,
    eventCount: 0
  });
  const [appleReconnectNeeded, setAppleReconnectNeeded] = useState<string | null>(null);
  const [appleIcsUrl, setAppleIcsUrl] = useState('');
  const [appleCalendarName, setAppleCalendarName] = useState('Apple Calendar');
  const [showAppleInstructions, setShowAppleInstructions] = useState(false);

  // Auto-sync states
  const [autoSyncEnabled, setAutoSyncEnabled] = useState(false);
  const [autoSyncFrequency, setAutoSyncFrequency] = useState<'daily' | 'weekly' | 'monthly'>('weekly');
  const [autoSyncCalendars, setAutoSyncCalendars] = useState<string[]>([]);
  const [showAutoSyncModal, setShowAutoSyncModal] = useState(false);

  // Data states
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [availability, setAvailability] = useState<AvailabilitySlot[]>([]);
  const [podcastSessions, setPodcastSessions] = useState<PodcastSession[]>([]);
  const [meetingRequests, setMeetingRequests] = useState<MeetingRequest[]>([]);
  const [planningTasks, setPlanningTasks] = useState<PlanningTask[]>([
    { 
      id: '1', 
      name: "Chat flow wireframes maken", 
      category: "Ontwerp",
      color: "#8B5CF6", 
      status: "todo",
      priority: "high",
      assignee: "Emma R.",
      deadline: "2025-09-25",
      description: "Conversatie flows en UI mockups ontwerpen"
    },
    { 
      id: '2', 
      name: "Bot ontwikkeling starten", 
      category: "Implementatie", 
      color: "#EF4444", 
      status: "todo",
      priority: "urgent",
      assignee: "Alex P.",
      deadline: "2025-10-05",
      description: "AI bot implementatie en training"
    },
    { 
      id: '3', 
      name: "Gebruikerstests uitvoeren", 
      category: "Evaluatie", 
      color: "#3B82F6", 
      status: "todo",
      priority: "medium",
      assignee: "Lisa M.",
      deadline: "2025-10-15",
      description: "User testing en feedback verzamelen"
    },
    { 
      id: '4', 
      name: "Requirements documenteren", 
      category: "Analyse", 
      color: "#F59E0B", 
      status: "in-progress",
      priority: "high",
      assignee: "Mike L.",
      deadline: "2025-09-20",
      description: "Technische requirements en functionele specificaties"
    }
  ]);
  
  // Modal states
  const [showEventModal, setShowEventModal] = useState(false);
  const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showPodcastModal, setShowPodcastModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState<PlanningTask | null>(null);
  const updateSelectedTaskFields = (updates: Partial<PlanningTask>) => {
    setSelectedTask(prev => (prev ? { ...prev, ...updates } : prev));
  };
  
  // Tasks sidebar state (Google Tasks stijl)
  const [isMessagesMinimized, setIsMessagesMinimized] = useState(false);
  const [sidebarAnimating, setSidebarAnimating] = useState(false);
  const toggleSidebar = (minimized: boolean) => {
    setSidebarAnimating(true);
    setIsMessagesMinimized(minimized);
    setTimeout(() => setSidebarAnimating(false), 320);
  };
  const [showTaskListManager, setShowTaskListManager] = useState(false);
  const defaultTaskLists: AgendaTaskList[] = [
    { id: 'all', name: 'Alles', isSystem: true, balanceCategory: 'none' },
    { id: 'ideas', name: 'Ideeën', balanceCategory: 'self' },
    { id: 'activities', name: 'Activiteiten', balanceCategory: 'work' },
    { id: 'actions', name: 'Acties', balanceCategory: 'work' },
    { id: 'private', name: 'Privé', balanceCategory: 'personal' },
    { id: 'done', name: 'Klaar', isSystem: true, balanceCategory: 'none' }
  ];
  
  const balanceCategoryLabels: Record<BalanceCategory, { label: string; emoji: string }> = {
    work: { label: 'Werk', emoji: '■' },
    personal: { label: 'Privé', emoji: '■' },
    self: { label: 'Zelf', emoji: '■' },
    essential: { label: 'Essentieel', emoji: '■' },
    none: { label: 'Niet meewegen', emoji: '—' }
  };
  const [taskLists, setTaskLists] = useState<AgendaTaskList[]>(defaultTaskLists);
  const [activeTaskListId, setActiveTaskListId] = useState<string>('ideas');
  const [tasks, setTasks] = useState<AgendaTask[]>([]);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [showNewListInput, setShowNewListInput] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [newListCategory, setNewListCategory] = useState<BalanceCategory>('work');
  
  // Klaar tab filters
  const [doneFilterCategory, setDoneFilterCategory] = useState<string>('all'); // all, activities, actions, planning, private
  const [doneFilterTimeInterval, setDoneFilterTimeInterval] = useState<'week' | 'month' | 'year'>('month');

  // ── Mastermind / Netwerkclub taken ────────────────────────────────────────
  interface GroupMembership { id: string; name: string; type: 'mastermind' | 'networkclub' | 'community'; }
  interface GroupTask {
    id: string; entityId: string; entityName?: string;
    title: string; ownerUid: string; ownerName: string; ownerAvatar: string | null;
    status: 'open' | 'done' | 'blocked' | 'carried_over'; createdAt: any;
  }
  const [userMemberships, setUserMemberships] = useState<GroupMembership[]>([]);
  const [groupTasks, setGroupTasks] = useState<GroupTask[]>([]);
  const [groupTasksLoaded, setGroupTasksLoaded] = useState(false);
  /** Optioneel: zelfde taak ook naar mastermindTasks (zichtbaar in Jitsi-meetings van die groep). */
  const [shareToMeetingEntityId, setShareToMeetingEntityId] = useState('');
  const [hubQuickAddTitle, setHubQuickAddTitle] = useState<Record<string, string>>({});
  // ─────────────────────────────────────────────────────────────────────────

  // Doelstellingen integratie
  const [doelstellingenActions, setDoelstellingenActions] = useState<any[]>([]);
  const [doelstellingenGoals, setDoelstellingenGoals] = useState<any[]>([]);
  const [doelstellingenMilestones, setDoelstellingenMilestones] = useState<any[]>([]);
  const [showDoelstellingenInSidebar, setShowDoelstellingenInSidebar] = useState(true);
  const [editingActionId, setEditingActionId] = useState<string | null>(null);
  const [editingActionTitle, setEditingActionTitle] = useState('');
  const [processingActionId, setProcessingActionId] = useState<string | null>(null);
  const [actiesViewMode, setActiesViewMode] = useState<'focus' | 'milestones' | 'actueel' | 'full'>('focus');
  const [expandedMilestones, setExpandedMilestones] = useState<Set<string>>(new Set()); // Accordion state
  const [restoreActionModal, setRestoreActionModal] = useState<{ actionId: string; actionTitle: string } | null>(null);
  const [pulsingMilestoneId, setPulsingMilestoneId] = useState<string | null>(null); // Mijlpaal die moet pulsen
  const [pulsingActionId, setPulsingActionId] = useState<string | null>(null); // Actie die moet pulsen
  const shiftingEventsRef = useRef<Set<string>>(new Set());
  // Timer data wordt nu direct in doelstellingenActions opgeslagen (Firebase synced)
  
  // Handmatige doelactie toevoegen
  const [showNewActionModal, setShowNewActionModal] = useState(false);
  const [newActionTitle, setNewActionTitle] = useState('');
  const [newActionGoalId, setNewActionGoalId] = useState<string>('');
  const [newActionMilestoneId, setNewActionMilestoneId] = useState<string>('');

  // Klaar tab pulse - zachte herinnering per maand
  const shouldPulseKlaar = (): boolean => {
    const now = new Date();
    const currentMonth = `${now.getFullYear()}-${now.getMonth()}`;
    const lastViewed = localStorage.getItem('klaarLastViewed');
    return lastViewed !== currentMonth;
  };
  
  const handleKlaarClick = () => {
    const now = new Date();
    localStorage.setItem('klaarLastViewed', `${now.getFullYear()}-${now.getMonth()}`);
    setActiveTaskListId('done');
  };

  // Duur berekenen tussen aanmaken en voltooien
  const formatTaskDuration = (createdAt: Date, completedAt: Date): string => {
    const ms = completedAt.getTime() - createdAt.getTime();
    const minutes = Math.floor(ms / (1000 * 60));
    const hours = Math.floor(ms / (1000 * 60 * 60));
    const days = Math.floor(ms / (1000 * 60 * 60 * 24));
    
    if (days > 0) return `${days}d`;
    if (hours > 0) return `${hours}u`;
    if (minutes > 0) return `${minutes}m`;
    return '<1m';
  };
  
  // Form states
  const [newEvent, setNewEvent] = useState({
    title: '',
    description: '',
    startTime: '',
    endTime: '',
    type: 'meeting' as CalendarEvent['type'],
    participants: [] as string[],
    location: '',
    meetingUrl: ''
  });

  // Agenda context coming from ConnectieFlow (to show which connection you're planning for)
  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      if (!detail?.connectionId) return;
      setConnectionContext({
        connectionId: detail.connectionId,
        name: detail.name,
        otherUserId: detail.otherUserId,
      });
      setToast({ message: `Agenda voor ${detail.name || 'connectie'}`, type: 'info' });
      setActiveTab('kalender');
    };
    window.addEventListener('openAgendaForConnection', handler as EventListener);
    return () => window.removeEventListener('openAgendaForConnection', handler as EventListener);
  }, []);

  // Load Doelstellingen data for sidebar integration
  // Also reload when window gets focus (user might have added actions from another tab/page)
  const [doelstellingenRefreshKey, setDoelstellingenRefreshKey] = useState(0);
  
  useEffect(() => {
    if (!userProfile?.uid) return;
    
    const loadDoelstellingenData = async () => {
      try {
        // Dynamic import to avoid circular dependencies
        const { getGoals } = await import('./Doelstellingen/Milestone_organizer/firebase/goals.service');
        const { getAllActions } = await import('./Doelstellingen/Milestone_organizer/firebase/actions.service');
        const { getAllMilestones } = await import('./Doelstellingen/Milestone_organizer/firebase/milestones.service');
        
        const [goals, actions, milestones] = await Promise.all([
          getGoals(userProfile.uid),
          getAllActions(userProfile.uid),
          getAllMilestones(userProfile.uid),
        ]);
        
        // Filter: alleen niet-voltooide acties
        const sortedActions = [...actions].sort((a: any, b: any) => {
          if (a.completed === b.completed) {
            return a.createdAt - b.createdAt;
          }
          return a.completed ? 1 : -1;
        });

        setDoelstellingenGoals(goals);
        setDoelstellingenActions(sortedActions);
        setDoelstellingenMilestones(milestones);
      } catch (err) {
        console.error('Failed to load Doelstellingen data:', err);
      }
    };
    
    loadDoelstellingenData();
  }, [userProfile?.uid, doelstellingenRefreshKey]);
  
  // Refresh doelstellingen data when:
  // 1. Window regains focus (user might have added actions from another tab)
  // 2. Custom event is fired (actions created from sparring in same tab)
  useEffect(() => {
    const handleRefresh = () => {
      console.log('📋 Refreshing doelstellingen data...');
      setDoelstellingenRefreshKey(prev => prev + 1);
    };
    
    window.addEventListener('focus', handleRefresh);
    window.addEventListener('doelstellingen-actions-changed', handleRefresh);
    
    return () => {
      window.removeEventListener('focus', handleRefresh);
      window.removeEventListener('doelstellingen-actions-changed', handleRefresh);
    };
  }, []);

  useEffect(() => {
    const shiftStaleDoelstellingenEvents = async () => {
      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);

      for (const event of events) {
        const meta = event.doelstellingenMeta;
        if (!meta || event.status === 'completed' || event.status === 'cancelled') continue;

        const eventStart = event.startTime instanceof Date ? event.startTime : new Date(event.startTime);
        if (eventStart >= todayStart) continue;

        if (shiftingEventsRef.current.has(event.id)) continue;

        const lastShifted = meta.lastShiftedDate ? new Date(meta.lastShiftedDate) : null;
        if (lastShifted && lastShifted >= todayStart) continue;

        shiftingEventsRef.current.add(event.id);

        const duration = event.endTime
          ? event.endTime.getTime() - eventStart.getTime()
          : 60 * 60 * 1000;
        const newStart = new Date(todayStart);
        newStart.setHours(eventStart.getHours(), eventStart.getMinutes(), eventStart.getSeconds());
        const newEnd = new Date(newStart.getTime() + duration);
        const docRef = doc(db, 'calendarEvents', event.id);
        const newGhostDates = [...(meta.ghostDates || []), eventStart.toISOString()];
        try {
          await updateDoc(docRef, {
            startTime: newStart,
            endTime: newEnd,
            doelstellingenMeta: {
              ...meta,
              ghostDates: newGhostDates,
              lastShiftedDate: newStart.toISOString(),
            },
            updatedAt: new Date(),
          });
        } catch (error) {
          console.error('Error shifting doelstelling event:', error);
        } finally {
          shiftingEventsRef.current.delete(event.id);
        }
      }
    };

    if (events.length > 0) {
      shiftStaleDoelstellingenEvents();
    }
  }, [events]);

  const handleToggleFocusAction = async (action: any) => {
    if (!action || action.completed) return;
    setProcessingActionId(action.id);
    try {
      const { updateAction } = await import('./Doelstellingen/Milestone_organizer/firebase/actions.service');
      const newFocusState = !action.isFocus;
      await updateAction(action.id, { isFocus: newFocusState });
      setDoelstellingenActions(prev =>
        prev.map(a =>
          a.id === action.id ? { ...a, isFocus: newFocusState } : a
        )
      );
      
      showToast(newFocusState ? 'Actie toegevoegd aan Focus' : 'Actie verwijderd uit Focus', 'success');
    } catch (error) {
      console.error('Error toggling focus:', error);
      showToast('❌ Kon focus niet wijzigen', 'error');
    } finally {
      setProcessingActionId(null);
    }
  };

  const handleCompleteDoelstellingenAction = async (action: any) => {
    if (!action) return;
    
    // Als actie al voltooid is, toon restore modal
    if (action.completed) {
      setRestoreActionModal({ actionId: action.id, actionTitle: action.title });
      return;
    }
    
    // Anders: markeer als voltooid
    setProcessingActionId(action.id);
    try {
      const { updateAction } = await import('./Doelstellingen/Milestone_organizer/firebase/actions.service');
      const completedAt = Date.now();
      // Wanneer actie wordt voltooid, verwijder ook uit focus (niet meer nodig)
      await updateAction(action.id, { completed: true, completedAt, isFocus: false });
      setDoelstellingenActions(prev =>
        prev.map(a =>
          a.id === action.id ? { ...a, completed: true, completedAt, isFocus: false } : a
        )
      );
      
      // NEW: Update gekoppelde calendar events naar 'completed' status (bidirectionele sync)
      const linkedEvents = events.filter(e => 
        e.doelstellingenMeta && e.doelstellingenMeta.actionId === action.id
      );
      
      console.log('🔄 Syncing', linkedEvents.length, 'linked calendar events for action:', action.id);
      
      for (const event of linkedEvents) {
        try {
          const eventRef = doc(db, 'calendarEvents', event.id);
          await updateDoc(eventRef, {
            status: 'completed'
          });
        } catch (err) {
          console.error('Error updating linked event:', err);
        }
      }
      
      showToast('Actie gemarkeerd als voltooid', 'success');
    } catch (error) {
      console.error('Error completing action:', error);
      showToast('❌ Kon actie niet afronden', 'error');
    } finally {
      setProcessingActionId(null);
    }
  };

  const handleRestoreDoelstellingenAction = async () => {
    if (!restoreActionModal) return;
    
    setProcessingActionId(restoreActionModal.actionId);
    try {
      const { updateAction } = await import('./Doelstellingen/Milestone_organizer/firebase/actions.service');
      
      // Gebruik deleteField() voor Firebase om velden te verwijderen
      await updateAction(restoreActionModal.actionId, { 
        completed: false, 
        completedAt: deleteField(),
        isFocus: false // Reset focus bij restore
      });
      
      // Vind de actie om milestoneId te krijgen
      const restoredAction = doelstellingenActions.find(a => a.id === restoreActionModal.actionId);
      const milestoneId = restoredAction?.milestoneId;
      
      setDoelstellingenActions(prev =>
        prev.map(a =>
          a.id === restoreActionModal.actionId 
            ? { ...a, completed: false, completedAt: undefined, isFocus: false } 
            : a
        )
      );
      
      // Update gekoppelde calendar events terug naar 'scheduled' status
      const linkedEvents = events.filter(e => 
        e.doelstellingenMeta && e.doelstellingenMeta.actionId === restoreActionModal.actionId
      );
      
      for (const event of linkedEvents) {
        try {
          const eventRef = doc(db, 'calendarEvents', event.id);
          await updateDoc(eventRef, {
            status: 'scheduled'
          });
        } catch (err) {
          console.error('Error updating linked event:', err);
        }
      }
      
      // Start pulse animatie voor mijlpaal en actie
      if (milestoneId) {
        setPulsingMilestoneId(milestoneId);
        // Open automatisch de mijlpaal
        setExpandedMilestones(prev => new Set(prev).add(milestoneId));
      }
      setPulsingActionId(restoreActionModal.actionId);
      
      // Reset pulse na 3 seconden (3x pulse animatie duurt ~3 seconden)
      setTimeout(() => {
        setPulsingMilestoneId(null);
        setPulsingActionId(null);
      }, 3000);
      
      showToast('Actie teruggezet naar niet-voltooid', 'success');
      setRestoreActionModal(null);
    } catch (error) {
      console.error('Error restoring action:', error);
      showToast(`❌ Kon actie niet terugzetten: ${error instanceof Error ? error.message : 'Onbekende fout'}`, 'error');
    } finally {
      setProcessingActionId(null);
    }
  };

  const handleStartEditingAction = (action: any) => {
    setEditingActionId(action.id);
    setEditingActionTitle(action.title || '');
  };

  const handleCancelEditingAction = () => {
    setEditingActionId(null);
    setEditingActionTitle('');
  };

  const handleSaveActionTitle = async () => {
    if (!editingActionId) return;
    setProcessingActionId(editingActionId);
    try {
      const { updateAction } = await import('./Doelstellingen/Milestone_organizer/firebase/actions.service');
      await updateAction(editingActionId, { title: editingActionTitle.trim() });
      setDoelstellingenActions(prev =>
        prev.map(a =>
          a.id === editingActionId ? { ...a, title: editingActionTitle.trim() } : a
        )
      );
      showToast('Actie succesvol bijgewerkt', 'success');
      handleCancelEditingAction();
    } catch (error) {
      console.error('Error updating action:', error);
      showToast('❌ Kon actie niet bijwerken', 'error');
    } finally {
      setProcessingActionId(null);
    }
  };

  const handleDeleteDoelstellingenAction = async (actionId: string, title?: string) => {
    setProcessingActionId(actionId);
    try {
      const { deleteAction } = await import('./Doelstellingen/Milestone_organizer/firebase/actions.service');
      await deleteAction(actionId);
      setDoelstellingenActions(prev => prev.filter(a => a.id !== actionId));
      showToast(`🗑️ ${title || 'Actie'} verwijderd`, 'success');
      if (editingActionId === actionId) {
        handleCancelEditingAction();
      }
    } catch (error) {
      console.error('Error deleting action:', error);
      showToast('❌ Kon actie niet verwijderen', 'error');
    } finally {
      setProcessingActionId(null);
    }
  };

  const handleAddManualDoelAction = async () => {
    if (!newActionTitle.trim() || !newActionGoalId || !userProfile?.uid) {
      showToast('⚠️ Vul een titel in en selecteer een doel', 'error');
      return;
    }

    try {
      const { createAction } = await import('./Doelstellingen/Milestone_organizer/firebase/actions.service');
      
       // Bepaal de hoogste order voor dit doel
       const existingActions = doelstellingenActions.filter(a => a.goalId === newActionGoalId && !a.completed);
       const maxOrder = existingActions.length > 0
         ? Math.max(...existingActions.map(a => a.order ?? 0))
         : 0;
      
         const actionId = await createAction(
           userProfile.uid,
           newActionGoalId,
           newActionMilestoneId || null,
           {
             title: newActionTitle.trim(),
             completed: false,
             origin: 'manual',
             activeTime: 0,
             timerStartedAt: null,
             isTimerRunning: false,
             order: maxOrder + 1,
           }
         );

       // Add to local state
       const newAction = {
         id: actionId,
         title: newActionTitle.trim(),
         completed: false,
         origin: 'manual',
         goalId: newActionGoalId,
         milestoneId: newActionMilestoneId || null,
         ownerId: userProfile.uid,
         createdAt: Date.now(),
         activeTime: 0,
         timerStartedAt: null,
         isTimerRunning: false,
         order: maxOrder + 1,
       };

      setDoelstellingenActions(prev => [...prev, newAction]);
      
      // Reset form
      setNewActionTitle('');
      setNewActionGoalId('');
      setNewActionMilestoneId('');
      setShowNewActionModal(false);
      
      showToast('Handmatige actie toegevoegd', 'success');
    } catch (error) {
      console.error('Error creating manual action:', error);
      showToast('❌ Kon actie niet toevoegen', 'error');
    }
  };

  // Handler voor reorderen van doelstellingen acties binnen sidebar
  const handleDoelActiesReorder = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const activeAction = doelstellingenActions.find(a => a.id === active.id);
    const overAction = doelstellingenActions.find(a => a.id === over.id);

    if (!activeAction || !overAction) return;

    // Alleen reorderen binnen zelfde doel en zelfde type (gegenereerd/handmatig)
    if (activeAction.goalId !== overAction.goalId) return;
    if ((activeAction.origin !== 'manual') !== (overAction.origin !== 'manual')) return;

    // Get all actions for this goal and type
    const actionsToReorder = doelstellingenActions
      .filter(a => 
        a.goalId === activeAction.goalId && 
        !a.completed &&
        ((a.origin !== 'manual') === (activeAction.origin !== 'manual'))
      )
      .sort((a, b) => (a.order ?? a.createdAt) - (b.order ?? b.createdAt));

    const oldIndex = actionsToReorder.findIndex(a => a.id === active.id);
    const newIndex = actionsToReorder.findIndex(a => a.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const reorderedActions = arrayMove(actionsToReorder, oldIndex, newIndex);

    // Update order field for all reordered actions
    const updates = reorderedActions.map(async (action, index) => {
      const newOrder = index;
      if (action.order !== newOrder) {
        const { updateAction } = await import('./Doelstellingen/Milestone_organizer/firebase/actions.service');
        await updateAction(action.id, { order: newOrder });
        return { ...action, order: newOrder };
      }
      return action;
    });

    try {
      const updatedActions = await Promise.all(updates);
      
      // Update local state
      setDoelstellingenActions(prev =>
        prev.map(a => {
          const updated = updatedActions.find(u => u.id === a.id);
          return updated || a;
        })
      );
    } catch (error) {
      console.error('Error reordering actions:', error);
      showToast('❌ Kon volgorde niet opslaan', 'error');
    }
  };

  const [newAvailability, setNewAvailability] = useState({
    dayOfWeek: 1,
    startTime: '09:00',
    endTime: '17:00',
    isAvailable: true
  });

  // Edit modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState<CalendarEvent | null>(null);
  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    startDate: '',
    startTime: '',
    endTime: '',
    type: 'google' as CalendarEvent['type'],
    category: 'work' as CalendarEvent['category'],
    color: '#3B82F6',
    repeat: 'none' as 'none' | 'daily' | 'weekly' | 'biweekly' | 'monthly',
    repeatEndDate: '',
    reminderEnabled: false,
    reminderMinutesBefore: 30
  });

  // Delete confirmation modal for recurring events
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<CalendarEvent | null>(null);

  // Reschedule modal states
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [rescheduleEvent, setRescheduleEvent] = useState<CalendarEvent | null>(null);
  const [rescheduleForm, setRescheduleForm] = useState({
    newStartDate: '',
    newStartTime: '',
    newEndTime: '',
    reason: ''
  });

  // New event modal states
  const [newEventForm, setNewEventForm] = useState({
    title: '',
    description: '',
    startDate: '',
    startTime: '',
    endTime: '',
    type: 'google' as CalendarEvent['type'],
    category: 'work' as CalendarEvent['category'],
    color: '#3B82F6',
    repeat: 'none' as 'none' | 'daily' | 'weekly' | 'biweekly' | 'monthly',
    repeatEndDate: '',
    reminderEnabled: true,
    reminderMinutesBefore: 30
  });
  const [isNetworkingTemplate, setIsNetworkingTemplate] = useState(false);

  // New task modal states
  const [newTaskForm, setNewTaskForm] = useState({
    title: '',
    description: '',
    deadline: '',
    priority: 'medium' as PlanningTask['priority'],
    status: 'todo' as PlanningTask['status']
  });

  // Podcast planning modal states
  const [podcastForm, setPodcastForm] = useState({
    title: '',
    description: '',
    date: '',
    time: '',
    duration: '60',
    topic: '',
    mainSpeaker: '',
    guests: [] as Array<{name: string, email: string}>
  });

  // Podcast availability system
  const [podcastAvailability, setPodcastAvailability] = useState({
    availableDates: [] as string[],
    availableTimes: ['09:00', '10:00', '11:00', '14:00', '15:00', '16:00'],
    blockedDates: [] as string[],
    adminMode: false,
    requests: [] as PodcastRequest[],
    templates: [] as AvailabilityTemplate[],
    selectedTemplate: null as string | null
  });

  // Persist agenda visibility preferences
  useEffect(() => {
    if (typeof window === 'undefined') return;
    localStorage.setItem('agendaShowCompleted', showCompleted ? 'true' : 'false');
    localStorage.setItem('agendaShowCancelled', showCancelled ? 'true' : 'false');
  }, [showCompleted, showCancelled]);

  const visibleEvents = useMemo(
    () => events.filter((e) => isVisibleEvent(e, showCompleted, showCancelled)),
    [events, showCompleted, showCancelled]
  );

  const eventStatusCounts = useMemo(() => {
    const counts = { active: 0, completed: 0, cancelled: 0 };
    events.forEach((e) => {
      if (e.status === 'completed') counts.completed += 1;
      else if (e.status === 'cancelled') counts.cancelled += 1;
      else counts.active += 1;
    });
    return counts;
  }, [events]);

  // Availability template interface
  interface AvailabilityTemplate {
    id: string;
    name: string;
    description: string;
    availableTimes: string[];
    blockedTimes: string[];
    days: string[]; // ['monday', 'tuesday', etc.]
    isDefault: boolean;
  }

  // Admin calendar state
  const [adminCalendarView, setAdminCalendarView] = useState<'month' | 'week'>('month');
  const [adminCurrentDate, setAdminCurrentDate] = useState(new Date());
  const [draggedItem, setDraggedItem] = useState<{type: 'available' | 'blocked', time: string} | null>(null);

  // Podcast request interface
  interface PodcastRequest {
    id: string;
    title: string;
    description: string;
    requestedDate: string;
    requestedTime: string;
    duration: number;
    topic: string;
    mainSpeaker: string;
    guests: Array<{name: string, email: string}>;
    requesterId: string;
    requesterName: string;
    status: 'pending' | 'approved' | 'rejected';
    createdAt: Date;
    adminNotes?: string;
  }

  // Sample events data
  const [sampleEvents] = useState<CalendarEvent[]>([
    {
      id: '1',
      title: "ConnectFlow Demo",
      startTime: new Date(2025, 8, 17, 14, 0),
      endTime: new Date(2025, 8, 17, 15, 0),
      type: 'planning',
      participants: [userProfile?.uid || ''],
      participantNames: ['Team'],
      status: 'confirmed',
      createdBy: userProfile?.uid || '',
      createdAt: new Date(),
      reminderSent: false
    },
    {
      id: '2',
      title: "Podcast Opname - Tech Trends 2025",
      startTime: new Date(2025, 8, 18, 10, 0),
      endTime: new Date(2025, 8, 18, 11, 30),
      type: 'podcast',
      participants: [userProfile?.uid || ''],
      participantNames: ['Team'],
      status: 'confirmed',
      createdBy: userProfile?.uid || '',
      createdAt: new Date(),
      reminderSent: false
    },
    {
      id: '3',
      title: "Team Standup",
      startTime: new Date(2025, 8, 17, 9, 0),
      endTime: new Date(2025, 8, 17, 9, 30),
      type: 'google',
      participants: [userProfile?.uid || ''],
      participantNames: ['Team'],
      status: 'confirmed',
      createdBy: userProfile?.uid || '',
      createdAt: new Date(),
      reminderSent: false
    }
  ]);

  // Sample podcast sessions
  const [samplePodcastSessions] = useState<PodcastSession[]>([
    {
      id: '1',
      title: "AI in Business: Praktische Toepassingen",
      description: "Live podcast sessie over AI implementaties in bedrijven",
      host: "SuperKonnected Team",
      guests: [],
      scheduledDate: new Date(2025, 8, 20, 15, 0),
      duration: 90,
      category: "Technology",
      isPublic: true,
      maxParticipants: 25,
      currentParticipants: 12,
      status: 'planned'
    },
    {
      id: '2',
      title: "Automatisering van Bedrijfsprocessen",
      description: "Discussie over business automation en efficiency",
      host: "Expert Panel",
      guests: [],
      scheduledDate: new Date(2025, 8, 22, 14, 0),
      duration: 60,
      category: "Business",
      isPublic: true,
      maxParticipants: 20,
      currentParticipants: 8,
      status: 'planned'
    }
  ]);

  // Toast notification helper
  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  useEffect(() => {
    if (userProfile?.uid) {
      loadData();
      
      // Set up real-time listeners
      const cleanup = setupRealtimeListeners();
      
      // Cleanup function
      return () => {
        if (cleanup) cleanup();
      };
    }
  }, [userProfile]);

  // Handle Google Calendar OAuth callback
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const googleCode = params.get('googleCode');
    const googleSuccess = params.get('googleSuccess');
    const googleError = params.get('googleError');

    // Handle authorization code from callback
    if (googleCode) {
      // Remove code from URL
      const newUrl = window.location.pathname + window.location.hash.split('?')[0];
      window.history.replaceState({}, '', newUrl);
      
      // Process the authorization code
      handleGoogleCalendarCode(googleCode);
    } else if (googleSuccess === 'true') {
      // Remove query params from URL
      const newUrl = window.location.pathname + window.location.hash.split('?')[0];
      window.history.replaceState({}, '', newUrl);
      
      // Update status and show success
      setGoogleCalendarStatus(prev => ({ ...prev, connected: true }));
      showToast('✅ Google Calendar succesvol verbonden', 'success');
      
      // Reload data to get updated status
      loadData();
    } else if (googleError) {
      // Remove query params from URL
      const newUrl = window.location.pathname + window.location.hash.split('?')[0];
      window.history.replaceState({}, '', newUrl);
      
      // Show error
      showToast('❌ Google Calendar verbinding mislukt: ' + decodeURIComponent(googleError), 'error');
    }
  }, []);

  // Handle Outlook Calendar OAuth callback
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const outlookSuccess = params.get('outlookSuccess');
    const outlookError = params.get('outlookError');

    if (outlookSuccess === 'true') {
      // Remove query params from URL
      const newUrl = window.location.pathname + window.location.hash.split('?')[0];
      window.history.replaceState({}, '', newUrl);
      
      // Update status and show success
      setOutlookCalendarStatus(prev => ({ ...prev, connected: true }));
      showToast('✅ Outlook Calendar succesvol verbonden', 'success');
      
      // Reload data to get updated status
      loadData();
    } else if (outlookError) {
      // Remove query params from URL
      const newUrl = window.location.pathname + window.location.hash.split('?')[0];
      window.history.replaceState({}, '', newUrl);
      
      // Show error
      showToast('❌ Outlook Calendar verbinding mislukt: ' + decodeURIComponent(outlookError), 'error');
    }
  }, []);

  // Process Google Calendar authorization code
  const handleGoogleCalendarCode = async (code: string) => {
    try {
      const redirectUri = `${window.location.origin}/auth/google/callback`;
      
      // Call Firebase Cloud Function
      const functions = getFunctions(undefined, 'europe-west1');
      const linkGoogleAccount = httpsCallable(functions, 'linkGoogleAccount');
      
      showToast('🔄 Google Calendar verbinden...', 'info');
      
      const result = await linkGoogleAccount({ 
        code: code,
        redirectUri: redirectUri 
      });
      
      const data = result.data as { connected?: boolean; ok?: boolean; success?: boolean; message?: string; error?: string };
      
      if (data.connected || data.ok || data.success) {
        setGoogleCalendarStatus(prev => ({ ...prev, connected: true }));
        showToast('✅ Google Calendar succesvol verbonden', 'success');
        
        // Update user profile
        if (userProfile?.uid) {
          await updateDoc(doc(db, 'userProfiles', userProfile.uid), {
            'integrations.googleCalendar': true,
            'integrations.googleCalendarConnectedAt': new Date()
          });
        }
        
        // Reload data
        loadData();
      } else {
        throw new Error(data.error || data.message || 'Failed to connect Google Calendar');
      }
    } catch (error: any) {
      console.error('❌ Error connecting Google Calendar:', error);
      showToast('❌ Google Calendar verbinding mislukt: ' + (error.message || 'Onbekende fout'), 'error');
    }
  };

  // Cleanup duplicate events in Firestore (runs once per session)
  const cleanupDuplicateEvents = React.useCallback(async (events: CalendarEvent[]) => {
    if (!userProfile?.uid) return;
    
    // Use a ref to ensure this only runs once per session
    const hasRunKey = `cleanup_duplicates_${userProfile.uid}`;
    if (sessionStorage.getItem(hasRunKey)) return;
    
    try {
      // Group events by unique combination of time + participants
      const eventGroups = new Map<string, CalendarEvent[]>();
      
      events.forEach(event => {
        if (event.type !== 'networking' || !event.participants) return;
        
        const sortedParticipants = [...event.participants].sort().join(',');
        const startTime = event.startTime.getTime();
        const endTime = event.endTime.getTime();
        const key = `${startTime}_${endTime}_${sortedParticipants}`;
        
        if (!eventGroups.has(key)) {
          eventGroups.set(key, []);
        }
        eventGroups.get(key)!.push(event);
      });
      
      // Find duplicates and delete them
      let deletedCount = 0;
      for (const [key, group] of Array.from(eventGroups.entries())) {
        if (group.length > 1) {
          // Keep the oldest event (first created)
          const sorted = group.sort((a: CalendarEvent, b: CalendarEvent) => a.createdAt.getTime() - b.createdAt.getTime());
          const toKeep = sorted[0];
          const toDelete = sorted.slice(1);
          
          console.log(`Found ${toDelete.length} duplicate(s) for event at ${toKeep.startTime}. Keeping ${toKeep.id}, deleting others.`);
          
          // Delete duplicates from Firestore
          for (const duplicate of toDelete) {
            try {
              await deleteDoc(doc(db, 'calendarEvents', duplicate.id));
              deletedCount++;
              console.log(`Deleted duplicate event ${duplicate.id}`);
            } catch (err) {
              console.error(`Failed to delete duplicate ${duplicate.id}:`, err);
            }
          }
        }
      }
      
      if (deletedCount > 0) {
        console.log(`✓ Cleaned up ${deletedCount} duplicate event(s)`);
      }
      
      // Mark as done for this session
      sessionStorage.setItem(hasRunKey, 'true');
    } catch (error) {
      console.error('Error cleaning up duplicates:', error);
    }
  }, [userProfile?.uid]);

  const setupRealtimeListeners = () => {
    if (!userProfile?.uid) return;

    // Real-time listener for calendar events
    const eventsQuery = query(
      collection(db, 'calendarEvents'),
      where('participants', 'array-contains', userProfile.uid),
      orderBy('startTime', 'asc')
    );
    
    const unsubscribeEvents = onSnapshot(eventsQuery, (snapshot) => {
      const eventsData = snapshot.docs.map(doc => {
        const raw: any = doc.data();
        
        const recurring = parseRecurring(raw);

        // Parse pendingChangeRequest if it exists
        let pendingChangeRequest: CalendarEvent['pendingChangeRequest'] = undefined;
        if (raw.pendingChangeRequest) {
          pendingChangeRequest = {
            id: raw.pendingChangeRequest.id,
            fromUserId: raw.pendingChangeRequest.fromUserId,
            newStartTime: raw.pendingChangeRequest.newStartTime?.toDate ? raw.pendingChangeRequest.newStartTime.toDate() : new Date(raw.pendingChangeRequest.newStartTime),
            newEndTime: raw.pendingChangeRequest.newEndTime?.toDate ? raw.pendingChangeRequest.newEndTime.toDate() : new Date(raw.pendingChangeRequest.newEndTime),
            reason: raw.pendingChangeRequest.reason || '',
            status: raw.pendingChangeRequest.status || 'pending'
          };
        }

        return {
          id: doc.id,
          ...(raw as any),
          startTime: raw.startTime?.toDate ? raw.startTime.toDate() : new Date(),
          endTime: raw.endTime?.toDate ? raw.endTime.toDate() : new Date(),
          createdAt: raw.createdAt?.toDate ? raw.createdAt.toDate() : new Date(),
          source: (raw.source as string) || 'superkonnected',
          recurring,
          pendingChangeRequest
        } as CalendarEvent;
      });
      
      // Dedupe events by ID (prevents duplicate display)
      const uniqueEvents = eventsData.filter((event, index, self) => 
        index === self.findIndex((e) => e.id === event.id)
      );
      
      // Detect and cleanup duplicate events in Firestore
      cleanupDuplicateEvents(eventsData);
      
      setEvents(uniqueEvents);
    }, (error) => {
      console.error('Error in calendar events listener:', error);
    });

    // Real-time listener for planning tasks
    const planningTasksQuery = query(
      collection(db, 'planningTasks'),
      where('assignee', '==', userProfile.uid || 'current-user'),
      orderBy('createdAt', 'desc')
    );
    
    const unsubscribePlanningTasks = onSnapshot(planningTasksQuery, (snapshot) => {
      const planningTasksData = snapshot.docs.map(doc => {
        const raw = doc.data();
        return {
          id: doc.id,
          ...raw,
          createdAt: raw.createdAt?.toDate ? raw.createdAt.toDate() : new Date(),
          updatedAt: raw.updatedAt?.toDate ? raw.updatedAt.toDate() : new Date()
        } as PlanningTask;
      });
      setPlanningTasks(planningTasksData);
    }, (error) => {
      console.error('Error in planning tasks listener:', error);
    });

    // Real-time listener for agenda tasks
    const agendaTasksQuery = query(
      collection(db, 'agendaTasks'),
      where('userId', '==', userProfile.uid || 'current-user'),
      orderBy('createdAt', 'desc')
    );
    
    const unsubscribeAgendaTasks = onSnapshot(agendaTasksQuery, (snapshot) => {
      const agendaTasksData = snapshot.docs.map(doc => {
        const raw = doc.data();
        return {
          id: doc.id,
          ...raw,
          createdAt: raw.createdAt?.toDate ? raw.createdAt.toDate() : new Date(),
          completedAt: raw.completedAt?.toDate ? raw.completedAt.toDate() : null
        } as AgendaTask;
      });
      setTasks(agendaTasksData);
    }, (error) => {
      console.error('Error in agenda tasks listener:', error);
    });

    // ── Groepslidmaatschappen ophalen voor Mastermind/Netwerkclub tabs ──────
    // entityMembers heeft geen 'status' veld — query alleen op userId
    const membershipsQuery = query(
      collection(db, 'entityMembers'),
      where('userId', '==', userProfile.uid)
    );
    const unsubscribeMemberships = onSnapshot(membershipsQuery, async (snap) => {
      if (snap.empty) { setGroupTasksLoaded(true); return; }

      // Haal entityIds op uit de membershipdocs
      const entityIds = snap.docs.map(d => d.data().entityId).filter(Boolean);
      const uniqueEntityIds = [...new Set(entityIds)] as string[];
      if (uniqueEntityIds.length === 0) { setGroupTasksLoaded(true); return; }

      // Gebruik 'entities' collectie (primaire bron: heeft 'type' en 'name')
      // Firestore: max 30 docs tegelijk via getDoc calls (vermijd 'in' query voor zekerheid)
      const entityDocs = await Promise.all(
        uniqueEntityIds.slice(0, 10).map(eid =>
          getDocs(query(collection(db, 'entities'), where('__name__', '==', eid))).catch(() => null)
        )
      );

      const memberships: { id: string; name: string; type: 'mastermind' | 'networkclub' | 'community' }[] = [];
      entityDocs.forEach((snap, i) => {
        const d = snap?.docs[0];
        if (d) {
          const data = d.data();
          memberships.push({
            id: uniqueEntityIds[i],
            name: data.name ?? 'Groep',
            type: (data.type ?? 'mastermind') as 'mastermind' | 'networkclub' | 'community',
          });
        } else {
          // Fallback: entiteit niet gevonden, toon met id als naam
          memberships.push({ id: uniqueEntityIds[i], name: 'Groep', type: 'mastermind' });
        }
      });

      setUserMemberships(memberships);

      // Haal taken op voor alle groepen
      const tasksQ = query(
        collection(db, 'mastermindTasks'),
        where('entityId', 'in', uniqueEntityIds.slice(0, 10))
      );
      onSnapshot(tasksQ, (tSnap) => {
        setGroupTasks(tSnap.docs.map(d => ({ id: d.id, ...d.data() } as any)));
        setGroupTasksLoaded(true);
      });
    });
    // ─────────────────────────────────────────────────────────────────────

    // Real-time listener for meeting requests
    const meetingRequestsQuery = query(
      collection(db, 'meetingRequests'),
      where('toUserId', '==', userProfile.uid),
      orderBy('proposedDate', 'asc')
    );
    
    const unsubscribeMeetingRequests = onSnapshot(meetingRequestsQuery, (snapshot) => {
      const meetingRequestsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        proposedDate: doc.data().proposedDate?.toDate() || new Date(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date()
      })) as MeetingRequest[];
      setMeetingRequests(meetingRequestsData);
    }, (error) => {
      console.error('Error in meeting requests listener:', error);
    });

    // Real-time listener for podcast sessions
    const podcastsQuery = query(
      collection(db, 'podcastSessions'),
      orderBy('scheduledDate', 'asc')
    );
    
    const unsubscribePodcasts = onSnapshot(podcastsQuery, (snapshot) => {
      const podcastsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        scheduledDate: doc.data().scheduledDate?.toDate() || new Date()
      })) as PodcastSession[];
      setPodcastSessions(podcastsData.length > 0 ? podcastsData : samplePodcastSessions);
    }, (error) => {
      console.error('Error in podcast sessions listener:', error);
    });

    // Return cleanup function
    return () => {
      unsubscribeEvents();
      unsubscribePlanningTasks();
      unsubscribeAgendaTasks();
      unsubscribeMeetingRequests();
      unsubscribePodcasts();
    };
  };

  const loadData = async () => {
    try {
      // Load events from Firebase (fallback to sample data)
      setLoading(prev => ({ ...prev, events: true }));
      setErrors(prev => ({ ...prev, events: null }));
      try {
      const eventsQuery = query(
        collection(db, 'calendarEvents'),
        where('participants', 'array-contains', userProfile?.uid),
        orderBy('startTime', 'asc')
      );
      const eventsSnapshot = await getDocs(eventsQuery);
      const eventsData = eventsSnapshot.docs.map(doc => {
        const raw: any = doc.data();

        // Converteer Firestore Timestamps naar Date voor recurring endDate
        const recurring = parseRecurring(raw);

        const event: CalendarEvent = {
          id: doc.id,
          ...(raw as any),
          startTime: raw.startTime?.toDate ? raw.startTime.toDate() : new Date(),
          endTime: raw.endTime?.toDate ? raw.endTime.toDate() : new Date(),
          createdAt: raw.createdAt?.toDate ? raw.createdAt.toDate() : new Date(),
          source: (raw.source as string) || 'superkonnected',
          recurring
        };

        return event;
      });

      // Gebruik alleen echte events; geen demo/sample data meer in productie
      setEvents(eventsData);
      setLoading(prev => ({ ...prev, events: false }));
      } catch (error) {
        console.error('Error loading calendar events:', error);
        setEvents([]);
        setLoading(prev => ({ ...prev, events: false }));
        setErrors(prev => ({ ...prev, events: 'Kon events niet laden' }));
      }

      // Load podcast sessions
      setLoading(prev => ({ ...prev, podcastSessions: true }));
      try {
      const podcastsQuery = query(
        collection(db, 'podcastSessions'),
        orderBy('scheduledDate', 'asc')
      );
      const podcastsSnapshot = await getDocs(podcastsQuery);
      const podcastsData = podcastsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        scheduledDate: doc.data().scheduledDate?.toDate() || new Date()
      })) as PodcastSession[];
        setPodcastSessions(podcastsData.length > 0 ? podcastsData : samplePodcastSessions);
        setLoading(prev => ({ ...prev, podcastSessions: false }));
      } catch (error) {
        console.log('Using sample podcast data');
        setPodcastSessions(samplePodcastSessions);
        setLoading(prev => ({ ...prev, podcastSessions: false }));
      }

      // Load meeting requests
      setLoading(prev => ({ ...prev, meetingRequests: true }));
      try {
        const meetingRequestsQuery = query(
          collection(db, 'meetingRequests'),
          where('toUserId', '==', userProfile?.uid),
          orderBy('proposedDate', 'asc')
        );
        const meetingRequestsSnapshot = await getDocs(meetingRequestsQuery);
        const meetingRequestsData = meetingRequestsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          proposedDate: doc.data().proposedDate?.toDate() || new Date(),
          createdAt: doc.data().createdAt?.toDate() || new Date(),
          updatedAt: doc.data().updatedAt?.toDate() || new Date()
        })) as MeetingRequest[];
        setMeetingRequests(meetingRequestsData);
        setLoading(prev => ({ ...prev, meetingRequests: false }));
      } catch (error) {
        console.log('Error loading meeting requests:', error);
        setMeetingRequests([]);
        setLoading(prev => ({ ...prev, meetingRequests: false }));
      }

      // Load planning tasks
      setLoading(prev => ({ ...prev, planningTasks: true }));
      try {
        const planningTasksQuery = query(
          collection(db, 'planningTasks'),
          where('assignee', '==', userProfile?.uid || 'current-user'),
          orderBy('createdAt', 'desc')
        );
        const planningTasksSnapshot = await getDocs(planningTasksQuery);
        const planningTasksData = planningTasksSnapshot.docs.map(doc => {
          const raw = doc.data();
          return {
            id: doc.id,
            ...raw,
            createdAt: raw.createdAt?.toDate ? raw.createdAt.toDate() : new Date(),
            updatedAt: raw.updatedAt?.toDate ? raw.updatedAt.toDate() : new Date()
          } as PlanningTask;
        });
        if (planningTasksData.length > 0) {
          setPlanningTasks(planningTasksData);
        }
        setLoading(prev => ({ ...prev, planningTasks: false }));
      } catch (error) {
        console.log('Error loading planning tasks:', error);
        setLoading(prev => ({ ...prev, planningTasks: false }));
        // Keep default sample tasks if Firebase fails
      }

      // Load agenda tasks
      setLoading(prev => ({ ...prev, agendaTasks: true }));
      try {
        const agendaTasksQuery = query(
          collection(db, 'agendaTasks'),
          where('userId', '==', userProfile?.uid || 'current-user'),
          orderBy('createdAt', 'desc')
        );
        const agendaTasksSnapshot = await getDocs(agendaTasksQuery);
        const agendaTasksData = agendaTasksSnapshot.docs.map(doc => {
          const raw = doc.data();
          return {
            id: doc.id,
            ...raw,
            createdAt: raw.createdAt?.toDate ? raw.createdAt.toDate() : new Date(),
            completedAt: raw.completedAt?.toDate ? raw.completedAt.toDate() : null
          } as AgendaTask;
        });
        if (agendaTasksData.length > 0) {
          setTasks(agendaTasksData);
        }
        setLoading(prev => ({ ...prev, agendaTasks: false }));
      } catch (error) {
        console.log('Error loading agenda tasks:', error);
        setLoading(prev => ({ ...prev, agendaTasks: false }));
      }

      // Load task lists
      try {
        const taskListsQuery = query(
          collection(db, 'agendaTaskLists'),
          where('userId', '==', userProfile?.uid || 'current-user')
        );
        const taskListsSnapshot = await getDocs(taskListsQuery);
        const taskListsData = taskListsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as AgendaTaskList[];
        // Merge: behoud alle default lijsten + voeg extra user-created lijsten toe
        // Filter: alleen lijsten met een geldige naam (geen Firebase IDs)
        const defaultIds = defaultTaskLists.map(l => l.id);
        const extraUserLists = taskListsData.filter(l => 
          !defaultIds.includes(l.id) && 
          !l.isSystem && 
          l.name && 
          l.name.length > 0 &&
          l.name.length < 50 // Geen Firebase IDs (die zijn lang)
        );
        setTaskLists([...defaultTaskLists, ...extraUserLists]);
      } catch (error) {
        console.log('Error loading task lists:', error);
      }

    } catch (error) {
      console.error('Error loading agenda data:', error);
    }
  };

  // Compute visible task lists with dynamic 'Vanuit Doelen' list
  const visibleTaskLists = useMemo(() => {
    let lists = [...taskLists];
    
    // Add 'Vanuit Doelen' list if there are Doelstellingen actions
    const hasGoalsActions = doelstellingenActions.length > 0 && showDoelstellingenInSidebar;
    
    if (hasGoalsActions) {
      const hasGoalsList = lists.some(l => l.id === 'goals');
      if (!hasGoalsList) {
        // Insert before 'done' list
        const doneIndex = lists.findIndex(l => l.id === 'done');
        const insertIndex = doneIndex !== -1 ? doneIndex : lists.length;
        
        lists.splice(insertIndex, 0, {
          id: 'goals',
          name: 'Doelen Acties',
          isSystem: true,
          balanceCategory: 'work',
        });
      }
      
      // Hide standard "Acties" tab when Goals list is present
      lists = lists.filter(list => {
        // Keep all lists except non-system "Acties" tab
        if (list.id === 'actions' && !list.isSystem) {
          return false;
        }
        return true;
      });
    }
    
    // Voeg Mastermind en Netwerkclub tabs toe als de gebruiker lid is
    const hasMasterminds = userMemberships.some((m: any) => m.type === 'mastermind' || m.type === 'community');
    const hasNetworkclubs = userMemberships.some((m: any) => m.type === 'networkclub');

    if (hasMasterminds && !lists.some(l => l.id === 'mastermind-hub')) {
      const doneIndex = lists.findIndex(l => l.id === 'done');
      lists.splice(doneIndex !== -1 ? doneIndex : lists.length, 0, {
        id: 'mastermind-hub', name: 'Mastermind', isSystem: true, balanceCategory: 'none',
      });
    }
    if (hasNetworkclubs && !lists.some(l => l.id === 'networkclub-hub')) {
      const doneIndex = lists.findIndex(l => l.id === 'done');
      lists.splice(doneIndex !== -1 ? doneIndex : lists.length, 0, {
        id: 'networkclub-hub', name: 'Netwerkclub', isSystem: true, balanceCategory: 'none',
      });
    }

    return lists;
  }, [taskLists, doelstellingenActions.length, showDoelstellingenInSidebar, userMemberships]);

  // Navigation functions
  const navigateWeek = (direction: number) => {
    const newDate = new Date(currentDate);
    if (viewMode === 'month') {
      newDate.setMonth(currentDate.getMonth() + direction);
    } else {
      const daysToMove = viewMode === 'biweek' ? 14 : 7;
      newDate.setDate(currentDate.getDate() + (direction * daysToMove));
    }
    setCurrentDate(newDate);
  };

  const toggleViewMode = () => {
    if (viewMode === 'week') {
      setViewMode('biweek');
    } else if (viewMode === 'biweek') {
      setViewMode('month');
      } else {
      setViewMode('week');
    }
  };

  // Event management functions
  const editEvent = (event: CalendarEvent) => {
    if (isLockedConnectionEvent(event)) {
      showToast('⚠️ Deze afspraak wijzig je via de connectie-flow.', 'info');
      if (event.connectionId) {
        openInConnectieFlow(event);
      }
      return;
    }
    setEditingEvent(event);
    setEditForm({
      title: event.title,
      description: event.description || '',
      startDate: event.startTime.toISOString().split('T')[0],
      startTime: event.startTime.toTimeString().slice(0, 5),
      endTime: event.endTime.toTimeString().slice(0, 5),
      type: event.type,
      category: event.category || 'work',
      color: event.color || '#3B82F6',
      repeat: event.recurring?.frequency || 'none',
      repeatEndDate: event.recurring?.endDate
        ? event.recurring.endDate.toISOString().split('T')[0]
        : '',
      reminderEnabled: event.reminderEnabled ?? false,
      reminderMinutesBefore: event.reminderMinutesBefore ?? 30
    });
    setShowEditModal(true);
  };

  const handleEditSave = async () => {
    if (!editingEvent) return;

    const startDateTime = new Date(`${editForm.startDate}T${editForm.startTime}`);
    const endDateTime = new Date(`${editForm.startDate}T${editForm.endTime}`);

    let recurring: CalendarEvent['recurring'] | undefined = undefined;
    if (editForm.repeat !== 'none') {
      recurring = editForm.repeatEndDate
        ? {
            frequency: editForm.repeat as 'daily' | 'weekly' | 'monthly',
            endDate: new Date(editForm.repeatEndDate + 'T23:59:59')
          }
        : {
            frequency: editForm.repeat as 'daily' | 'weekly' | 'monthly'
          };
    }

    const reminderEnabled = editForm.reminderEnabled;
    const reminderMinutesBefore = editForm.reminderMinutesBefore;
    const reminderAt =
      reminderEnabled && reminderMinutesBefore
        ? new Date(startDateTime.getTime() - reminderMinutesBefore * 60 * 1000)
        : null;

    // Prepare update data for Firebase
    const updateData: any = {
      title: editForm.title,
      description: editForm.description,
      startTime: startDateTime,
      endTime: endDateTime,
      type: editForm.type,
      category: editForm.category,
      color: editForm.color,
      reminderEnabled,
      reminderMinutesBefore,
      reminderAt
    };

    if (recurring) {
      updateData.recurring = recurring;
    } else {
      updateData.recurring = null; // Remove recurring if set to 'none'
    }

    // Optimistic update - update UI immediately
    const previousEvent = events.find(e => e.id === editingEvent.id);
    const optimisticEvent = {
      ...editingEvent,
      title: editForm.title,
      description: editForm.description,
      startTime: startDateTime,
      endTime: endDateTime,
      type: editForm.type,
      category: editForm.category,
      color: editForm.color,
      recurring,
      reminderEnabled,
      reminderMinutesBefore,
      reminderAt
    };

    setEvents((prev: CalendarEvent[]) => prev.map(e => 
      e.id === editingEvent.id ? optimisticEvent : e
    ));
    setShowEditModal(false);
    setEditingEvent(null);

    try {
      // Update in Firebase
      const eventRef = doc(db, 'calendarEvents', editingEvent.id);
      await updateDoc(eventRef, updateData);
      showToast('✅ Afspraak succesvol bijgewerkt', 'success');
    } catch (error) {
      console.error('Error updating calendar event:', error);
      // Rollback on error
      if (previousEvent) {
        setEvents((prev: CalendarEvent[]) => prev.map(e => 
          e.id === editingEvent.id ? previousEvent : e
        ));
      }
      setShowEditModal(true);
      setEditingEvent(editingEvent);
      showToast('❌ Er ging iets mis bij het bijwerken van het agendapunt', 'error');
    }
  };

  const handleEditCancel = () => {
    setShowEditModal(false);
    setEditingEvent(null);
  };

  // New event functions
  const openNewEventModal = () => {
    setNewEventForm({
      title: '',
      description: '',
      startDate: new Date().toISOString().split('T')[0],
      startTime: '09:00',
      endTime: '10:00',
      type: 'google',
      category: 'work',
      color: '#3B82F6',
      repeat: 'none',
      repeatEndDate: '',
      reminderEnabled: true,
      reminderMinutesBefore: 30
    });
    setIsNetworkingTemplate(false);
    setShowEventModal(true);
  };

  // Quick access: "Vrij voor netwerken" agendablok
  const openNetworkingQuickSlot = () => {
    const baseDate =
      selectedDayDetails && selectedDayDetails instanceof Date
        ? selectedDayDetails
        : selectedDate || new Date();

    const isoDate = baseDate.toISOString().split('T')[0];

    setNewEventForm({
      title: '',
      description: '',
      startDate: isoDate,
      startTime: '14:00',
      endTime: '15:00',
      type: 'networking',
      category: 'work',
      color: '#10B981',
      repeat: 'weekly',
      repeatEndDate: '',
      reminderEnabled: true,
      reminderMinutesBefore: 60
    });
    setShowEventModal(true);
    setIsNetworkingTemplate(true);
  };

  const handleNewEventSave = async () => {
    const isNetworking = newEventForm.type === 'networking';

    // Alleen titel verplicht als het geen networking-blok is
    if (!isNetworking && !newEventForm.title.trim()) {
      alert('Voer een titel in voor de afspraak');
      return;
    }

    // Template: "Vrij voor netwerken" – vul automatisch titel/omschrijving in
    const title = isNetworking
      ? 'Vrij voor netwerken'
      : newEventForm.title;
    const description = isNetworking
      ? 'Tijdblok waarin ik beschikbaar ben om nieuwe connecties te maken via SuperKonnected.'
      : newEventForm.description;

    // Bepaal werk/prive-domein op basis van categorie
    const category = newEventForm.category;
    const domain: CalendarEvent['domain'] =
      category === 'work' ? 'work' : 'private';

    const startDateTime = new Date(`${newEventForm.startDate}T${newEventForm.startTime}`);
    const endDateTime = new Date(`${newEventForm.startDate}T${newEventForm.endTime}`);

    let recurring: CalendarEvent['recurring'] | undefined = undefined;
    if (newEventForm.repeat !== 'none') {
      recurring = newEventForm.repeatEndDate
        ? {
            frequency: newEventForm.repeat,
            endDate: new Date(newEventForm.repeatEndDate + 'T23:59:59')
          }
        : {
            frequency: newEventForm.repeat
          };
    }

    const reminderEnabled = newEventForm.reminderEnabled;
    const reminderMinutesBefore = newEventForm.reminderMinutesBefore;
    const reminderAt =
      reminderEnabled && reminderMinutesBefore
        ? new Date(startDateTime.getTime() - reminderMinutesBefore * 60 * 1000)
        : null;

    const baseEventData: any = {
      title,
      description,
      startTime: startDateTime,
      endTime: endDateTime,
      type: newEventForm.type,
      category,
      domain,
      source: 'superkonnected',
      participants: userProfile?.uid ? [userProfile.uid] : [],
      participantNames: userProfile?.displayName ? [userProfile.displayName] : [],
      status: 'scheduled' as CalendarEvent['status'],
      createdBy: userProfile?.uid || 'current-user',
      createdAt: new Date(),
      reminderEnabled,
      reminderMinutesBefore,
      reminderAt,
      reminderSent: false,
      // Set autoAcceptNetworking for networking events (default: false)
      ...(isNetworking ? { autoAcceptNetworking: false } : {}),
      color: isNetworking ? '#10B981' : newEventForm.color
    };

    if (recurring) {
      baseEventData.recurring = recurring;
    }

    // Optimistic update - create temporary ID
    const tempId = `temp-${Date.now()}`;
    const optimisticEvent: CalendarEvent = {
      id: tempId,
      ...baseEventData
    };

    // Update UI immediately
    setEvents((prev: CalendarEvent[]) => [...prev, optimisticEvent]);
    setShowEventModal(false);

    try {
      // Sla op in Firestore zodat agenda-synchronisatie en reloads werken
      const docRef = await addDoc(collection(db, 'calendarEvents'), baseEventData);
      // Journey linkage (if connectionId present)
      if (baseEventData.connectionId) {
        try {
          const { linkActiveEvent, setConnectionPhaseStatus, setConnectionPhase } = await import('../services/connectionJourneyService');
          await linkActiveEvent(baseEventData.connectionId, docRef.id);
          await setConnectionPhaseStatus(baseEventData.connectionId, 'scheduled');
          await setConnectionPhase(baseEventData.connectionId, 'call', 'scheduled');
        } catch (err) {
          console.warn('⚠️ kon journey linkage niet zetten voor event', err);
        }
      }

      const newEvent: CalendarEvent = {
        id: docRef.id,
        ...baseEventData
      };

      // Replace temporary event with real one
      setEvents((prev: CalendarEvent[]) => 
        prev.map(e => e.id === tempId ? newEvent : e)
      );

      // Maak ook een inbox reminder-item aan (voor Inbox -> Reminders)
      if (reminderEnabled && userProfile?.uid) {
        try {
          const inboxItem = {
            userId: userProfile.uid,
            type: 'reminder',
            category: newEvent.type === 'podcast' ? 'podcast'
              : newEvent.type === 'meeting' ? 'meeting'
              : newEvent.type === 'call' ? 'call'
              : 'system',
            title: `⏰ Reminder - ${newEvent.title}`,
            message: `Je afspraak "${newEvent.title}" begint om ${newEvent.startTime.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}.`,
            isRead: false,
            priority: 'high',
            createdAt: new Date(),
            metadata: {
              eventDate: newEvent.startTime,
              color: newEvent.color,
              isRecurring: !!recurring,
              repeat: newEventForm.repeat
            }
          };
          await addDoc(collection(db, 'inboxItems'), inboxItem);
        } catch (err) {
          console.error('Error creating inbox reminder item:', err);
        }
      }

      // Sla ook een compacte versie op in localStorage voor globale notificaties
      try {
        const notificationPayload = {
          id: newEvent.id,
          title: newEvent.title,
          startTime: newEvent.startTime.toISOString(),
          type: newEvent.type,
          category: newEvent.category
        };
        localStorage.setItem('lastAgendaEvent', JSON.stringify(notificationPayload));
      } catch {
        // localStorage is optioneel; fouten hier mogen UI niet breken
      }

      showToast('✅ Afspraak succesvol aangemaakt', 'success');
    } catch (error) {
      console.error('Error saving new calendar event:', error);
      // Rollback on error
      setEvents((prev: CalendarEvent[]) => prev.filter(e => e.id !== tempId));
      setShowEventModal(true);
      showToast('❌ Er ging iets mis bij het opslaan van het agendapunt', 'error');
    }
  };

  const handleNewEventCancel = () => {
    setShowEventModal(false);
  };

  // Task functions
  const openNewTaskModal = () => {
    setNewTaskForm({
        title: '',
        description: '',
      deadline: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 7 dagen van nu
      priority: 'medium',
      status: 'todo'
    });
    setShowTaskModal(true);
  };

  const handleNewTaskSave = async () => {
    if (!newTaskForm.title.trim()) {
      alert('Voer een titel in voor de taak');
      return;
    }

    const taskData = {
      name: newTaskForm.title, // Use 'name' instead of 'title'
      title: newTaskForm.title, // Keep for compatibility
      category: 'Algemeen',
      color: '#3B82F6',
      description: newTaskForm.description,
      status: newTaskForm.status,
      priority: newTaskForm.priority,
      assignee: userProfile?.uid || 'current-user',
      deadline: newTaskForm.deadline,
      createdAt: new Date(),
      updatedAt: new Date()
    };

    try {
      // Save to Firebase
      const docRef = await addDoc(collection(db, 'planningTasks'), taskData);

      const newTask: PlanningTask = {
        id: docRef.id,
        ...taskData
      };

      // Update local state
      setPlanningTasks((prev: PlanningTask[]) => [...prev, newTask]);
      setShowTaskModal(false);
      showToast('✅ Taak succesvol aangemaakt', 'success');
    } catch (error) {
      console.error('Error saving planning task:', error);
      showToast('❌ Er ging iets mis bij het opslaan van de taak', 'error');
    }
  };

  const handleNewTaskCancel = () => {
    setShowTaskModal(false);
  };

  // Podcast functions
  const openPodcastModal = () => {
    setPodcastForm({
      title: '',
      description: '',
      date: new Date().toISOString().split('T')[0],
      time: '14:00',
      duration: '60',
      topic: '',
      mainSpeaker: '',
      guests: []
    });
    setShowPodcastModal(true);
  };

  // Guest management functions
  const addGuest = () => {
    if (podcastForm.guests.length < 5) {
      setPodcastForm(prev => ({
        ...prev,
        guests: [...prev.guests, { name: '', email: '' }]
      }));
    }
  };

  const removeGuest = (index: number) => {
    setPodcastForm(prev => ({
      ...prev,
      guests: prev.guests.filter((_, i) => i !== index)
    }));
  };

  const updateGuest = (index: number, field: 'name' | 'email', value: string) => {
    setPodcastForm(prev => ({
      ...prev,
      guests: prev.guests.map((guest, i) => 
        i === index ? { ...guest, [field]: value } : guest
      )
    }));
  };

  const handlePodcastSave = () => {
    if (!podcastForm.title.trim()) {
      alert('Voer een titel in voor de podcast');
      return;
    }

    if (!podcastForm.date || !podcastForm.time) {
      alert('Selecteer een datum en tijd voor de podcast');
      return;
    }

    // Check availability
    const selectedDateTime = `${podcastForm.date}T${podcastForm.time}`;
    if (podcastAvailability.blockedDates.includes(podcastForm.date)) {
      alert('Deze datum is niet beschikbaar voor podcast opnames');
      return;
    }

    const startDateTime = new Date(selectedDateTime);
    // Add 30 minutes buffer for arrival/departure time
    const totalDuration = parseInt(podcastForm.duration) + 30;
    const endDateTime = new Date(startDateTime.getTime() + totalDuration * 60 * 1000);

    const newPodcastEvent: CalendarEvent = {
      id: Date.now().toString(),
      title: podcastForm.title,
      description: `${podcastForm.description}${podcastForm.description ? '\n\n' : ''}Podcast duur: ${podcastForm.duration} minuten + 30 minuten voorbereidingstijd`,
      startTime: startDateTime,
      endTime: endDateTime,
      type: 'podcast',
      participants: [],
      participantNames: [],
      status: 'scheduled',
      createdBy: 'current-user',
      createdAt: new Date(),
      reminderSent: false,
      color: '#8B5CF6'
    };

    setEvents((prev: CalendarEvent[]) => [...prev, newPodcastEvent]);
    setShowPodcastModal(false);
  };

  const handlePodcastCancel = () => {
    setShowPodcastModal(false);
  };

  // Admin functions for podcast availability
  const toggleAdminMode = () => {
    setPodcastAvailability(prev => ({ ...prev, adminMode: !prev.adminMode }));
  };

  const blockDate = (date: string) => {
    setPodcastAvailability(prev => ({
      ...prev,
      blockedDates: [...prev.blockedDates, date]
    }));
  };

  const unblockDate = (date: string) => {
    setPodcastAvailability(prev => ({
      ...prev,
      blockedDates: prev.blockedDates.filter(d => d !== date)
    }));
  };

  const addAvailableTime = (time: string) => {
    setPodcastAvailability(prev => ({
      ...prev,
      availableTimes: [...prev.availableTimes, time].sort()
    }));
  };

  const removeAvailableTime = (time: string) => {
    setPodcastAvailability(prev => ({
      ...prev,
      availableTimes: prev.availableTimes.filter(t => t !== time)
    }));
  };

  // Check if date/time is available
  const isDateTimeAvailable = (date: string, time: string) => {
    if (podcastAvailability.blockedDates.includes(date)) return false;
    if (!podcastAvailability.availableTimes.includes(time)) return false;
    
    // Check if there's already a podcast scheduled at this time
    const existingEvent = events.find(event => {
      const eventDate = event.startTime.toISOString().split('T')[0];
      const eventTime = event.startTime.toTimeString().slice(0, 5);
      return eventDate === date && eventTime === time && event.type === 'podcast';
    });
    
    return !existingEvent;
  };

  // Get available times for a specific date
  const getAvailableTimesForDate = (date: string) => {
    return podcastAvailability.availableTimes.filter(time => 
      isDateTimeAvailable(date, time)
    );
  };

  // Podcast request functions
  const submitPodcastRequest = () => {
    if (!podcastForm.title.trim()) {
      alert('Voer een titel in voor de podcast');
      return;
    }

    if (!podcastForm.date || !podcastForm.time) {
      alert('Selecteer een datum en tijd voor de podcast');
      return;
    }

    if (!isDateTimeAvailable(podcastForm.date, podcastForm.time)) {
      alert('Deze datum/tijd is niet beschikbaar');
      return;
    }

    const newRequest: PodcastRequest = {
      id: Date.now().toString(),
      title: podcastForm.title,
      description: podcastForm.description,
      requestedDate: podcastForm.date,
      requestedTime: podcastForm.time,
      duration: parseInt(podcastForm.duration),
      topic: podcastForm.topic,
      mainSpeaker: podcastForm.mainSpeaker,
      guests: podcastForm.guests.filter(guest => guest.name.trim() !== ''), // Only include guests with names
      requesterId: 'current-user',
      requesterName: userProfile?.displayName || 'Gebruiker',
      status: 'pending',
      createdAt: new Date()
    };

    setPodcastAvailability(prev => ({
      ...prev,
      requests: [...prev.requests, newRequest]
    }));

    setShowPodcastModal(false);
    alert('Podcast aanvraag is ingediend! Je ontvangt een bevestiging zodra deze is goedgekeurd.');
  };

  const approveRequest = (requestId: string) => {
    const request = podcastAvailability.requests.find(r => r.id === requestId);
    if (!request) return;

    // Create the actual podcast event
    const startDateTime = new Date(`${request.requestedDate}T${request.requestedTime}`);
    // Add 30 minutes buffer for arrival/departure time
    const totalDuration = request.duration + 30;
    const endDateTime = new Date(startDateTime.getTime() + totalDuration * 60 * 1000);

    const newPodcastEvent: CalendarEvent = {
      id: Date.now().toString(),
      title: request.title,
      description: `${request.description}${request.description ? '\n\n' : ''}Podcast duur: ${request.duration} minuten + 30 minuten voorbereidingstijd`,
      startTime: startDateTime,
      endTime: endDateTime,
      type: 'podcast',
      participants: [],
      participantNames: [],
      status: 'scheduled',
      createdBy: request.requesterId,
        createdAt: new Date(),
      reminderSent: false,
      color: '#8B5CF6'
    };

    setEvents((prev: CalendarEvent[]) => [...prev, newPodcastEvent]);

    // Update request status
    setPodcastAvailability(prev => ({
      ...prev,
      requests: prev.requests.map(r => 
        r.id === requestId ? { ...r, status: 'approved' as const } : r
      )
    }));
  };

  const rejectRequest = (requestId: string) => {
    setPodcastAvailability(prev => ({
      ...prev,
      requests: prev.requests.map(r => 
        r.id === requestId ? { ...r, status: 'rejected' as const } : r
      )
    }));
  };

  // Advanced admin functions
  const getDayAvailability = (date: string) => {
    const dayOfWeek = new Date(date).toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
    const template = podcastAvailability.templates.find(t => t.days.includes(dayOfWeek));
    
    return {
      availableTimes: template?.availableTimes || podcastAvailability.availableTimes,
      blockedTimes: template?.blockedTimes || [],
      isBlocked: podcastAvailability.blockedDates.includes(date)
    };
  };

  const toggleTimeSlot = (date: string, time: string, type: 'available' | 'blocked') => {
    const dayAvailability = getDayAvailability(date);
    
    if (type === 'available') {
      // Remove from blocked, add to available
      const newBlockedTimes = dayAvailability.blockedTimes.filter(t => t !== time);
      const newAvailableTimes = dayAvailability.availableTimes.includes(time) 
        ? dayAvailability.availableTimes 
        : [...dayAvailability.availableTimes, time].sort();
      
      // Update template or global settings
      updateDayTemplate(date, newAvailableTimes, newBlockedTimes);
    } else {
      // Remove from available, add to blocked
      const newAvailableTimes = dayAvailability.availableTimes.filter(t => t !== time);
      const newBlockedTimes = dayAvailability.blockedTimes.includes(time)
        ? dayAvailability.blockedTimes
        : [...dayAvailability.blockedTimes, time].sort();
      
      updateDayTemplate(date, newAvailableTimes, newBlockedTimes);
    }
  };

  const updateDayTemplate = (date: string, availableTimes: string[], blockedTimes: string[]) => {
    const dayOfWeek = new Date(date).toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
    const existingTemplate = podcastAvailability.templates.find(t => t.days.includes(dayOfWeek));
    
    if (existingTemplate) {
      // Update existing template
      setPodcastAvailability(prev => ({
        ...prev,
        templates: prev.templates.map(t => 
          t.id === existingTemplate.id 
            ? { ...t, availableTimes, blockedTimes }
            : t
        )
      }));
    } else {
      // Create new template for this day
      const newTemplate: AvailabilityTemplate = {
        id: Date.now().toString(),
        name: `${dayOfWeek.charAt(0).toUpperCase() + dayOfWeek.slice(1)} Template`,
        description: `Template for ${dayOfWeek}`,
        availableTimes,
        blockedTimes,
        days: [dayOfWeek],
        isDefault: false
      };
      
      setPodcastAvailability(prev => ({
        ...prev,
        templates: [...prev.templates, newTemplate]
      }));
    }
  };

  const blockWeek = (startDate: string) => {
    const start = new Date(startDate);
    const blockedDates: string[] = [];
    
    for (let i = 0; i < 7; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      blockedDates.push(date.toISOString().split('T')[0]);
    }
    
    setPodcastAvailability(prev => ({
      ...prev,
      blockedDates: [...prev.blockedDates, ...blockedDates]
    }));
  };

  const unblockWeek = (startDate: string) => {
    const start = new Date(startDate);
    const weekDates: string[] = [];
    
    for (let i = 0; i < 7; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      weekDates.push(date.toISOString().split('T')[0]);
    }
    
    setPodcastAvailability(prev => ({
      ...prev,
      blockedDates: prev.blockedDates.filter(date => !weekDates.includes(date))
    }));
  };

  const applyTemplate = (templateId: string, date: string) => {
    const template = podcastAvailability.templates.find(t => t.id === templateId);
    if (!template) return;
    
    updateDayTemplate(date, template.availableTimes, template.blockedTimes);
  };

  const createTemplate = (name: string, description: string, days: string[], availableTimes: string[], blockedTimes: string[]) => {
    const newTemplate: AvailabilityTemplate = {
      id: Date.now().toString(),
      name,
      description,
      availableTimes,
      blockedTimes,
      days,
      isDefault: false
    };
    
    setPodcastAvailability(prev => ({
      ...prev,
      templates: [...prev.templates, newTemplate]
    }));
  };

  // Drag and drop handlers
  const handleDragStart = (type: 'available' | 'blocked', time: string) => {
    setDraggedItem({ type, time });
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Set dropEffect to show copy cursor
    e.dataTransfer.dropEffect = 'copy';
    console.log('🟡 Drag over calendar');
  };

  const handleDrop = async (e: React.DragEvent, date: string) => {
    e.preventDefault();
    console.log('🟢 Drop triggered on date:', date);
    
    // Bestaande logic (BEHOUDEN) - time slot dragging
    if (draggedItem) {
      console.log('🟢 Old drag system (time slots)');
      toggleTimeSlot(date, draggedItem.time, draggedItem.type);
      setDraggedItem(null);
      return;
    }
    
    // ⚠️ GEANKERD: Defensieve drag payload validatie
    // Onbekende payloads worden GENEGEERD (niet verwerkt)
    const validatedPayload = parseDragData(e);
    
    if (!validatedPayload) {
      console.log('⚠️ Unknown or invalid drag payload - IGNORED (anchored behavior)');
      return;
    }
    
    console.log('✅ Validated drag payload:', validatedPayload.type);
    
    // Handle Doelstellingen actions (GEANKERD)
    if (isDoelstellingenPayload(validatedPayload)) {
      const data = validatedPayload;
        // Parse date string as local date (avoid UTC timezone issues)
        const [year, month, day] = date.split('-').map(Number);
        const startTime = new Date(year, month - 1, day, 9, 0, 0, 0);
        const endTime = new Date(year, month - 1, day, 10, 0, 0, 0);
        
        // Check of deze actie al bestaat voor deze specifieke datum
        const targetDateStr = startTime.toDateString();
        const alreadyScheduledForDate = events.some(
          (event) =>
            event.doelstellingenMeta?.actionId === data.actionId &&
            event.status !== 'cancelled' &&
            event.startTime.toDateString() === targetDateStr
        );

        if (alreadyScheduledForDate) {
          showToast('⚠️ Deze actie staat al op deze datum in je agenda', 'info');
          return;
        }
        
        const eventData = {
          title: `${data.actionTitle}`,
          description: `Doel: ${data.goalTitle}${data.milestoneTitle ? `\nMijlpaal: ${data.milestoneTitle}` : ''}`,
          startTime,
          endTime,
          type: 'planning' as const,
          category: 'action' as const,
          participants: [userProfile?.uid || ''],
          participantNames: [],
          status: 'scheduled' as const,
          createdBy: userProfile?.uid || '',
          createdAt: new Date(),
          reminderSent: false,
          doelstellingenMeta: {
            actionId: data.actionId,
            goalId: data.goalId,
            milestoneId: data.milestoneId,
          },
        };
        
        try {
          // Save to Firebase - realtime listener will update UI
          const docRef = await addDoc(collection(db, 'calendarEvents'), eventData);
          console.log('🟢 Doelstellingen event created:', docRef.id);
          showToast('✅ Actie toegevoegd aan agenda', 'success');
        } catch (err) {
          console.error('Error creating calendar event:', err);
          showToast('❌ Fout bij toevoegen aan agenda', 'error');
        }
        return;
      }
      
      // Handle normal tasks (GEANKERD)
      if (isAgendaTaskPayload(validatedPayload)) {
        const data = validatedPayload;
        console.log('🟢 Creating calendar event for task:', data.taskTitle);
        
        // Parse date string as local date (avoid UTC timezone issues)
        const [year, month, day] = date.split('-').map(Number);
        const startTime = new Date(year, month - 1, day, 9, 0, 0, 0);
        const endTime = new Date(year, month - 1, day, 10, 0, 0, 0);
        
        // Check of deze taak al bestaat voor deze specifieke datum
        const targetDateStr = startTime.toDateString();
        const alreadyScheduledForDate = events.some(
          (event) =>
            event.linkedTaskId === data.taskId &&
            event.status !== 'cancelled' &&
            event.startTime.toDateString() === targetDateStr
        );

        if (alreadyScheduledForDate) {
          showToast('⚠️ Deze taak staat al op deze datum in je agenda', 'info');
          return;
        }
        
        const eventData = {
          title: `${data.taskTitle}`,
          description: `Van: ${data.listName}`,
          startTime,
          endTime,
          type: 'planning' as const,
          category: 'action' as const,
          participants: [userProfile?.uid || ''],
          participantNames: [],
          status: 'scheduled' as const,
          createdBy: userProfile?.uid || '',
          createdAt: new Date(),
          reminderSent: false,
          linkedTaskId: data.taskId,
          linkedTaskMeta: {
            listId: data.listId,
            listName: data.listName,
            balanceCategory: data.balanceCategory,
          },
        };
        
        try {
          // Save to Firebase - realtime listener will update UI
          const docRef = await addDoc(collection(db, 'calendarEvents'), eventData);
          console.log('🟢 Calendar event created:', docRef.id);
          showToast('✅ Taak toegevoegd aan agenda', 'success');
        } catch (err) {
          console.error('Error creating calendar event:', err);
          showToast('❌ Fout bij toevoegen aan agenda', 'error');
        }
        return;
      }
      
      // Als we hier komen is het een onbekend payload type (zou niet moeten met gevalideerde payloads)
      console.log('⚠️ Unhandled validated payload type');
  };

  const handleDayDetailDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    if (!selectedDayDetails) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
    e.currentTarget.classList.add('bg-blue-50', 'ring-2', 'ring-blue-200');
  };

  const handleDayDetailDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    if (!selectedDayDetails) return;
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.classList.remove('bg-blue-50', 'ring-2', 'ring-blue-200');
  };

  const handleDayDetailDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    if (!selectedDayDetails) return;
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.classList.remove('bg-blue-50', 'ring-2', 'ring-blue-200');
    await handleDrop(e, toLocalDateString(selectedDayDetails));
  };

  // Specifieke handlers voor Afspraken drop zone
  const handleAfsprakenDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    // Check of er drag data is (types bevat 'text/plain' wanneer er data is)
    if (e.dataTransfer.types.includes('text/plain')) {
      e.dataTransfer.dropEffect = 'copy';
      setIsDraggingOverAfspraken(true);
    }
  };

  const handleAfsprakenDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOverAfspraken(false);
  };

  const handleAfsprakenDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    if (!selectedDayDetails) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOverAfspraken(false);
    
    // Check of het een actie is
    try {
      const dragData = e.dataTransfer.getData('text/plain');
      if (dragData) {
        const parsed = JSON.parse(dragData);
        if (parsed.type === 'doelstellingen-action' || parsed.type === 'agenda-task') {
          // Drop de actie (gaat automatisch naar Taken via category: 'action')
          await handleDrop(e, toLocalDateString(selectedDayDetails));
          // Automatisch Taken tab openen
          setDayViewTab('tasks');
          showToast('✅ Actie toegevoegd aan Taken', 'success');
          return;
        }
      }
    } catch (err) {
      // Geen geldige drag data, gebruik normale drop
    }
    
    // Normale drop voor andere items
    await handleDrop(e, toLocalDateString(selectedDayDetails));
  };

  // Specifieke handlers voor Taken drop zone
  const handleTakenDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    // Check of er drag data is (types bevat 'text/plain' wanneer er data is)
    if (e.dataTransfer.types.includes('text/plain')) {
      e.dataTransfer.dropEffect = 'copy';
      setIsDraggingOverTaken(true);
    }
  };

  const handleTakenDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOverTaken(false);
  };

  const handleTakenDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    if (!selectedDayDetails) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOverTaken(false);
    
    // Check of het een actie is
    try {
      const dragData = e.dataTransfer.getData('text/plain');
      if (dragData) {
        const parsed = JSON.parse(dragData);
        if (parsed.type === 'doelstellingen-action' || parsed.type === 'agenda-task') {
          // Drop de actie (gaat automatisch naar Taken via category: 'action')
          await handleDrop(e, toLocalDateString(selectedDayDetails));
          // Zorg dat Taken tab actief is
          setDayViewTab('tasks');
          showToast('✅ Actie toegevoegd aan Taken', 'success');
          return;
        }
      }
    } catch (err) {
      // Geen geldige drag data, gebruik normale drop
    }
    
    // Normale drop voor andere items
    await handleDrop(e, toLocalDateString(selectedDayDetails));
  };

  const deleteEvent = async (eventId: string, scope: 'single' | 'series' = 'single', occurrenceDate?: Date) => {
    // Zoek het event VOORDAT we state updaten
    const eventToDelete = events.find(e => e.id === eventId);
    
    if (!eventToDelete) {
      console.warn('Event niet gevonden:', eventId);
      return;
    }
    if (isLockedConnectionEvent(eventToDelete)) {
      showToast('⚠️ Dit event beheer je via de connectie-flow.', 'info');
      if (eventToDelete.connectionId) {
        openInConnectieFlow(eventToDelete);
      }
      return;
    }

    try {
      const eventRef = doc(db, 'calendarEvents', eventId);

      // CASE 1: Geen recurring event OF hele reeks verwijderen → ECHT VERWIJDEREN
      if (!eventToDelete.recurring || scope === 'series') {
        // Verwijder uit Firebase EERST
        await deleteDoc(eventRef);
        console.log('Event permanent verwijderd uit Firebase:', eventId);

        // Update lokale state - filter het event volledig weg
        setEvents((prev: CalendarEvent[]) => prev.filter(e => e.id !== eventId));
        showToast(scope === 'series' ? '✅ Hele reeks verwijderd' : '✅ Afspraak verwijderd', 'success');
        return;
      }

      // CASE 2: Alleen deze occurrence verwijderen uit een recurring reeks
      if (occurrenceDate) {
        const targetDateStr = occurrenceDate.toDateString();
        const existingExcluded = eventToDelete.recurring?.excludedDates || [];

        // Check of al excluded
        const alreadyExcluded = existingExcluded.some(d => d.toDateString() === targetDateStr);
        if (alreadyExcluded) {
          console.log('Datum was al excluded:', targetDateStr);
          return;
        }

        // Maak nieuwe excluded dates array
        const newExcludedDate = new Date(
          occurrenceDate.getFullYear(), 
          occurrenceDate.getMonth(), 
          occurrenceDate.getDate()
        );
        const updatedExcluded = [...existingExcluded, newExcludedDate];

        // Update Firebase EERST
        await updateDoc(eventRef, {
          'recurring.excludedDates': updatedExcluded
        });
        console.log('Occurrence excluded in Firebase:', targetDateStr);

        // Update lokale state
        setEvents((prev: CalendarEvent[]) =>
          prev.map(e =>
            e.id === eventId
              ? {
                  ...e,
                  recurring: {
                    ...e.recurring!,
                    excludedDates: updatedExcluded
                  }
                }
              : e
          )
        );
        showToast('✅ Occurrence verwijderd uit reeks', 'success');
      }
    } catch (err) {
      console.error('FOUT bij verwijderen event:', err);
      showToast('❌ Er ging iets mis bij het verwijderen', 'error');
    }
  };

  const updateEventTime = async (eventId: string, newDate: Date, newHour: number) => {
    const event = events.find(e => e.id === eventId);
    if (!event) return;
    if (event.lockedToConnectionFlow || event.connectionId) {
      showToast('⚠️ Dit event is gekoppeld aan een connectieflow en kan hier niet verplaatst worden. Open in ConnectieFlow.', 'info');
      return;
    }

    const newStartTime = new Date(newDate);
    newStartTime.setHours(newHour, 0, 0, 0);
    
    const duration = event.endTime.getTime() - event.startTime.getTime();
    const newEndTime = new Date(newStartTime.getTime() + duration);

    try {
      // Update in Firebase
      const eventRef = doc(db, 'calendarEvents', eventId);
      await updateDoc(eventRef, {
        startTime: newStartTime,
        endTime: newEndTime
      });
      if (event.connectionId) {
        try {
          const { setConnectionPhaseStatus } = await import('../services/connectionJourneyService');
          await setConnectionPhaseStatus(event.connectionId, 'scheduled');
        } catch (err) {
          console.warn('⚠️ kon phaseStatus niet bijwerken na verplaatsing', err);
        }
      }

      // Update local state
      setEvents((prev: CalendarEvent[]) => prev.map(ev => {
        if (ev.id === eventId) {
          return {
            ...ev,
            startTime: newStartTime,
            endTime: newEndTime
          };
        }
        return ev;
      }));
      showToast('✅ Tijd bijgewerkt', 'success');
    } catch (error) {
      console.error('Error updating event time:', error);
      showToast('❌ Er ging iets mis bij het bijwerken van de tijd', 'error');
    }
  };

  const getViewModeLabel = () => {
    switch (viewMode) {
      case 'week': return '1 Week';
      case 'biweek': return '2 Weken';
      case 'month': return 'Maand';
      default: return '2 Weken';
    }
  };

  const toggleCalendarVisibility = (type: keyof typeof calendarVisibility) => {
    setCalendarVisibility(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  const moveTask = async (taskId: string, newStatus: 'todo' | 'in-progress' | 'completed') => {
    try {
      // Update in Firebase
      const taskRef = doc(db, 'planningTasks', taskId);
      await updateDoc(taskRef, {
        status: newStatus,
        updatedAt: new Date()
      });

      // Update local state
      setPlanningTasks(tasks => 
        tasks.map(task => 
          task.id === taskId ? { ...task, status: newStatus, updatedAt: new Date() } : task
        )
      );
      console.log(`Task ${taskId} moved to ${newStatus}`);
      showToast('✅ Taakstatus bijgewerkt', 'success');
    } catch (error) {
      console.error('Error updating task status:', error);
      showToast('❌ Er ging iets mis bij het bijwerken van de taak', 'error');
    }
  };

  const openTaskModal = (task: PlanningTask) => {
    setSelectedTask(task);
    setShowTaskModal(true);
  };

  const updateTask = async (updatedTask: PlanningTask) => {
    try {
      // Update in Firebase
      const taskRef = doc(db, 'planningTasks', updatedTask.id);
      await updateDoc(taskRef, {
        ...updatedTask,
        updatedAt: new Date()
      });

      // Update local state
      setPlanningTasks(tasks => 
        tasks.map(task => 
          task.id === updatedTask.id ? { ...updatedTask, updatedAt: new Date() } : task
        )
      );
      showToast('✅ Taak bijgewerkt', 'success');
    } catch (error) {
      console.error('Error updating planning task:', error);
      showToast('❌ Er ging iets mis bij het bijwerken van de taak', 'error');
    }
  };

  const deletePlanningTask = async (taskId: string) => {
    if (!window.confirm('Weet je zeker dat je deze taak wilt verwijderen?')) {
      return;
    }

    try {
      // Delete from Firebase
      await deleteDoc(doc(db, 'planningTasks', taskId));

      // Update local state
      setPlanningTasks(tasks => tasks.filter(task => task.id !== taskId));
      showToast('✅ Taak verwijderd', 'success');
    } catch (error) {
      console.error('Error deleting planning task:', error);
      showToast('❌ Er ging iets mis bij het verwijderen van de taak', 'error');
    }
  };

  const getEventsForDate = (date: Date) => {
    const targetDateStr = date.toDateString();
    const dayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    const calendarEvents: CalendarEvent[] = [];

    visibleEvents.forEach(event => {
      if (!isVisibleEvent(event, showCompleted, showCancelled)) return;
      if (!calendarVisibility[event.type as keyof typeof calendarVisibility]) {
        return;
      }
      
      // ⚠️ FILTER: Events gekoppeld aan taken worden NIET getoond in Afspraken tab
      // Deze verschijnen in de Taken tab via getTasksForDay()
      if (event.linkedTaskId || event.doelstellingenMeta) {
        return;
      }

      const baseStart = event.startTime;
      const baseEnd = event.endTime;

      // Geen herhaling: gewone datumvergelijking
      if (!event.recurring) {
        if (baseStart.toDateString() === targetDateStr) {
          calendarEvents.push(event);
        }
        return;
      }

      const recurring = event.recurring;
      if (!recurring) return;

      // Excluded dates: specifieke occurrences overslaan
      if (recurring.excludedDates && recurring.excludedDates.length > 0) {
        const isExcluded = recurring.excludedDates.some(d => d.toDateString() === targetDateStr);
        if (isExcluded) return;
      }

      // Herhalende events: check of deze dag binnen het patroon valt
      const baseDate = new Date(
        baseStart.getFullYear(),
        baseStart.getMonth(),
        baseStart.getDate()
      );

      // Te vroeg?
      if (dayStart < baseDate) return;

      // Te laat?
      if (recurring.endDate) {
        const endDate = new Date(
          recurring.endDate.getFullYear(),
          recurring.endDate.getMonth(),
          recurring.endDate.getDate()
        );
        if (dayStart > endDate) return;
      }

      let occurs = false;
      switch (recurring.frequency) {
        case 'daily':
          occurs = true;
          break;
        case 'weekly':
          occurs = dayStart.getDay() === baseDate.getDay();
          break;
        case 'biweekly': {
          const msPerDay = 86400000;
          const daysDiff = Math.round((dayStart.getTime() - baseDate.getTime()) / msPerDay);
          occurs = daysDiff >= 0 && daysDiff % 14 === 0;
          break;
        }
        case 'monthly':
          occurs = dayStart.getDate() === baseDate.getDate();
          break;
        default:
          occurs = false;
      }

      if (!occurs) return;

      // Maak een occurrence op deze dag met dezelfde start-/eindtijd
      const occurrenceStart = new Date(dayStart);
      occurrenceStart.setHours(baseStart.getHours(), baseStart.getMinutes(), 0, 0);
      const duration = baseEnd.getTime() - baseStart.getTime();
      const occurrenceEnd = new Date(occurrenceStart.getTime() + duration);

      calendarEvents.push({
        ...event,
        startTime: occurrenceStart,
        endTime: occurrenceEnd
      });
    });

    const meetingEvents: CalendarEvent[] = meetingRequests
      .filter(request => 
        request.proposedDate.toDateString() === date.toDateString() &&
        calendarVisibility.meetings &&
        request.status === 'accepted'
      )
      .map(request => ({
        id: request.id,
        title: request.title,
        description: request.description,
        startTime: new Date(request.proposedDate),
        endTime: new Date(request.proposedDate.getTime() + request.duration * 60000),
        type: request.type as CalendarEvent['type'],
        category: undefined,
        domain: undefined,
        source: 'superkonnected',
        participants: [request.fromUserId, request.toUserId],
        participantNames: [request.fromUserId, request.toUserId], // Will be populated with actual names
        location: request.location,
        meetingUrl: request.meetingLink,
        status: request.status as CalendarEvent['status'],
        createdBy: request.fromUserId,
        createdAt: request.createdAt,
        reminderSent: false,
        color: getMeetingTypeColor(request.type),
        recurring: undefined
      } as CalendarEvent));
    
    return [...calendarEvents, ...meetingEvents];
  };

  const getMeetingTypeColor = (type: string) => {
    switch (type) {
      case 'chat': return '#10B981';
      case 'call': return '#3B82F6';
      case 'video': return '#8B5CF6';
      case 'meeting': return '#F59E0B';
      default: return '#6B7280';
    }
  };

  const getWeekDates = (date: Date, biweek = false) => {
    const week = [];
    const startOfWeek = new Date(date);
    const day = startOfWeek.getDay();
    const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1);
    startOfWeek.setDate(diff);

    const daysToShow = biweek ? 14 : 7;
    for (let i = 0; i < daysToShow; i++) {
      const currentDay = new Date(startOfWeek);
      currentDay.setDate(startOfWeek.getDate() + i);
      week.push(currentDay);
    }
    return week;
  };

  const getMonthDates = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startDay = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;
    
    const days = [];
    
    // Previous month days
    for (let i = startDay - 1; i >= 0; i--) {
      const prevDate = new Date(year, month, -i);
      days.push({ date: prevDate, isCurrentMonth: false });
    }
    
    // Current month days
    for (let day = 1; day <= daysInMonth; day++) {
      const currentDate = new Date(year, month, day);
      days.push({ date: currentDate, isCurrentMonth: true });
    }
    
    // Next month days to fill the grid
    const remainingDays = 42 - days.length;
    for (let day = 1; day <= remainingDays; day++) {
      const nextDate = new Date(year, month + 1, day);
      days.push({ date: nextDate, isCurrentMonth: false });
    }
    
    return days;
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const daysOfWeek = ['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo'];
  const monthNames = [
    'januari', 'februari', 'maart', 'april', 'mei', 'juni',
    'juli', 'augustus', 'september', 'oktober', 'november', 'december'
  ];

  const getSourceColor = (source?: string) => {
    switch (source) {
      case 'google':
        return '#3B82F6'; // blauw
      case 'outlook':
        return '#FACC15'; // geel
      case 'ics':
        return '#F97316'; // oranje
      case 'superkonnected':
      default:
        return '#10B981'; // groen
    }
  };

  // =========================
  // Task helpers (sidebar)
  // =========================

  const addTaskToActiveList = async () => {
    if (!newTaskTitle.trim() || activeTaskListId === 'done' || activeTaskListId === 'all') return;
    
    const taskData = {
      title: newTaskTitle.trim(),
      listId: activeTaskListId === 'all' ? 'activities' : activeTaskListId,
      completed: false,
      completedAt: null,
      userId: userProfile?.uid || 'current-user',
      createdAt: new Date()
    };

    try {
      const docRef = await addDoc(collection(db, 'agendaTasks'), taskData);

      if (shareToMeetingEntityId && userProfile?.uid) {
        const m = userMemberships.find((x) => x.id === shareToMeetingEntityId);
        try {
          await addDoc(collection(db, 'mastermindTasks'), {
            entityId: shareToMeetingEntityId,
            entityName: m?.name ?? '',
            title: newTaskTitle.trim(),
            ownerUid: userProfile.uid,
            ownerName: userProfile.displayName || userProfile.email?.split('@')[0] || 'Lid',
            ownerAvatar: userProfile.photoURL ?? null,
            status: 'open',
            createdFromAgenda: true,
            agendaTaskId: docRef.id,
            createdByUid: userProfile.uid,
            createdByName: userProfile.displayName || userProfile.email?.split('@')[0] || 'Lid',
            createdAt: serverTimestamp(),
          });
        } catch (syncErr) {
          console.error('mastermindTasks mirror from agenda:', syncErr);
          showToast('Taak op agenda gezet; koppeling met meeting-groep mislukte.', 'error');
          return;
        }
      }

      setNewTaskTitle('');
      showToast(shareToMeetingEntityId ? '✅ Taak op agenda én in groepsmeetings' : '✅ Taak toegevoegd', 'success');
    } catch (error) {
      console.error('Error saving agenda task:', error);
      showToast('❌ Er ging iets mis bij het opslaan van de taak', 'error');
    }
  };

  const submitHubGroupTask = async (group: GroupMembership) => {
    const title = (hubQuickAddTitle[group.id] || '').trim();
    if (!title || !userProfile?.uid) return;
    try {
      await addDoc(collection(db, 'mastermindTasks'), {
        entityId: group.id,
        entityName: group.name,
        title,
        ownerUid: userProfile.uid,
        ownerName: userProfile.displayName || userProfile.email?.split('@')[0] || 'Lid',
        ownerAvatar: userProfile.photoURL ?? null,
        status: 'open',
        createdFromAgenda: true,
        createdByUid: userProfile.uid,
        createdByName: userProfile.displayName || userProfile.email?.split('@')[0] || 'Lid',
        createdAt: serverTimestamp(),
      });
      setHubQuickAddTitle((prev) => ({ ...prev, [group.id]: '' }));
      showToast(`Taak toegevoegd voor ${group.name} (ook in meeting)`, 'success');
    } catch (err) {
      console.error('submitHubGroupTask:', err);
      showToast('Opslaan groepstaak mislukt', 'error');
    }
  };

  const toggleTaskCompleted = async (taskId: string) => {
    // Check if it's a Doelstellingen action (pseudo-task)
    const doelAction = doelstellingenActions.find(a => a.id === taskId);
    
    if (doelAction) {
      // This is a Doelstellingen action - use the action completion handler
      console.log('🎯 Completing Doelstellingen action:', doelAction.title);
      await handleCompleteDoelstellingenAction(doelAction);
      return;
    }
    
    // Regular task handling
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;

    // In "klaar" laten we unchecken niet toe
    if (task.completed) return;

    try {
      // Update in Firebase - realtime listener zal automatisch state bijwerken
      const taskRef = doc(db, 'agendaTasks', taskId);
      await updateDoc(taskRef, {
        completed: true,
        completedAt: new Date(),
        originalListId: task.listId,
        listId: 'done'
      });
      
      // NEW: Update gekoppelde calendar events naar 'completed' status
      const linkedEvents = events.filter(e => 
        e.linkedTaskId === taskId || 
        (e.doelstellingenMeta && e.doelstellingenMeta.actionId === taskId)
      );
      
      for (const event of linkedEvents) {
        try {
          const eventRef = doc(db, 'calendarEvents', event.id);
          await updateDoc(eventRef, {
            status: 'completed'
          });
        } catch (err) {
          console.error('Error updating linked event:', err);
        }
      }
      
      showToast('Taak voltooid!', 'success');
    } catch (error) {
      console.error('Error updating task completion:', error);
      showToast('❌ Er ging iets mis bij het bijwerken van de taak', 'error');
    }
  };

  const restoreTask = async (taskId: string) => {
    const task = tasks.find(t => t.id === taskId);
    if (!task || !task.completed) return;

    const targetListId = task.originalListId || 'activities';

    try {
      const taskRef = doc(db, 'agendaTasks', taskId);
      await updateDoc(taskRef, {
        completed: false,
        completedAt: null,
        listId: targetListId
      });
      
      // NEW: Update gekoppelde calendar events terug naar 'scheduled' status
      const linkedEvents = events.filter(e => 
        e.linkedTaskId === taskId || 
        (e.doelstellingenMeta && e.doelstellingenMeta.actionId === taskId)
      );
      
      for (const event of linkedEvents) {
        try {
          const eventRef = doc(db, 'calendarEvents', event.id);
          await updateDoc(eventRef, {
            status: 'scheduled'
          });
        } catch (err) {
          console.error('Error updating linked event:', err);
        }
      }
    } catch (error) {
      console.error('Error restoring task:', error);
      showToast('❌ Er ging iets mis bij het herstellen van de taak', 'error');
    }
  };

  // Timer functies voor taken
  const toggleTaskTimer = async (taskId: string) => {
    const task = tasks.find(t => t.id === taskId);
    if (!task || task.completed) return;

    const now = new Date();
    const taskRef = doc(db, 'agendaTasks', taskId);

    if (task.isTimerRunning && task.timerStartedAt) {
      // Stop timer - bereken tijd en voeg toe aan activeTime
      const elapsed = now.getTime() - new Date(task.timerStartedAt).getTime();
      const newActiveTime = (task.activeTime || 0) + elapsed;
      
      try {
        await updateDoc(taskRef, {
          isTimerRunning: false,
          timerStartedAt: null,
          activeTime: newActiveTime
        });
      } catch (error) {
        console.error('Error stopping timer:', error);
      }
    } else {
      // Start timer
      try {
        await updateDoc(taskRef, {
          isTimerRunning: true,
          timerStartedAt: now
        });
      } catch (error) {
        console.error('Error starting timer:', error);
      }
    }
  };

  // Handle drag start for Doelstellingen actions
  const handleActionDragStart = (
    e: React.DragEvent,
    action: any,
    goal: any,
    milestone?: any
  ) => {
    const dragData = {
      type: 'doelstellingen-action',
      actionId: action.id,
      goalId: goal.id,
      goalTitle: goal.title,
      milestoneId: milestone?.id,
      milestoneTitle: milestone?.title,
      actionTitle: action.title,
    };
    
    e.dataTransfer.setData('text/plain', JSON.stringify(dragData));
    e.dataTransfer.effectAllowed = 'copy'; // Kopie, geen move
  };

  // Handle drag start for normal tasks (NEW)
  const handleTaskDragStart = (
    e: React.DragEvent,
    task: AgendaTask,
    list: AgendaTaskList
  ) => {
    console.log('🔵 Drag started for task:', task.title);
    const dragData = {
      type: 'agenda-task',
      taskId: task.id,
      taskTitle: task.title,
      listId: list.id,
      listName: list.name,
      balanceCategory: list.balanceCategory,
    };
    
    console.log('🔵 Drag data:', dragData);
    e.dataTransfer.setData('text/plain', JSON.stringify(dragData));
    e.dataTransfer.effectAllowed = 'copy';
  };

  // Toggle timer voor Doelstellingen acties - SYNCED TO FIREBASE for meetbaarheid
  const toggleDoelstellingenActionTimer = async (actionId: string) => {
    const action = doelstellingenActions.find((a: any) => a.id === actionId);
    if (!action || action.completed) return;
    
    const now = Date.now();
    const actionRef = doc(db, 'actions', actionId);
    
    // Check: is er al een timer actief (normale taken of andere Doelstellingen actie)?
    const anyTaskTimerRunning = tasks.some(t => t.isTimerRunning);
    const anyDoelstellingenTimerRunning = doelstellingenActions.some((a: any) => a.isTimerRunning && a.id !== actionId);
    
    // Stop alle andere timers eerst
    if (anyTaskTimerRunning) {
      for (const task of tasks) {
        if (task.isTimerRunning) {
          await toggleTaskTimer(task.id);
        }
      }
    }
    
    if (anyDoelstellingenTimerRunning) {
      for (const otherAction of doelstellingenActions) {
        if (otherAction.isTimerRunning && otherAction.id !== actionId) {
          const otherRef = doc(db, 'actions', otherAction.id);
          const elapsed = now - (otherAction.timerStartedAt || 0);
          const newActiveTime = (otherAction.activeTime || 0) + elapsed;
          
          try {
            await updateDoc(otherRef, {
              isTimerRunning: false,
              timerStartedAt: null,
              activeTime: newActiveTime
            });
            // Update local state
            setDoelstellingenActions((prev: any[]) => prev.map(a => 
              a.id === otherAction.id 
                ? { ...a, isTimerRunning: false, timerStartedAt: null, activeTime: newActiveTime }
                : a
            ));
          } catch (error) {
            console.error('Error stopping other timer:', error);
          }
        }
      }
    }
    
    // Toggle deze timer
    if (action.isTimerRunning && action.timerStartedAt) {
      // Stop timer - bereken tijd en voeg toe aan activeTime
      const elapsed = now - action.timerStartedAt;
      const newActiveTime = (action.activeTime || 0) + elapsed;
      
      try {
        await updateDoc(actionRef, {
          isTimerRunning: false,
          timerStartedAt: null,
          activeTime: newActiveTime
        });
        // Update local state for immediate UI update
        setDoelstellingenActions((prev: any[]) => prev.map(a => 
          a.id === actionId 
            ? { ...a, isTimerRunning: false, timerStartedAt: null, activeTime: newActiveTime }
            : a
        ));
      } catch (error) {
        console.error('Error stopping timer:', error);
      }
    } else {
      // Start timer
      try {
        await updateDoc(actionRef, {
          isTimerRunning: true,
          timerStartedAt: now
        });
        // Update local state for immediate UI update
        setDoelstellingenActions((prev: any[]) => prev.map(a => 
          a.id === actionId 
            ? { ...a, isTimerRunning: true, timerStartedAt: now }
            : a
        ));
      } catch (error) {
        console.error('Error starting timer:', error);
      }
    }
  };

  // Formatteer actieve tijd voor weergave
  const formatActiveTime = (ms: number, timerStartedAt?: Date | null | any, isRunning?: boolean): string => {
    let totalMs = ms || 0;
    
    // Voeg huidige sessie toe als timer loopt
    if (isRunning && timerStartedAt) {
      try {
        // Handle Firestore Timestamp of Date
        const startTime = timerStartedAt?.toDate?.() || timerStartedAt?.seconds 
          ? new Date(timerStartedAt.seconds * 1000) 
          : new Date(timerStartedAt);
        const elapsed = Date.now() - startTime.getTime();
        if (!isNaN(elapsed)) {
          totalMs += elapsed;
        }
      } catch (e) {
        // Ignore invalid date
      }
    }
    
    if (isNaN(totalMs) || totalMs < 60000) return '<1m';
    
    const minutes = Math.floor(totalMs / 60000);
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    
    if (hours > 0) {
      return remainingMinutes > 0 ? `${hours}u${remainingMinutes}m` : `${hours}u`;
    }
    return `${minutes}m`;
  };

  // Get all tasks scheduled for a specific day (NEW)
  const getTasksForDay = (date: Date): Array<{
    task: AgendaTask;
    list: AgendaTaskList;
    event?: CalendarEvent;
  }> => {
    const dateString = date.toDateString();
    const results: Array<{ task: AgendaTask; list: AgendaTaskList; event?: CalendarEvent }> = [];
    
    console.log('🔵 getTasksForDay called for:', dateString);
    console.log('🔵 Total events:', events.length);
    console.log('🔵 Total tasks:', tasks.length);
    
    // Find events linked to tasks for this day
    const dayEvents = events.filter(e => {
      const eventDate = e.startTime.toDateString();
      const matches = eventDate === dateString && (e.linkedTaskId || e.doelstellingenMeta);
      if (matches) {
        console.log('🔵 Found matching event:', e.title, 'linkedTaskId:', e.linkedTaskId);
      }
      return matches;
    });
    
    console.log('🔵 Day events found:', dayEvents.length);
    
    // Match linked tasks
    dayEvents.forEach(event => {
      if (event.linkedTaskId) {
        console.log('🔵 Looking for task with ID:', event.linkedTaskId);
        const task = tasks.find(t => t.id === event.linkedTaskId);
        const list = taskLists.find(l => l.id === event.linkedTaskMeta?.listId);
        console.log('🔵 Task found:', !!task, 'List found:', !!list);
        if (task && list) {
          results.push({ task, list, event });
          console.log('🔵 Added task to results:', task.title);
        } else {
          console.log('❌ Could not find task or list!', { taskExists: !!task, listExists: !!list });
        }
      }
    });
    
    // Add Doelstellingen actions
    dayEvents.forEach(event => {
      if (event.doelstellingenMeta) {
        const action = doelstellingenActions.find(a => a.id === event.doelstellingenMeta?.actionId);
        const goal = doelstellingenGoals.find(g => g.id === event.doelstellingenMeta?.goalId);
        if (action && goal) {
          // Create pseudo-task representation
          const pseudoTask: AgendaTask = {
            id: action.id,
            title: action.title,
            listId: 'goals',
            completed: action.completed || false,
            createdAt: new Date(),
          };
          const pseudoList: AgendaTaskList = {
            id: 'goals',
            name: `${goal.title}`,
          };
          results.push({ task: pseudoTask, list: pseudoList, event });
        }
      }
    });
    
    return results;
  };

  // Get timer status for a task (NEW)
  const getTaskTimerStatus = (task: AgendaTask): {
    isRunning: boolean;
    displayTime: string;
  } | null => {
    // Check normal task timers
    if (task.isTimerRunning || task.activeTime) {
      return {
        isRunning: task.isTimerRunning || false,
        displayTime: formatActiveTime(task.activeTime || 0, task.timerStartedAt, task.isTimerRunning),
      };
    }
    
    // Check Doelstellingen action timers (now synced with Firebase)
    if (task.listId === 'goals') {
      const action = doelstellingenActions.find((a: any) => a.id === task.id);
      if (action && (action.isTimerRunning || action.activeTime)) {
        return {
          isRunning: action.isTimerRunning || false,
          displayTime: formatActiveTime(action.activeTime || 0, action.timerStartedAt ? new Date(action.timerStartedAt) : null, action.isTimerRunning),
        };
      }
    }
    
    return null;
  };

  // Navigate to sidebar tab where timer controls are (NEW)
  const switchToSidebarTab = (listId: string) => {
    setActiveTaskListId(listId);
    
    // Optional: Scroll sidebar into view
    const sidebar = document.querySelector('[class*="Tasks Sidebar"]');
    if (sidebar) {
      sidebar.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  // Handle task reorder in day view (NEW)
  const handleTaskReorder = (event: DragEndEvent, date: Date) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) return;
    
    const dateString = date.toDateString();
    const dayTasks = getTasksForDay(date);
    const activeIndex = dayTasks.findIndex(t => t.task.id === active.id);
    const overIndex = dayTasks.findIndex(t => t.task.id === over.id);
    
    if (activeIndex === -1 || overIndex === -1) return;
    
    const reordered = arrayMove(dayTasks, activeIndex, overIndex);
    const newOrder = reordered.map(t => t.task.id);
    
    // Save custom order
    setCustomTaskOrder(prev => ({
      ...prev,
      [dateString]: newOrder,
    }));
    
    // Switch to manual mode
    setTaskSortMode('manual');
    
    // Optional: Persist to localStorage
    try {
      const key = `agenda-task-order-${dateString}`;
      localStorage.setItem(key, JSON.stringify(newOrder));
    } catch (error) {
      console.error('Error saving task order:', error);
    }
  };

  const deleteTask = async (taskId: string) => {
    try {
      // Delete from Firebase - realtime listener zal automatisch state bijwerken
      await deleteDoc(doc(db, 'agendaTasks', taskId));
      showToast('✅ Taak verwijderd', 'success');
    } catch (error) {
      console.error('Error deleting agenda task:', error);
      showToast('❌ Er ging iets mis bij het verwijderen van de taak', 'error');
    }
  };

  const editAgendaTaskTitle = async (task: AgendaTask) => {
    const nextTitle = window.prompt('Taak aanpassen', task.title)?.trim();
    if (!nextTitle || nextTitle === task.title) return;

    try {
      await updateDoc(doc(db, 'agendaTasks', task.id), {
        title: nextTitle,
        updatedAt: new Date()
      });
      setTasks(prev => prev.map(item => item.id === task.id ? { ...item, title: nextTitle } : item));
      showToast('✅ Taak bijgewerkt', 'success');
    } catch (error) {
      console.error('Error updating agenda task:', error);
      showToast('❌ Er ging iets mis bij het bijwerken van de taak', 'error');
    }
  };

  const addTaskList = async () => {
    const name = newListName.trim();
    if (!name) return;
    const id = name.toLowerCase().replace(/\s+/g, '-');
    if (taskLists.some(l => l.id === id)) {
      setNewListName('');
      setShowNewListInput(false);
      return;
    }

    const listData = {
      name,
      userId: userProfile?.uid || 'current-user',
      isSystem: false,
      balanceCategory: newListCategory
    };

    try {
      // Save to Firebase
      const docRef = await addDoc(collection(db, 'agendaTaskLists'), listData);

      const list: AgendaTaskList = { id: docRef.id, ...listData };
      setTaskLists(prev => [...prev, list]);
      setActiveTaskListId(docRef.id);
      setNewListName('');
      setNewListCategory('work');
      setShowNewListInput(false);
      showToast('✅ Lijst aangemaakt', 'success');
    } catch (error) {
      console.error('Error saving task list:', error);
      showToast('❌ Er ging iets mis bij het opslaan van de lijst', 'error');
    }
  };

  const deleteTaskList = async (listId: string) => {
    const list = taskLists.find(l => l.id === listId);
    if (!list || list.isSystem) return;

    try {
      // Delete from Firebase
      await deleteDoc(doc(db, 'agendaTaskLists', listId));

      // Delete all tasks in this list
      const tasksToDelete = tasks.filter(task => task.listId === listId);
      for (const task of tasksToDelete) {
        await deleteDoc(doc(db, 'agendaTasks', task.id));
      }

      // Update local state - realtime listener handelt tasks automatisch af
      setTaskLists(prev => prev.filter(l => l.id !== listId));
      if (activeTaskListId === listId) {
        setActiveTaskListId('activities');
      }
      showToast('✅ Lijst verwijderd', 'success');
    } catch (error) {
      console.error('Error deleting task list:', error);
      showToast('❌ Er ging iets mis bij het verwijderen van de lijst', 'error');
    }
  };

  const moveTaskList = (listId: string, direction: 'up' | 'down') => {
    setTaskLists(prev => {
      const index = prev.findIndex(l => l.id === listId);
      if (index === -1) return prev;

      const newIndex = direction === 'up' ? index - 1 : index + 1;
      if (newIndex < 0 || newIndex >= prev.length) return prev;

      const updated = [...prev];
      const [moved] = updated.splice(index, 1);
      updated.splice(newIndex, 0, moved);
      return updated;
    });
  };

  const getTasksForActiveList = () => {
    if (activeTaskListId === 'done') {
      return tasks.filter(task => task.listId === 'done' && task.completed);
    }
    if (activeTaskListId === 'all') {
      return tasks.filter(task => task.listId !== 'done' && !task.completed);
    }
    return tasks.filter(task => task.listId === activeTaskListId && !task.completed);
  };

  const getFilteredDoneTasks = () => {
    const now = new Date();
    let startDate: Date;

    // Bepaal startdatum op basis van tijdsinterval
    if (doneFilterTimeInterval === 'week') {
      startDate = new Date(now);
      startDate.setDate(now.getDate() - 7);
    } else if (doneFilterTimeInterval === 'month') {
      startDate = new Date(now);
      startDate.setMonth(now.getMonth() - 1);
    } else {
      // year
      startDate = new Date(now);
      startDate.setFullYear(now.getFullYear() - 1);
    }

    return tasks.filter(task => {
      // Moet voltooid zijn
      if (!task.completed || !task.completedAt) return false;
      
      // Moet in tijdsinterval vallen
      if (task.completedAt < startDate) return false;

      // Categorie filter (dynamisch - werkt met alle bestaande en toekomstige tabbladen)
      if (doneFilterCategory !== 'all') {
        // Gebruik originalListId als beschikbaar, anders listId
        const taskOrigin = task.originalListId || (task.listId !== 'done' ? task.listId : null);
        if (taskOrigin !== doneFilterCategory) {
          return false;
        }
      }

      return true;
    }).sort((a, b) => {
      // Nieuwste eerst
      const dateA = a.completedAt ? new Date(a.completedAt).getTime() : 0;
      const dateB = b.completedAt ? new Date(b.completedAt).getTime() : 0;
      return dateB - dateA;
    });
  };

  const getDoneIntervalStart = () => {
    const now = new Date();
    let startDate;

    if (doneFilterTimeInterval === 'week') {
      startDate = new Date(now);
      startDate.setDate(now.getDate() - 7);
    } else if (doneFilterTimeInterval === 'month') {
      startDate = new Date(now);
      startDate.setMonth(now.getMonth() - 1);
    } else {
      startDate = new Date(now);
      startDate.setFullYear(now.getFullYear() - 1);
    }

    return startDate;
  };

  const getCompletedDoelstellingenForInterval = () => {
    const startDate = getDoneIntervalStart();

    return doelstellingenActions.filter((action: any) => {
      if (!action.completed || !action.completedAt) return false;
      const completedAt =
        typeof action.completedAt === 'number'
          ? new Date(action.completedAt)
          : action.completedAt?.toDate
            ? action.completedAt.toDate()
            : new Date(action.completedAt);
      return completedAt >= startDate;
    });
  };

  const getDoneTasksByMonth = () => {
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

    const groups: { [key: string]: { label: string; items: AgendaTask[] } } =
      {};

    tasks.forEach(task => {
      if (!task.completed || task.listId !== 'done' || !task.completedAt) {
        return;
      }
      if (task.completedAt < oneYearAgo) {
        return; // automatisch "verlopen" na een jaar
      }

      const year = task.completedAt.getFullYear();
      const month = task.completedAt.getMonth();
      const key = `${year}-${month}`;
      const label = `${monthNames[month]} ${year}`;

      if (!groups[key]) {
        groups[key] = { label, items: [] };
      }
      groups[key].items.push(task);
    });

    return Object.entries(groups)
      .sort(([a], [b]) => (a > b ? -1 : 1)) // nieuwste maand eerst
      .map(([key, value]) => ({ key, ...value }));
  };

  // Render functions
  const renderCalendarView = () => {
    if (viewMode === 'month') {
      return renderMonthView();
    } else {
      return renderWeekView();
    }
  };

  const renderMobileGoogleCalendarView = () => {
    const monthDates = getMonthDates(currentDate);
    const currentMonthDates = monthDates.filter(dayInfo => dayInfo.isCurrentMonth);
    const selected = selectedDayDetails || new Date();
    const visibleMobileDates =
      mobileCalendarMode === 'day'
        ? [selected]
        : mobileCalendarMode === 'week'
        ? getWeekDates(selected, false)
        : currentMonthDates.map(dayInfo => dayInfo.date);
    const selectedEvents = getEventsForDate(selected)
      .slice()
      .sort((a, b) => a.startTime.getTime() - b.startTime.getTime());
    const selectedTasks = getTasksForDay(selected);

    const navigateMobileCalendar = (direction: number) => {
      const next = new Date(selected);
      if (mobileCalendarMode === 'day') {
        next.setDate(next.getDate() + direction);
      } else if (mobileCalendarMode === 'week') {
        next.setDate(next.getDate() + (direction * 7));
      } else {
        next.setMonth(next.getMonth() + direction);
      }
      setCurrentDate(next);
      setSelectedDayDetails(next);
      setSelectedDate(next);
    };

    const goToToday = () => {
      const today = new Date();
      setCurrentDate(today);
      setSelectedDayDetails(today);
      setSelectedDate(today);
      setDayViewTab('events');
    };

    const renderSelectedTasksCard = (expanded = false) => {
      if (selectedTasks.length === 0) return null;
      const visibleTasks = expanded ? selectedTasks : selectedTasks.slice(0, 3);

      return (
        <div className="mt-4 rounded-3xl border border-[#E5E0D7] bg-white/72 p-4 shadow-sm">
          <div className="mb-3 flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold text-[#2F3A36]">Taken op deze dag</div>
              <div className="text-[11px] text-[#7A7F78]">{selectedTasks.length} taak{selectedTasks.length === 1 ? '' : 'en'}</div>
            </div>
            {!expanded && selectedTasks.length > visibleTasks.length && (
              <button
                type="button"
                onClick={() => setDayViewTab('tasks')}
                className="rounded-full bg-[#F0F7F4] px-3 py-1 text-[11px] font-semibold text-[#4F6660]"
              >
                Bekijk alles
              </button>
            )}
          </div>
          <div className="divide-y divide-[#E5E0D7]">
            {visibleTasks.map(({ task, list, event }) => {
              const isGoalAction = !!event?.doelstellingenMeta;
              const title = (task.title || (event ? getEventDisplayTitle(event, userProfile?.uid) : '') || list.name || 'Taak zonder titel').trim();
              return (
                <div key={`${list.id}-${task.id}`} className="grid grid-cols-[22px_minmax(0,1fr)_auto] items-start gap-3 py-3 first:pt-0 last:pb-0">
                  <button
                    type="button"
                    onClick={() => {
                      if (isGoalAction) {
                        const action = doelstellingenActions.find((item: any) => item.id === task.id);
                        if (action) void handleCompleteDoelstellingenAction(action);
                        return;
                      }
                      toggleTaskCompleted(task.id);
                    }}
                    className="mt-0.5 flex h-5 w-5 items-center justify-center"
                    aria-label="Taak afvinken"
                  >
                    {task.completed ? (
                      <CheckCircle size={17} className="text-[#8DA8A0]" />
                    ) : (
                      <Circle size={17} className="text-[#8DA8A0]" />
                    )}
                  </button>
                  <div className="min-w-0">
                    <div
                      className={`text-sm font-semibold leading-snug ${task.completed ? 'text-[#9BA8A3] line-through' : 'text-[#2F3A36]'}`}
                      style={{ color: task.completed ? '#9BA8A3' : '#2F3A36' }}
                    >
                      {title}
                    </div>
                    {task.note && (
                      <div className="mt-1 line-clamp-2 text-xs text-[#7A7F78]">{task.note}</div>
                    )}
                    <div className="mt-1 text-[10px] font-medium text-[#9BA8A3]">{list.name}</div>
                  </div>
                  <div className="flex items-center gap-1">
                    {!isGoalAction && (
                      <button
                        type="button"
                        onClick={() => editAgendaTaskTitle(task)}
                        className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-white text-[#4F6660] shadow-sm"
                        aria-label="Taak bewerken"
                      >
                        <Edit3 size={13} />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        if (isGoalAction) {
                          toggleDoelstellingenActionTimer(task.id);
                          return;
                        }
                        toggleTaskTimer(task.id);
                      }}
                      className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full shadow-sm ${
                        task.isTimerRunning ? 'bg-[#4F6660] text-white' : 'bg-white text-[#4F6660]'
                      }`}
                      aria-label="Timer starten"
                    >
                      {task.isTimerRunning ? <PauseIcon size={13} /> : <PlayIcon size={13} />}
                    </button>
                    {!isGoalAction && (
                      <button
                        type="button"
                        onClick={() => deleteTask(task.id)}
                        className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-white text-red-400 shadow-sm"
                        aria-label="Taak verwijderen"
                      >
                        <Trash2 size={13} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {!expanded && selectedTasks.length > visibleTasks.length && (
            <button
              type="button"
              onClick={() => setDayViewTab('tasks')}
              className="mt-3 w-full rounded-full bg-[#F0F7F4] py-2 text-xs font-semibold text-[#4F6660]"
            >
              Toon alle {selectedTasks.length} taken
            </button>
          )}
        </div>
      );
    };

    return (
      <div className="md:hidden">
        <div className="sticky top-0 z-10 -mx-3 bg-[#F4F1EA]/95 px-3 pb-2 backdrop-blur">
          <div className="mb-2 flex rounded-full bg-white/50 p-1">
            {([
              { key: 'day' as const, label: 'Dag' },
              { key: 'week' as const, label: 'Week' },
              { key: 'month' as const, label: 'Maand' },
            ]).map(({ key, label }) => (
              <button
                key={key}
                type="button"
                onClick={() => setMobileCalendarMode(key)}
                className={`flex-1 rounded-full px-3 py-1.5 text-[11px] font-semibold transition-colors ${
                  mobileCalendarMode === key ? 'bg-white text-[#4F6660] shadow-sm' : 'text-[#7A7F78]'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="mb-2 grid grid-cols-[44px_minmax(0,1fr)_44px] items-center gap-2">
            <button
              type="button"
              onClick={() => navigateMobileCalendar(-1)}
              className="flex h-9 w-9 items-center justify-center rounded-full bg-white/60 text-[#4F6660]"
              aria-label="Vorige maand"
            >
              <ChevronLeft size={18} />
            </button>
            <div className="min-w-0 text-center">
              <div className="whitespace-nowrap text-base font-semibold leading-tight text-[#2F3A36]">
                {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
              </div>
              <button
                type="button"
                onClick={goToToday}
                className="mt-0.5 whitespace-nowrap text-[11px] font-semibold leading-none text-[#7A9488]"
              >
                Vandaag
              </button>
            </div>
            <button
              type="button"
              onClick={() => navigateMobileCalendar(1)}
              className="ml-auto flex h-9 w-9 items-center justify-center rounded-full bg-white/60 text-[#4F6660]"
              aria-label="Volgende maand"
            >
              <ChevronRight size={18} />
            </button>
          </div>

          <div className="-mx-2 flex gap-2 overflow-x-auto px-2 pb-1" style={{ WebkitOverflowScrolling: 'touch' }}>
            {visibleMobileDates.map((date) => {
              const dayEvents = getEventsForDate(date);
              const tasks = getTasksForDay(date);
              const isSelected = selected.toDateString() === date.toDateString();
              const today = isToday(date);
              return (
                <button
                  key={date.toISOString()}
                  type="button"
                  onClick={() => {
                    setSelectedDayDetails(date);
                    setSelectedDate(date);
                  }}
                  className={`flex min-w-[52px] flex-col items-center rounded-2xl px-2 py-2 transition-all ${
                    isSelected
                      ? 'bg-[#4F6660] text-white shadow-sm'
                      : today
                      ? 'bg-[#F0F7F4] text-[#4F6660]'
                      : 'bg-white/55 text-[#4F6660]'
                  }`}
                >
                  <span className="text-[10px] font-semibold uppercase opacity-75">
                    {date.toLocaleDateString('nl-NL', { weekday: 'short' }).slice(0, 2)}
                  </span>
                  <span className="text-base font-bold leading-tight">{date.getDate()}</span>
                  <span className="mt-1 flex h-1.5 items-center gap-0.5">
                    {dayEvents.length > 0 && <span className={`h-1.5 w-1.5 rounded-full ${isSelected ? 'bg-white' : 'bg-[#7A9488]'}`} />}
                    {tasks.length > 0 && <span className={`h-1.5 w-1.5 rounded-full ${isSelected ? 'bg-white/70' : 'bg-[#C89B6B]'}`} />}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-2 flex rounded-full bg-white/55 p-1">
          <button
            type="button"
            onClick={() => setDayViewTab('events')}
            className={`flex-1 rounded-full px-3 py-2 text-xs font-semibold ${
              dayViewTab === 'events' ? 'bg-white text-[#4F6660] shadow-sm' : 'text-[#7A7F78]'
            }`}
          >
            Afspraken ({selectedEvents.length})
          </button>
          <button
            type="button"
            onClick={() => setDayViewTab('tasks')}
            className={`flex-1 rounded-full px-3 py-2 text-xs font-semibold ${
              dayViewTab === 'tasks' ? 'bg-white text-[#4F6660] shadow-sm' : 'text-[#7A7F78]'
            }`}
          >
            Taken ({selectedTasks.length})
          </button>
        </div>

        <div className="mt-3">
          <div className="mb-3">
            <div className="text-lg font-semibold capitalize text-[#2F3A36]">
              {selected.toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' })}
            </div>
            <div className="text-xs text-[#7A7F78]">
              {dayViewTab === 'events' ? 'Agenda voor deze dag' : 'Taken voor deze dag'}
            </div>
          </div>

          {dayViewTab === 'events' ? (
            <>
              {selectedEvents.length === 0 ? (
                <div className="py-12 text-center">
                  <Calendar size={32} className="mx-auto mb-3 text-[#8DA8A0]" />
                  <div className="text-sm font-semibold text-[#3C4F49]">Geen afspraken</div>
                  <p className="mt-1 text-xs text-[#7A7F78]">Tik op Nieuw agendapunt om iets toe te voegen.</p>
                </div>
              ) : (
                <div className="relative space-y-0">
                  {selectedEvents.map(event => (
                    <div key={event.id} className="flex gap-3 border-b border-[#E5E0D7] py-3 last:border-b-0">
                      <div className="w-14 flex-shrink-0 pt-0.5 text-right">
                        <div className="text-xs font-semibold text-[#4F6660]">
                          {event.startTime.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}
                        </div>
                        <div className="text-[10px] text-[#9BA8A3]">
                          {event.endTime.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => editEvent(event)}
                        className="min-w-0 flex-1 rounded-2xl bg-white/75 px-3 py-2 text-left shadow-sm"
                        style={{ borderLeft: `4px solid ${event.status === 'completed' ? '#9CA3AF' : (event.color || '#8DA8A0')}` }}
                      >
                        <div className={`truncate text-sm font-semibold ${event.status === 'completed' ? 'text-gray-400 line-through' : 'text-[#2F3A36]'}`}>
                          {getEventDisplayTitle(event, userProfile?.uid)}
                        </div>
                        {(event.location || event.meetingUrl) && (
                          <div className="mt-1 flex items-center gap-1 truncate text-[11px] text-[#7A7F78]">
                            <MapPin size={11} />
                            <span className="truncate">{event.location || 'Online afspraak'}</span>
                          </div>
                        )}
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {renderSelectedTasksCard(false)}
            </>
          ) : selectedTasks.length === 0 ? (
            <div className="py-16 text-center">
              <CheckCircle size={32} className="mx-auto mb-3 text-[#8DA8A0]" />
              <div className="text-sm font-semibold text-[#3C4F49]">Geen taken</div>
              <p className="mt-1 text-xs text-[#7A7F78]">Taken voor deze dag verschijnen hier.</p>
            </div>
          ) : renderSelectedTasksCard(true)}
        </div>
      </div>
    );
  };

  const renderWeekView = () => {
    const weekDates = getWeekDates(currentDate, viewMode === 'biweek');
    
    return (
      <div className="space-y-6">
        {/* View Controls */}
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <button
              onClick={toggleViewMode}
              className="px-4 py-2 bg-stone-100 text-[#4F6660] rounded-lg hover:bg-stone-200 "
            >
              {getViewModeLabel()}
            </button>
            <button
              onClick={() => setCurrentDate(new Date())}
              className="px-4 py-2 bg-white text-gray-700 rounded-lg hover:bg-gray-50 "
            >
              Vandaag
            </button>
          </div>
          </div>
          
        {/* Calendar Filters – compact tool mode */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="relative">
            <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none" />
            <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none" />
            
            <div className="flex items-center gap-1.5 px-2 py-1 overflow-x-auto scrollbar-hide">
              <span className="text-xs font-medium text-gray-500 mr-1 flex-shrink-0">Kalenders:</span>
              {Object.entries(calendarVisibility).map(([type, visible]) => (
                <button
                  key={type}
                  onClick={() => toggleCalendarVisibility(type as keyof typeof calendarVisibility)}
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border transition-colors flex-shrink-0 ${
                    visible
                      ? 'bg-stone-50 text-[#4F6660] border-stone-200 shadow-sm'
                      : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
                  }`}
                >
                  {visible ? (
                    <Eye size={12} className="mr-1" />
                  ) : (
                    <EyeOff size={12} className="mr-1" />
                  )}
                  <span className="capitalize">{type}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Historie toggles */}
        <div className="flex flex-wrap items-center gap-3 bg-white p-3 border border-gray-200 rounded-lg">
          <label className="flex items-center space-x-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={showCompleted}
              onChange={(e) => setShowCompleted(e.target.checked)}
              className="rounded border-gray-300 text-[#4F6660] focus:ring-stone-500"
            />
            <span>Toon afgeronde afspraken</span>
          </label>
          <label className="flex items-center space-x-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={showCancelled}
              onChange={(e) => setShowCancelled(e.target.checked)}
              className="rounded border-gray-300 text-[#4F6660] focus:ring-stone-500"
            />
            <span>Toon geannuleerde afspraken</span>
          </label>
          <div className="flex items-center text-xs text-gray-500 space-x-3">
            <span>Actief: {eventStatusCounts.active}</span>
            <span>Afgerond: {eventStatusCounts.completed}</span>
            <span>Geannuleerd: {eventStatusCounts.cancelled}</span>
          </div>
          <button
            onClick={() => {
              setShowCompleted(false);
              setShowCancelled(false);
            }}
            className="ml-auto px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-xs font-medium border border-gray-200"
          >
            Reset filters
          </button>
        </div>

        {/* Calendar Grid - Compact */}
        <div className="bg-white rounded-lg border border-gray-200">
          {/* Week Navigation - Compact */}
          <div className="flex justify-between items-center p-3 border-b">
            <button onClick={() => navigateWeek(-1)} className="p-1 hover:bg-gray-200 rounded-full">
              <ChevronLeft size={16} />
          </button>
            <h4 className="text-base font-semibold">
            {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            </h4>
            <button onClick={() => navigateWeek(1)} className="p-1 hover:bg-gray-200 rounded-full">
              <ChevronRight size={16} />
          </button>
        </div>

          {/* Days header - Compact */}
          <div className="flex border-b border-gray-200">
            {weekDates.map((date, index) => {
              // Get tasks for this day
              const dayTasks = getTasksForDay(date);
              const taskCount = dayTasks.length;
              const hasRunningTimer = dayTasks.some(({ task }) => {
                // Check normal task timer
                if (task.isTimerRunning) return true;
                // Check Doelstellingen action timer
                if (task.listId === 'goals') {
                  const action = doelstellingenActions.find((a: any) => a.id === task.id);
                  return action?.isTimerRunning;
                }
                return false;
              });
              
              return (
                <div 
                  key={index} 
                  className={`flex-1 p-2 text-center border-r border-gray-200 last:border-r-0 cursor-pointer hover:bg-gray-50 transition-colors ${
                    hasRunningTimer ? 'bg-stone-50' : ''
                  }`}
                  onClick={() => {
                    setSelectedDayDetails(date);
                    if (taskCount > 0) {
                      setDayViewTab('tasks');
                    }
                  }}
                >
                  <div className="text-xs text-gray-600 font-medium">
                    {daysOfWeek[date.getDay() === 0 ? 6 : date.getDay() - 1]}
                  </div>
                  <div className={`text-sm font-semibold ${isToday(date) ? 'text-stone-500' : 'text-gray-900'}`}>
                    {date.getDate()}
                  </div>
                  {/* Task indicator */}
                  {taskCount > 0 && (
                    <div className="flex items-center justify-center mt-1 space-x-1">
                      {hasRunningTimer && (
                        <div className="w-2 h-2 bg-stone-500 rounded-full animate-pulse" title="Timer actief" />
                      )}
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                        hasRunningTimer 
                          ? 'bg-stone-100 text-[#4F6660] font-medium' 
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {taskCount} {taskCount === 1 ? 'taak' : 'taken'}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Calendar grid - Fit in viewport with bottom padding */}
          <div className="flex overflow-hidden h-[400px] pb-3">
            {weekDates.map((date, dayIndex) => (
              <div 
                key={dayIndex} 
                className="flex-1 border-r border-gray-200 last:border-r-0 relative"
                onDragOver={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.add('bg-blue-50');
                }}
                onDragLeave={(e) => {
                  e.currentTarget.classList.remove('bg-blue-50');
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.remove('bg-blue-50');
                  
                  try {
                    const data = JSON.parse(e.dataTransfer.getData('text/plain'));
                    if (data.eventType === 'calendarEvent') {
                      // Calculate new time based on drop position
                      const rect = e.currentTarget.getBoundingClientRect();
                      const y = e.clientY - rect.top;
                      const newHour = Math.max(0, Math.min(23, Math.floor(y / 25)));
                      
                      // Update event time
                      updateEventTime(data.eventId, date, newHour);
                    }
                  } catch (error) {
                    console.error('Error parsing drag data:', error);
                  }
                }}
              >
                {getEventsForDate(date).map((event, eventIndex) => {
                  const ghostDates = event.doelstellingenMeta?.ghostDates || [];
                  const ghostDays = ghostDates.length;
                  return (
                    <div
                      key={event.id}
                      className="absolute left-1 right-1 m-1 p-2 rounded-lg shadow-sm hover:shadow-lg transition-all duration-200 group"
                      draggable={!isLockedConnectionEvent(event)}
                      onDragStart={(e) => {
                        if (isLockedConnectionEvent(event)) {
                          e.preventDefault();
                          return;
                        }
                        e.dataTransfer.setData('text/plain', JSON.stringify({
                          eventId: event.id,
                          eventType: 'calendarEvent'
                        }));
                        e.currentTarget.style.opacity = '0.5';
                      }}
                      onDragEnd={(e) => {
                        e.currentTarget.style.opacity = '1';
                      }}
                      style={{
                        top: `${Math.min((event.startTime.getHours() * 25) + 10, 320)}px`,
                        height: `${Math.max(((event.endTime.getHours() - event.startTime.getHours()) * 25), 65)}px`,
                        backgroundColor: `${event.status === 'completed' ? '#9CA3AF' : (event.color || '#3B82F6')}20`,
                        borderLeft: `4px solid ${event.status === 'completed' ? '#9CA3AF' : (event.color || '#3B82F6')}`,
                        zIndex: 1,
                        opacity: event.status === 'completed' ? 0.6 : 1
                      }}
                    >
                    {/* Normal compact view */}
                    <div className="flex flex-col h-full group-hover:hidden overflow-hidden justify-between">
                      <div className="flex-1 flex flex-col">
                        <div className={`text-xs font-medium mb-1 leading-tight break-words ${
                          event.status === 'completed' ? 'line-through text-gray-500' : ''
                        }`}>{getEventDisplayTitle(event, userProfile?.uid)}</div>
                        <div className={`text-xs mb-1 truncate ${
                          event.status === 'completed' ? 'line-through text-gray-500' : 'text-gray-600'
                        }`}>
                          {event.startTime.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })} - {event.endTime.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        {event.type === 'planning' && (
                          <div className="text-xs text-blue-600 font-medium truncate flex items-center gap-1">
                            <DocumentIcon size={12} /> Planning
                          </div>
                        )}
                        <div className="flex space-x-1 ml-auto">
                          {isLockedConnectionEvent(event) ? (
                            <>
                              <span className="text-[11px] text-gray-500 mr-1">Flow-locked</span>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (!event.connectionId) {
                                    showToast('⚠️ Event is gelockt; geen connectionId gevonden.', 'info');
                                    return;
                                  }
                                  openInConnectieFlow(event);
                                }}
                                className="text-[#4F6660] hover:text-[#4F6660] transition-colors text-xs font-medium"
                                title="Open in ConnectieFlow"
                              >
                                ↗ Flow
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  editEvent(event);
                                }}
                                className="text-gray-500 hover:text-blue-600 transition-colors"
                                title="Wijzigen"
                              >
                                <EditIcon size={14} />
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setEventToDelete(event);
                                  setShowDeleteModal(true);
                                }}
                                className="text-gray-500 hover:text-red-600 transition-colors"
                                title="Verwijderen"
                              >
                                🗑️
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                      {ghostDays > 0 && (
                        <div className="text-[10px] text-[#4F6660] mt-1">
                          ⌛ {ghostDays} dag{ghostDays === 1 ? '' : 'en'} vertraging
                        </div>
                      )}
                    </div>

                    {/* Expanded hover view */}
                    <div className="flex flex-col hidden group-hover:flex absolute top-0 left-0 bg-white rounded-lg shadow-lg border border-gray-200 p-3 w-64 max-w-64 z-20">
                      <div className="flex items-start justify-between mb-2">
                        <div className={`text-sm font-semibold break-words flex-1 pr-2 ${
                          event.status === 'completed' ? 'line-through text-gray-500' : 'text-gray-900'
                        }`}>{getEventDisplayTitle(event, userProfile?.uid)}</div>
                        <div className="flex space-x-1 ml-2 flex-shrink-0">
                          {isLockedConnectionEvent(event) ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!event.connectionId) {
                                  showToast('⚠️ Event is gelockt; geen connectionId gevonden.', 'info');
                                  return;
                                }
                                openInConnectieFlow(event);
                              }}
                              className="text-[#4F6660] hover:text-[#4F6660] transition-colors text-xs font-medium"
                              title="Open in ConnectieFlow"
                            >
                              ↗ Flow
                            </button>
                          ) : (
                            <>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  editEvent(event);
                                }}
                                className="text-gray-500 hover:text-blue-600 transition-colors"
                                title="Wijzigen"
                              >
                                <EditIcon size={14} />
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setEventToDelete(event);
                                  setShowDeleteModal(true);
                                }}
                                className="text-gray-500 hover:text-red-600 transition-colors"
                                title="Verwijderen"
                              >
                                🗑️
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="text-xs text-gray-600 mb-2">
                        {event.startTime.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })} - {event.endTime.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}
                      </div>
                      {event.description && (
                        <div className="text-xs text-gray-700 mb-2 break-words">
                          {event.description}
                        </div>
                      )}
                      {event.type === 'planning' && (
                        <div className="text-xs text-blue-600 font-medium mb-2 flex items-center gap-1">
                          <DocumentIcon size={12} /> Planning
                        </div>
                      )}
                      {event.type === 'meeting' && event.participants.length > 1 && (
                        <div className="flex flex-col space-y-2 mt-2 pt-2 border-t border-gray-200">
                          <div className="text-xs text-gray-600">
                            👥 Deelnemers: {Array.isArray(event.participantNames) 
                              ? event.participantNames.filter((n: string) => n).join(', ') 
                              : Object.values(event.participantNames || {}).filter(n => n).join(', ') 
                              || event.participants.length + ' personen'}
                          </div>
                          <div className="flex space-x-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                // Open reschedule modal
                                setRescheduleEvent(event);
                                setRescheduleForm({
                                  newStartDate: event.startTime.toISOString().split('T')[0],
                                  newStartTime: event.startTime.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' }),
                                  newEndTime: event.endTime.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' }),
                                  reason: ''
                                });
                                setShowRescheduleModal(true);
                              }}
                              className="flex-1 px-2 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600 transition-colors"
                            >
                              📅 Verzetten
                            </button>
                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                if (window.confirm('Weet je zeker dat je deze meeting wilt annuleren?')) {
                                  try {
                                    // Update event status to cancelled
                                    await updateDoc(doc(db, 'calendarEvents', event.id), {
                                      status: 'cancelled',
                                      updatedAt: new Date()
                                    });
                                    
                                    // If there's a meetingRequestId, update that too
                                    if ((event as any).meetingRequestId) {
                                      await updateDoc(doc(db, 'meetingRequests', (event as any).meetingRequestId), {
                                        status: 'declined',
                                        updatedAt: new Date()
                                      });
                                    }
                                    
                                    showToast('✅ Meeting geannuleerd', 'success');
                                  } catch (error) {
                                    console.error('Error cancelling meeting:', error);
                                    showToast('❌ Fout bij annuleren meeting', 'error');
                                  }
                                }
                              }}
                              className="px-2 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600 transition-colors"
                            >
                              ❌ Annuleren
                            </button>
                          </div>
                        </div>
                      )}
                      {event.type === 'networking' && event.title === 'Vrij voor netwerken' && (
                        <div className="flex items-center justify-between mt-2 p-2 bg-stone-50 rounded border border-stone-200">
                          <label className="text-xs text-gray-700 flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={event.autoAcceptNetworking || false}
                              onChange={async (e) => {
                                e.stopPropagation();
                                try {
                                  await updateDoc(doc(db, 'calendarEvents', event.id), {
                                    autoAcceptNetworking: e.target.checked
                                  });
                                  showToast(
                                    e.target.checked 
                                      ? '✅ Automatisch planning vullen ingeschakeld' 
                                      : '⚠️ Automatisch planning vullen uitgeschakeld',
                                    'success'
                                  );
                                } catch (error) {
                                  console.error('Error updating autoAcceptNetworking:', error);
                                  showToast('❌ Fout bij bijwerken instelling', 'error');
                                }
                              }}
                              className="w-4 h-4 text-[#4F6660] rounded focus:ring-stone-500"
                            />
                            <span className="font-medium">Automatisch planning vullen</span>
                          </label>
                          <span className="text-xs text-gray-500">
                            {event.autoAcceptNetworking ? '✅ Aan' : '❌ Uit'}
                          </span>
                        </div>
                      )}
                      <div className="text-xs text-gray-500 mt-auto">
                        Sleep om te verplaatsen
                      </div>
                    </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
      </div>
    </div>
  );
  };

  const renderMonthView = () => {
    const monthDates = getMonthDates(currentDate);
    
    return (
      <>
      {renderMobileGoogleCalendarView()}
      <div className="hidden space-y-1 md:block">
        {/* View Controls — compact */}
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <button
              onClick={toggleViewMode}
              className="px-3 py-1 bg-stone-100 text-[#4F6660] rounded-lg hover:bg-stone-200 text-sm"
            >
              {getViewModeLabel()}
            </button>
            <button
              onClick={() => setCurrentDate(new Date())}
              className="px-3 py-1 bg-white text-gray-700 rounded-lg hover:bg-gray-50 text-sm"
            >
              Vandaag
            </button>
          </div>
        </div>

        {/* Calendar Filters – compact tool mode */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="relative">
            <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none" />
            <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none" />
            
            <div className="flex items-center gap-1.5 px-2 py-1 overflow-x-auto scrollbar-hide">
              <span className="text-xs font-medium text-gray-500 mr-1 flex-shrink-0">Kalenders:</span>
              {Object.entries(calendarVisibility).map(([type, visible]) => (
                <button
                  key={type}
                  onClick={() => toggleCalendarVisibility(type as keyof typeof calendarVisibility)}
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border transition-colors flex-shrink-0 ${
                    visible
                      ? 'bg-stone-50 text-[#4F6660] border-stone-200 shadow-sm'
                      : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
                  }`}
                >
                  {visible ? (
                    <Eye size={12} className="mr-1" />
                  ) : (
                    <EyeOff size={12} className="mr-1" />
                  )}
                  <span className="capitalize">{type}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Month Calendar + Dagdetails in 2 kolommen (links maand, midden daglijst) */}
        <div className="mint-agenda-grid grid grid-cols-1 gap-2 items-start">
          {/* Linker kolom: compacte maandkalender */}
          <div>
            <div className="bg-white rounded-lg" style={{ border: '1px solid #E9E7E2', boxShadow: '0 4px 12px rgba(0,0,0,0.04)' }}>
              {/* Month Navigation — tool mode compact */}
              <div className="flex justify-between items-center px-3 py-1" style={{ borderBottom: '1px solid #ECEAE5', background: 'rgba(147,174,163,0.03)' }}>
                <button onClick={() => navigateWeek(-1)} className="p-1 hover:bg-gray-200 rounded-full">
                  <ChevronLeft size={16} />
                </button>
                <h4 style={{ fontSize: '0.95rem', fontWeight: 600, color: '#3C4F49', letterSpacing: '0.015em' }}>
                  {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
                </h4>
                <button onClick={() => navigateWeek(1)} className="p-1 hover:bg-gray-200 rounded-full">
                  <ChevronRight size={16} />
                </button>
              </div>

              {/* Days header — tool mode compact */}
              <div className="grid grid-cols-7 border-b border-gray-200">
                {daysOfWeek.map(day => (
                  <div
                    key={day}
                    className="py-1 px-1 text-center font-medium text-gray-600 border-r border-gray-200 last:border-r-0"
                    style={{ fontSize: '0.65rem' }}
                  >
                    {day}
                  </div>
                ))}
              </div>

              {/* Month grid - Compact */}
              <div className="grid grid-cols-7 gap-0">
                {monthDates.map((dayInfo, index) => {
                  const dayEvents = getEventsForDate(dayInfo.date);
                  const hasEvents = dayEvents.length > 0;
                  const isSelected =
                    selectedDayDetails &&
                    dayInfo.date.toDateString() ===
                      selectedDayDetails.toDateString();

                  return (
                    <div
                      key={index}
                      className={`h-14 p-1 border-r border-b border-gray-200 text-xs ${
                        !dayInfo.isCurrentMonth
                          ? 'bg-gray-50 text-gray-300'
                          : 'bg-white text-gray-900'
                      } ${isToday(dayInfo.date) ? 'bg-stone-50' : ''}`}
                      onDragOver={e => {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = 'copy';
                        e.currentTarget.classList.add('bg-blue-50');
                      }}
                      onDragLeave={e => {
                        e.currentTarget.classList.remove('bg-blue-50');
                      }}
                      onDrop={async (e) => {
                        e.preventDefault();
                        e.currentTarget.classList.remove('bg-blue-50');
                        
                        // Use local date format to avoid timezone issues
                        const year = dayInfo.date.getFullYear();
                        const month = String(dayInfo.date.getMonth() + 1).padStart(2, '0');
                        const day = String(dayInfo.date.getDate()).padStart(2, '0');
                        const dateString = `${year}-${month}-${day}`;
                        
                        // Call the unified handleDrop function
                        await handleDrop(e, dateString);
                      }}
                      onClick={() =>
                        dayInfo.isCurrentMonth &&
                        setSelectedDayDetails(dayInfo.date)
                      }
                    >
                      <div className="flex justify-center mb-1">
                        <div
                          className={`flex items-center justify-center text-xs font-medium w-6 h-6 ${
                            isSelected
                              ? 'rounded-full'
                              : ''
                          }`}
                          style={
                            isSelected
                              ? { backgroundColor: '#8DA8A0', color: '#FFFFFF' }
                              : isToday(dayInfo.date)
                              ? { color: '#7A9488', fontWeight: 600 }
                              : {}
                          }
                        >
                          {dayInfo.date.getDate()}
                        </div>
                      </div>

                      {/* Kleine streepjes per kalenderbron (multi-calendar indicators) */}
                      <div className="flex items-center justify-center mt-1 space-x-0.5">
                        {hasEvents &&
                          Array.from(
                            new Set(
                              dayEvents.map(ev => ((ev as any).source as string) || 'superkonnected')
                            )
                          ).map(sourceId => (
                            <span
                              key={sourceId}
                              className="inline-flex w-3 h-0.5 rounded-full"
                              style={{ backgroundColor: getSourceColor(sourceId) }}
                            />
                          ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Rechter kolom: duidelijke daglijst (boven naar beneden) */}
          <div>
            {selectedDayDetails && (
              <div className="bg-white rounded-lg p-3 flex flex-col" style={{ border: '1px solid #F0EEE9', boxShadow: '0 2px 8px rgba(0,0,0,0.025)', minHeight: 'auto' }}>
                {/* Datum header — compact */}
                <h4 className="text-sm font-semibold text-gray-900 mb-1">
                  {selectedDayDetails.toLocaleDateString('nl-NL', {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long'
                  })}
                </h4>
                
                {/* NIEUW: Tabs met counts */}
                <div className="flex space-x-1 mb-3 border-b border-gray-200">
                  <button
                    onClick={() => setDayViewTab('events')}
                    className={`flex items-center space-x-2 px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                      dayViewTab === 'events'
                        ? 'border-stone-500 text-[#4F6660]'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <Calendar size={16} />
                    <span>Afspraken</span>
                    <span className={`ml-1 px-1.5 py-0.5 text-xs rounded-full ${
                      dayViewTab === 'events' ? 'bg-stone-100 text-[#4F6660]' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {getEventsForDate(selectedDayDetails).length}
                    </span>
                  </button>
                  
                  <button
                    onClick={() => setDayViewTab('tasks')}
                    className={`flex items-center space-x-2 px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                      dayViewTab === 'tasks'
                        ? 'border-stone-500 text-[#4F6660]'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <CheckCircle size={16} />
                    <span>Taken</span>
                    <span className={`ml-1 px-1.5 py-0.5 text-xs rounded-full ${
                      dayViewTab === 'tasks' ? 'bg-stone-100 text-[#4F6660]' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {getTasksForDay(selectedDayDetails).length}
                    </span>
                  </button>
                </div>
                
                {/* Tab Content */}
                <div
                  className={`flex-1 overflow-y-auto transition-all duration-150 ${
                    dayViewTab === 'events' && isDraggingOverAfspraken 
                      ? 'animate-pulse-slow-3x ring-2 ring-[#7A9488] rounded-lg p-2' 
                      : dayViewTab === 'tasks' && isDraggingOverTaken
                      ? 'animate-pulse-slow-3x ring-2 ring-[#7A9488] rounded-lg p-2'
                      : ''
                  }`}
                  onDragOver={dayViewTab === 'events' ? handleAfsprakenDragOver : dayViewTab === 'tasks' ? handleTakenDragOver : handleDayDetailDragOver}
                  onDragLeave={dayViewTab === 'events' ? handleAfsprakenDragLeave : dayViewTab === 'tasks' ? handleTakenDragLeave : handleDayDetailDragLeave}
                  onDrop={dayViewTab === 'events' ? handleAfsprakenDrop : dayViewTab === 'tasks' ? handleTakenDrop : handleDayDetailDrop}
                >
                  {dayViewTab === 'events' ? (
                    // BESTAANDE Afspraken view (BEHOUDEN, exact zoals was)
                    <div className="min-h-full flex flex-col">
                      {getEventsForDate(selectedDayDetails).length === 0 ? (
                        <div className={`text-sm text-gray-500 flex items-center justify-center h-full ${
                          isDraggingOverAfspraken ? 'flex-col gap-2' : ''
                        }`}>
                          {isDraggingOverAfspraken && (
                            <div className="text-2xl mb-2 animate-pulse-slow-3x">➕</div>
                          )}
                          <p>Geen afspraken voor deze dag.</p>
                          {isDraggingOverAfspraken && (
                            <p className="text-xs text-[#4F6660] mt-1">Laat los om naar Taken te verplaatsen</p>
                          )}
                        </div>
                      ) : (
                        // Max 6 afspraken zichtbaar, daarna scrollbar
                        <div className="space-y-2 max-h-[420px] overflow-y-auto pr-2 scrollbar-always">
                          {getEventsForDate(selectedDayDetails).map(event => (
                          <div
                            key={event.id}
                            className={`flex items-center justify-between border rounded-lg px-3 py-2 ${
                              event.status === 'completed'
                                ? 'border-gray-200 bg-gray-50 opacity-60'
                                : 'border-gray-100 bg-gray-50'
                            }`}
                            style={{
                              borderLeft: `4px solid ${event.status === 'completed' ? '#9CA3AF' : (event.color || '#3B82F6')}`,
                              boxShadow: '0 0 0 1px rgba(0,0,0,0.01)'
                            }}
                          >
                            <div className="flex-1 mr-2">
                              <div className={`text-xs font-semibold ${
                                event.status === 'completed' ? 'line-through text-gray-500' : 'text-gray-900'
                              }`}>
                                {event.startTime.toLocaleTimeString('nl-NL', {
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}{' '}
                                -{' '}
                                {event.endTime.toLocaleTimeString('nl-NL', {
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </div>
                              <div className={`text-xs truncate ${
                                event.status === 'completed' ? 'line-through text-gray-500' : 'text-gray-700'
                              }`}>
                                {getEventDisplayTitle(event, userProfile?.uid)}
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); editEvent(event); }}
                                className="text-xs text-blue-600 hover:text-blue-800"
                              >
                                Wijzigen
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setEventToDelete(event);
                                  setShowDeleteModal(true);
                                }}
                                className="text-xs text-gray-400 hover:text-red-600"
                                title="Verwijderen"
                              >
                                🗑️
                              </button>
                            </div>
                          </div>
                          ))}
                          {/* Lege ruimte onder afspraken voor drop zone */}
                          {isDraggingOverAfspraken && (
                            <div className="min-h-[100px] p-3 bg-stone-50 border-2 border-stone-300 border-dashed rounded-lg flex items-center justify-center gap-2 animate-pulse-slow-3x">
                              <span className="text-2xl">➕</span>
                              <p className="text-sm text-[#4F6660]">Laat los om naar Taken te verplaatsen</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    // NIEUW: Taken view (wordt verder uitgebreid in Feature 3 & 4)
                    <>
                      {/* Sort mode dropdown (NEW) */}
                      {getTasksForDay(selectedDayDetails).length > 0 && (
                        <div className="flex items-center justify-between mb-3 px-1">
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs text-gray-500">Sortering:</span>
                            {/* Info icon met tooltip - zelfde stijl als dashboard */}
                            <div className="relative group">
                              <button className="text-gray-400 hover:text-gray-600 focus:outline-none transition-colors">
                                <span className="text-sm text-gray-400">i</span>
                              </button>
                              {/* Tooltip - appears on hover */}
                              <div className="absolute left-0 top-full mt-1 hidden group-hover:block z-50">
                                <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-3 w-72">
                                  <div className="space-y-2 text-xs">
                                    <div>
                                      <div className="font-semibold text-gray-800 mb-0.5 flex items-center gap-1"><GoalIcon size={12} className="text-red-500" /> Balans Modus</div>
                                      <p className="text-gray-600">Afwisseling tussen werk, persoonlijk en activiteiten</p>
                                    </div>
                                    <div className="border-t border-gray-100 pt-2">
                                      <div className="font-semibold text-gray-800 mb-0.5">Werk Eerst</div>
                                      <p className="text-gray-600">Werk taken bovenaan, daarna persoonlijk</p>
                                    </div>
                                    <div className="border-t border-gray-100 pt-2">
                                      <div className="font-semibold text-gray-800 mb-0.5">Random</div>
                                      <p className="text-gray-600">Willekeurige volgorde voor variatie</p>
                                    </div>
                                    <div className="border-t border-gray-100 pt-2">
                                      <div className="font-semibold text-gray-800 mb-0.5">Energie</div>
                                      <p className="text-gray-600">Makkelijke taken eerst, energie opbouwen</p>
                                    </div>
                                    <div className="border-t border-gray-100 pt-2">
                                      <div className="font-semibold text-gray-800 mb-0.5">Handmatig</div>
                                      <p className="text-gray-600">Sleep taken zelf in de gewenste volgorde</p>
                                    </div>
                                  </div>
                                  {/* Arrow */}
                                  <div className="absolute -top-1.5 left-4 w-3 h-3 bg-white border-l border-t border-gray-200 transform rotate-45"></div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <select
                              value={taskSortMode}
                              onChange={(e) => setTaskSortMode(e.target.value as SortMode)}
                              className="text-xs px-2 py-1 border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-stone-500"
                            >
                              <option value="balance">Balans Modus</option>
                              <option value="work-first">Werk Eerst</option>
                              <option value="random">Random</option>
                              <option value="energy">Energie</option>
                              <option value="manual">Handmatig</option>
                            </select>
                            
                            {taskSortMode === 'manual' && customTaskOrder[selectedDayDetails.toDateString()] && (
                              <button
                                onClick={() => {
                                  const newOrder = { ...customTaskOrder };
                                  delete newOrder[selectedDayDetails.toDateString()];
                                  setCustomTaskOrder(newOrder);
                                  setTaskSortMode(getSmartSortMode());
                                }}
                                className="text-xs text-gray-500 hover:text-[#4F6660]"
                              >
                                Reset
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                      
                      <div className="min-h-full flex flex-col relative">
                        {getTasksForDay(selectedDayDetails).length === 0 ? (
                          <div className={`flex-1 text-sm text-gray-500 flex items-center justify-center ${
                            isDraggingOverTaken ? 'flex-col gap-2' : ''
                          }`}>
                            {isDraggingOverTaken && (
                              <div className="text-2xl mb-2 animate-pulse-slow-3x">➕</div>
                            )}
                            <p>Geen taken voor deze dag.</p>
                            {isDraggingOverTaken && (
                              <p className="text-xs text-[#4F6660] mt-1">Laat los om toe te voegen</p>
                            )}
                          </div>
                        ) : (
                          <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={(event) => handleTaskReorder(event, selectedDayDetails)}
                          >
                            <SortableContext
                              items={sortTasks(
                                getTasksForDay(selectedDayDetails),
                                taskSortMode,
                                customTaskOrder[selectedDayDetails.toDateString()]
                              ).map(t => t.task.id)}
                              strategy={verticalListSortingStrategy}
                            >
                              {/* Max 6 taken zichtbaar, daarna scrollbar */}
                              <div className="space-y-2 max-h-[420px] overflow-y-auto pr-2 scrollbar-always">
                                {sortTasks(
                                  getTasksForDay(selectedDayDetails),
                                  taskSortMode,
                                  customTaskOrder[selectedDayDetails.toDateString()]
                                ).map(({ task, list, event }) => (
                                  <SortableTaskItem
                                    key={task.id}
                                    task={task}
                                    list={list}
                                    event={event}
                                  />
                                ))}
                                {/* Lege ruimte onder taken voor drop zone */}
                                {isDraggingOverTaken && (
                                  <div className="min-h-[100px] p-3 bg-stone-50 border-2 border-stone-300 border-dashed rounded-lg flex items-center justify-center gap-2 animate-pulse-slow-3x">
                                    <span className="text-2xl">➕</span>
                                    <p className="text-sm text-[#4F6660]">Laat los om toe te voegen</p>
                                  </div>
                                )}
                              </div>
                            </SortableContext>
                          </DndContext>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      </>
    );
  };

  const renderPlanningView = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
            <div>
          <h3 className="text-base font-semibold text-gray-900">Taken Overzicht</h3>
          <p className="text-gray-600 text-sm">Beheer je chat flow ontwikkeling taken</p>
            </div>
          <div className="flex items-center space-x-2">
          <button className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200  text-sm">
            2 Weken
          </button>
            <button
            onClick={openNewTaskModal}
            className="px-3 py-2 bg-stone-500 text-white rounded-lg hover:bg-[#4F6660]  text-sm"
            >
            + Nieuwe Taak
            </button>
          </div>
        </div>

      {/* Task Board - Compact */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {/* To Do Column */}
        <div 
          className="bg-gray-50 rounded-lg p-3"
          onDragOver={(e) => {
            e.preventDefault();
            e.currentTarget.classList.add('bg-blue-50', 'border-2', 'border-dashed', 'border-blue-300');
          }}
          onDragLeave={(e) => {
            e.currentTarget.classList.remove('bg-blue-50', 'border-2', 'border-dashed', 'border-blue-300');
          }}
          onDrop={(e) => {
            e.preventDefault();
            e.currentTarget.classList.remove('bg-blue-50', 'border-2', 'border-dashed', 'border-blue-300');
            const taskId = e.dataTransfer.getData('taskId');
            if (taskId) {
              moveTask(taskId, 'todo');
            }
          }}
        >
        <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-gray-900">Te Doen</h4>
            <span className="bg-gray-200 text-gray-700 px-2 py-1 rounded-full text-sm">
              {planningTasks.filter(t => t.status === 'todo').length}
            </span>
        </div>
        <div className="space-y-3">
            {planningTasks.filter(task => task.status === 'todo').map((task) => (
              <div
                key={task.id}
                onClick={() => openTaskModal(task)}
                className="bg-white p-3 rounded-lg shadow-sm border-l-4 cursor-pointer    "
                style={{ borderColor: task.color }}
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData('taskId', task.id);
                  e.dataTransfer.effectAllowed = 'move';
                  e.currentTarget.style.opacity = '0.5';
                }}
                onDragEnd={(e) => {
                  e.currentTarget.style.opacity = '1';
                }}
              >
            <div className="flex items-start justify-between mb-2">
                  <h5 className="font-medium text-gray-900 text-sm">{task.name}</h5>
                  <span 
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: task.color }}
                  ></span>
            </div>
                <div className="text-xs text-gray-600 mb-2">{task.category}</div>
            <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500">👤 {task.assignee}</span>
                  <span className="text-red-500">📅 {task.deadline}</span>
            </div>
          </div>
            ))}
        </div>
      </div>

      {/* In Progress Column */}
        <div 
          className="bg-stone-50 rounded-lg p-3"
          onDragOver={(e) => {
            e.preventDefault();
            e.currentTarget.classList.add('bg-stone-100', 'border-2', 'border-dashed', 'border-[#7A9488]');
          }}
          onDragLeave={(e) => {
            e.currentTarget.classList.remove('bg-stone-100', 'border-2', 'border-dashed', 'border-[#7A9488]');
          }}
          onDrop={(e) => {
            e.preventDefault();
            e.currentTarget.classList.remove('bg-stone-100', 'border-2', 'border-dashed', 'border-[#7A9488]');
            const taskId = e.dataTransfer.getData('taskId');
            if (taskId) {
              moveTask(taskId, 'in-progress');
            }
          }}
        >
        <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-gray-900">Bezig</h4>
            <span className="bg-stone-200 text-[#4F6660] px-2 py-1 rounded-full text-sm">
              {planningTasks.filter(t => t.status === 'in-progress').length}
            </span>
        </div>
        <div className="space-y-3">
            {planningTasks.filter(task => task.status === 'in-progress').map((task) => (
              <div
                key={task.id}
                onClick={() => openTaskModal(task)}
                className="bg-white p-3 rounded-lg shadow-sm border-l-4 cursor-pointer    "
                style={{ borderColor: task.color }}
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData('taskId', task.id);
                  e.dataTransfer.effectAllowed = 'move';
                  e.currentTarget.style.opacity = '0.5';
                }}
                onDragEnd={(e) => {
                  e.currentTarget.style.opacity = '1';
                }}
              >
            <div className="flex items-start justify-between mb-2">
                  <h5 className="font-medium text-gray-900 text-sm">{task.name}</h5>
                  <span 
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: task.color }}
                  ></span>
            </div>
                <div className="text-xs text-gray-600 mb-2">{task.category}</div>
            <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500">👤 {task.assignee}</span>
                  <span className="text-red-500">📅 {task.deadline}</span>
            </div>
            <div className="mt-2">
              <div className="w-full bg-gray-200 rounded-full h-1">
                <div className="bg-stone-500 h-1 rounded-full" style={{ width: '60%' }}></div>
              </div>
            </div>
          </div>
            ))}
        </div>
      </div>

      {/* Completed Column */}
        <div 
          className="bg-green-50 rounded-lg p-3"
          onDragOver={(e) => {
            e.preventDefault();
            e.currentTarget.classList.add('bg-green-100', 'border-2', 'border-dashed', 'border-green-400');
          }}
          onDragLeave={(e) => {
            e.currentTarget.classList.remove('bg-green-100', 'border-2', 'border-dashed', 'border-green-400');
          }}
          onDrop={(e) => {
            e.preventDefault();
            e.currentTarget.classList.remove('bg-green-100', 'border-2', 'border-dashed', 'border-green-400');
            const taskId = e.dataTransfer.getData('taskId');
            if (taskId) {
              moveTask(taskId, 'completed');
            }
          }}
        >
        <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-gray-900">Voltooid</h4>
            <span className="bg-green-200 text-green-700 px-2 py-1 rounded-full text-sm">
              {planningTasks.filter(t => t.status === 'completed').length}
            </span>
        </div>
        <div className="space-y-3">
            {planningTasks.filter(task => task.status === 'completed').map((task) => (
              <div
                key={task.id}
                onClick={() => openTaskModal(task)}
                className="bg-white p-4 rounded-lg shadow-sm border-l-4 cursor-pointer     opacity-75"
                style={{ borderColor: task.color }}
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData('taskId', task.id);
                  e.dataTransfer.effectAllowed = 'move';
                  e.currentTarget.style.opacity = '0.5';
                }}
                onDragEnd={(e) => {
                  e.currentTarget.style.opacity = '0.75';
                }}
              >
            <div className="flex items-start justify-between mb-2">
                  <h5 className="font-medium text-gray-900 text-sm line-through">{task.name}</h5>
              <span className="text-green-500">✅</span>
            </div>
                <div className="text-xs text-gray-600 mb-2">{task.category}</div>
            <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500">👤 {task.assignee}</span>
              <span className="text-green-500">📅 Voltooid</span>
            </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderPodcastView = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
            <div>
          <h3 className="text-lg font-semibold text-gray-900">Podcast Sessies</h3>
          <p className="text-gray-600">Plan en beheer je podcast events</p>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={toggleAdminMode}
                className={`px-3 py-2 text-sm rounded-lg border ${
                  podcastAvailability.adminMode 
                    ? 'bg-red-100 text-red-700 border-red-300' 
                    : 'bg-gray-100 text-gray-700 border-gray-300'
                }`}
              >
                {podcastAvailability.adminMode ? 'Admin Mode' : 'Admin'}
          </button>
            <button
                onClick={openPodcastModal}
                className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 "
            >
                Plan Podcast
          </button>
        </div>
      </div>

      {/* Advanced Admin Calendar */}
      {podcastAvailability.adminMode && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Admin Calendar</h3>
            <div className="flex space-x-2">
            <button
                onClick={() => setAdminCurrentDate(new Date(adminCurrentDate.getFullYear(), adminCurrentDate.getMonth() - 1, 1))}
                className="px-2 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
              >
                ←
              </button>
              <span className="px-3 py-1 text-sm font-medium">
                {adminCurrentDate.toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' })}
              </span>
              <button
                onClick={() => setAdminCurrentDate(new Date(adminCurrentDate.getFullYear(), adminCurrentDate.getMonth() + 1, 1))}
                className="px-2 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
              >
                →
            </button>
          </div>
        </div>

          {adminCalendarView === 'month' ? (
            <div className="grid grid-cols-7 gap-1">
              {/* Days of week header */}
              {['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'].map(day => (
                <div key={day} className="p-2 text-center text-sm font-medium text-gray-500 bg-gray-50">
                  {day}
      </div>
              ))}
              
              {/* Calendar days */}
              {Array.from({ length: 35 }, (_, i) => {
                const date = new Date(adminCurrentDate.getFullYear(), adminCurrentDate.getMonth(), i - 6);
                // Use local date format to avoid timezone issues
                const dateString = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                const dayAvailability = getDayAvailability(dateString);
                const isCurrentMonth = date.getMonth() === adminCurrentDate.getMonth();
                const today = new Date();
                const todayString = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
                const isToday = dateString === todayString;
                
                return (
                  <div
                    key={dateString}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, dateString)}
                    className={`p-2 min-h-[80px] border border-gray-200 ${
                      isCurrentMonth ? 'bg-white' : 'bg-gray-50'
                    } ${isToday ? 'ring-2 ring-purple-500' : ''} ${
                      dayAvailability.isBlocked ? 'bg-red-50 border-red-200' : ''
                    }`}
                  >
                    <div className="text-sm font-medium mb-1">
                      {date.getDate()}
      </div>

                    {/* Simple day status */}
                    <div className="text-center">
                      {dayAvailability.isBlocked ? (
                        <div className="text-xs bg-red-200 text-red-700 px-2 py-1 rounded cursor-pointer hover:bg-red-300"
                             onClick={() => unblockDate(dateString)}>
                          🚫 GEBLOKKEERD
              </div>
                      ) : (
                        <div className="text-xs bg-green-200 text-green-700 px-2 py-1 rounded cursor-pointer hover:bg-green-300"
                             onClick={() => blockDate(dateString)}>
                          ✅ OPEN
              </div>
                      )}
      </div>
    </div>
  );
              })}
            </div>
          ) : (
            <div className="grid grid-cols-7 gap-2">
              {/* Week view */}
              {Array.from({ length: 7 }, (_, i) => {
                const date = new Date(adminCurrentDate);
                date.setDate(adminCurrentDate.getDate() - adminCurrentDate.getDay() + i);
                // Use local date format to avoid timezone issues
                const dateString = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                const dayAvailability = getDayAvailability(dateString);
                const today = new Date();
                const todayString = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
                const isToday = dateString === todayString;
                
                return (
                  <div
                    key={dateString}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, dateString)}
                    className={`p-3 border rounded-lg ${
                      isToday ? 'ring-2 ring-purple-500' : 'border-gray-200'
                    } ${dayAvailability.isBlocked ? 'bg-red-50 border-red-200' : 'bg-white'}`}
                  >
                    <div className="text-sm font-medium mb-2">
                      {date.toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric' })}
        </div>

                    {/* Simple day status */}
                    <div className="text-center">
                      {dayAvailability.isBlocked ? (
                        <div className="text-xs bg-red-200 text-red-700 px-2 py-1 rounded cursor-pointer hover:bg-red-300"
                             onClick={() => unblockDate(dateString)}>
                          🚫 GEBLOKKEERD
      </div>
                      ) : (
                        <div className="text-xs bg-green-200 text-green-700 px-2 py-1 rounded cursor-pointer hover:bg-green-300"
                             onClick={() => blockDate(dateString)}>
                          ✅ OPEN
    </div>
                      )}
          </div>
        </div>
                );
              })}
          </div>
          )}
        </div>
      )}

      {/* Admin Panel for Podcast Requests */}
      {podcastAvailability.adminMode && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Podcast Aanvragen Beheer</h3>
          <div className="space-y-4">
            {podcastAvailability.requests.length === 0 ? (
              <p className="text-gray-500 text-center py-4">Geen aanvragen</p>
            ) : (
              podcastAvailability.requests.map((request) => (
                <div key={request.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h4 className="font-medium text-gray-900">{request.title}</h4>
                      <p className="text-sm text-gray-600">Door: {request.requesterName}</p>
          </div>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      request.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                      request.status === 'approved' ? 'bg-green-100 text-green-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {request.status === 'pending' ? 'In behandeling' :
                       request.status === 'approved' ? 'Goedgekeurd' : 'Afgewezen'}
                    </span>
      </div>

                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium">Datum:</span> {new Date(request.requestedDate).toLocaleDateString('nl-NL')}
          </div>
                    <div>
                      <span className="font-medium">Tijd:</span> {request.requestedTime}
          </div>
                    <div>
                      <span className="font-medium">Duur:</span> {request.duration} minuten
          </div>
                    <div>
                      <span className="font-medium">Onderwerp:</span> {request.topic}
        </div>
      </div>

                  {request.description && (
                    <div className="mt-2">
                      <span className="font-medium text-sm">Beschrijving:</span>
                      <p className="text-sm text-gray-600 mt-1">{request.description}</p>
            </div>
                  )}
                  
                  <div className="mt-2">
                    <span className="font-medium text-sm">Host:</span>
                    <p className="text-sm text-gray-600">{request.mainSpeaker}</p>
          </div>

                  {request.guests.length > 0 && (
                    <div className="mt-2">
                      <span className="font-medium text-sm">Gasten ({request.guests.length}):</span>
                      <div className="mt-1 space-y-1">
                        {request.guests.map((guest, index) => (
                          <p key={index} className="text-sm text-gray-600">
                            • {guest.name} ({guest.email})
                          </p>
                        ))}
            </div>
            </div>
                  )}
                  
                  {request.status === 'pending' && (
                    <div className="flex space-x-2 mt-3">
          <button
                        onClick={() => approveRequest(request.id)}
                        className="px-3 py-1 bg-green-500 text-white text-sm rounded hover:bg-green-600"
          >
                        Goedkeuren
          </button>
          <button 
                        onClick={() => rejectRequest(request.id)}
                        className="px-3 py-1 bg-red-500 text-white text-sm rounded hover:bg-red-600"
                      >
                        Afwijzen
          </button>
        </div>
                  )}
      </div>
              ))
            )}
          </div>
              </div>
      )}

      {/* Podcast Sessions */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {podcastSessions.map((session) => (
          <div key={session.id} className="bg-white rounded-lg border border-gray-200 p-6  ">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                <h4 className="text-lg font-semibold text-gray-900 mb-2">{session.title}</h4>
                    <div className="space-y-2 text-sm text-gray-600">
                      <div className="flex items-center">
                        <Calendar size={16} className="mr-2" />
                        {session.scheduledDate.toLocaleDateString('nl-NL', { 
                          weekday: 'long', 
                          day: 'numeric', 
                          month: 'long' 
                        })}
                      </div>
                      <div className="flex items-center">
                        <Clock size={16} className="mr-2" />
                    {session.scheduledDate.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })} ({session.duration} min)
                      </div>
                      <div className="flex items-center">
                        <Users size={16} className="mr-2" />
                        {session.currentParticipants}/{session.maxParticipants} deelnemers
                      </div>
                      <div className="flex items-center">
                        <Mic size={16} className="mr-2" />
                        Host: {session.host}
                      </div>
                    </div>
                  </div>
                  <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                    session.status === 'planned' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {session.status === 'planned' ? 'Open' : 'Vol'}
                  </div>
                </div>
                
                <div className="flex space-x-3">
              <button className="flex-1 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 ">
                    Aanmelden
                  </button>
              <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 ">
                    Details
                  </button>
                </div>
              </div>
            ))}
          </div>
    </div>
  );

  // Google Calendar functions
  const handleGoogleCalendarConnect = async () => {
    try {
      await loadGoogleIdentityServices();
      await connectGoogleCalendar();
      
      // Listen for success/error events from the OAuth callback
      const handleSuccess = (event: any) => {
        setGoogleCalendarStatus(prev => ({ ...prev, connected: true }));
        setGoogleReconnectNeeded(null);
        showToast('✅ Google Calendar succesvol verbonden', 'success');
        
        // Update user profile
        if (userProfile?.uid) {
          updateDoc(doc(db, 'userProfiles', userProfile.uid), {
            'integrations.googleCalendar': true,
            'integrations.googleCalendarConnectedAt': new Date()
          });
        }
        
        // Reload data
        loadData();
        
        // Remove listener
        window.removeEventListener('googleCalendarConnected', handleSuccess);
        window.removeEventListener('googleCalendarError', handleError);
      };
      
      const handleError = (event: any) => {
        showToast('❌ Google Calendar verbinding mislukt: ' + (event.detail || 'Onbekende fout'), 'error');
        
        // Remove listener
        window.removeEventListener('googleCalendarConnected', handleSuccess);
        window.removeEventListener('googleCalendarError', handleError);
      };
      
      // Add event listeners
      window.addEventListener('googleCalendarConnected', handleSuccess);
      window.addEventListener('googleCalendarError', handleError);
    } catch (error: any) {
      console.error('Error connecting Google Calendar:', error);
      showToast('❌ Fout bij verbinden met Google Calendar: ' + (error.message || 'Onbekende fout'), 'error');
    }
  };

  const handleGoogleCalendarSync = async () => {
    if (!googleCalendarStatus.connected) {
      showToast('⚠️ Verbind eerst je Google Calendar', 'info');
      return;
    }

    setGoogleCalendarStatus(prev => ({ ...prev, syncing: true }));
    
    try {
      const functions = getFunctions(undefined, 'europe-west1');
      const syncGoogleCalendar = httpsCallable(functions, 'syncGoogleCalendar');
      
      const result = await syncGoogleCalendar({ 
        userId: userProfile?.uid,
        syncDirection: 'both' // 'both', 'fromGoogle', 'toGoogle'
      });
      
      const data = result.data as { success?: boolean; ok?: boolean; eventCount?: number; error?: string; reason?: string };
      
      if (data.success || data.ok) {
        const eventCount = data.eventCount || 0;
        setGoogleCalendarStatus(prev => ({
          ...prev,
          syncing: false,
          lastSync: new Date(),
          eventCount
        }));
        setGoogleReconnectNeeded(null);
        showToast(`✅ ${eventCount} events gesynchroniseerd`, 'success');
        
        // Reload events to show synced Google events
        loadData();
      } else if (data.reason === 'INVALID_GOOGLE_CLIENT' || data.reason === 'GOOGLE_REFRESH_EXPIRED') {
        setGoogleCalendarStatus(prev => ({ ...prev, syncing: false, connected: false }));
        setGoogleReconnectNeeded(data.reason);
        showToast('⚠️ Google Calendar verbinding ongeldig of verlopen. Koppel opnieuw.', 'error');
        return;
      } else {
        throw new Error(data.error || 'Sync mislukt');
      }
    } catch (error: any) {
      console.error('Error syncing Google Calendar:', error);
      setGoogleCalendarStatus(prev => ({ ...prev, syncing: false }));
      showToast('❌ Fout bij synchroniseren: ' + (error.message || 'Onbekende fout'), 'error');
    }
  };

  const handleGoogleCalendarDisconnect = async () => {
    if (!window.confirm('Weet je zeker dat je Google Calendar wilt loskoppelen?')) {
      return;
    }

    try {
      if (userProfile?.uid) {
        await updateDoc(doc(db, 'userProfiles', userProfile.uid), {
          'integrations.googleCalendar': false,
          'integrations.googleCalendarConnectedAt': null,
          'integrations.googleCalendarLastSync': null
        });
      }

      // Call Firebase function to revoke access
      const functions = getFunctions(undefined, 'europe-west1');
      const disconnectGoogleCalendar = httpsCallable(functions, 'disconnectGoogleCalendar');
      await disconnectGoogleCalendar({ userId: userProfile?.uid });

      setGoogleCalendarStatus({
        connected: false,
        lastSync: null,
        syncing: false,
        eventCount: 0
      });

      showToast('✅ Google Calendar losgekoppeld', 'success');
    } catch (error: any) {
      console.error('Error disconnecting Google Calendar:', error);
      showToast('❌ Fout bij loskoppelen: ' + (error.message || 'Onbekende fout'), 'error');
    }
  };

  // Outlook Calendar functions
  const handleOutlookCalendarConnect = async () => {
    try {
      await connectOutlookCalendar();
      // The OAuth flow will redirect, so no further action needed here
    } catch (error: any) {
      console.error('Error connecting Outlook Calendar:', error);
      showToast('❌ Fout bij verbinden met Outlook Calendar: ' + (error.message || 'Onbekende fout'), 'error');
    }
  };

  const handleOutlookCalendarSync = async () => {
    if (!outlookCalendarStatus.connected) {
      showToast('⚠️ Verbind eerst je Outlook Calendar', 'info');
      return;
    }

    setOutlookCalendarStatus(prev => ({ ...prev, syncing: true }));

    try {
      const result = await syncOutlookCalendar();
      
      if (result.success) {
        setOutlookCalendarStatus(prev => ({
          ...prev,
          syncing: false,
          lastSync: new Date(),
          eventCount: result.eventCount || 0
        }));
        
        showToast(`✅ ${result.eventCount} Outlook events gesynchroniseerd`, 'success');
        
        // Reload calendar events
        await loadData();
      } else {
        throw new Error(result.error || 'Sync failed');
      }
    } catch (error: any) {
      console.error('Error syncing Outlook Calendar:', error);
      setOutlookCalendarStatus(prev => ({ ...prev, syncing: false }));
      
      if (error.message?.includes('expired') || error.message?.includes('reconnect')) {
        setOutlookReconnectNeeded('Je Outlook Calendar toegang is verlopen. Klik op "Opnieuw verbinden".');
        showToast('⚠️ Outlook Calendar toegang verlopen', 'error');
      } else {
        showToast('❌ Fout bij synchroniseren: ' + (error.message || 'Onbekende fout'), 'error');
      }
    }
  };

  const handleOutlookCalendarDisconnect = async () => {
    if (!window.confirm('Weet je zeker dat je je Outlook Calendar wilt ontkoppelen?')) {
      return;
    }

    try {
      const result = await disconnectOutlookCalendar();
      
      if (result.success) {
        setOutlookCalendarStatus({
          connected: false,
          lastSync: null,
          syncing: false,
          eventCount: 0
        });
        setOutlookReconnectNeeded(null);
        
        showToast('✅ Outlook Calendar ontkoppeld', 'success');
        
        // Reload to remove Outlook events
        await loadData();
      } else {
        throw new Error(result.error || 'Disconnect failed');
      }
    } catch (error: any) {
      console.error('Error disconnecting Outlook Calendar:', error);
      showToast('❌ Fout bij ontkoppelen: ' + (error.message || 'Onbekende fout'), 'error');
    }
  };

  // Apple Calendar handlers
  const handleAppleCalendarConnect = async () => {
    if (!appleIcsUrl.trim()) {
      showToast('⚠️ Vul een ICS URL in', 'info');
      return;
    }

    try {
      const { connectAppleCalendar } = await import('../utils/appleCalendar');
      const result = await connectAppleCalendar(appleIcsUrl, appleCalendarName);

      if (result.success) {
        setAppleCalendarStatus(prev => ({ ...prev, connected: true }));
        setAppleIcsUrl('');
        setShowAppleInstructions(false);
        showToast('✅ Apple Calendar verbonden', 'success');
        
        // Immediately sync after connecting
        await handleAppleCalendarSync();
      } else {
        throw new Error(result.error || 'Connection failed');
      }
    } catch (error: any) {
      console.error('Error connecting Apple Calendar:', error);
      showToast('❌ Fout bij verbinden: ' + (error.message || 'Onbekende fout'), 'error');
    }
  };

  const handleAppleCalendarSync = async () => {
    if (!appleCalendarStatus.connected) {
      showToast('⚠️ Verbind eerst je Apple Calendar', 'info');
      return;
    }

    setAppleCalendarStatus(prev => ({ ...prev, syncing: true }));

    try {
      const { syncAppleCalendar } = await import('../utils/appleCalendar');
      const result = await syncAppleCalendar();

      if (result.success) {
        setAppleCalendarStatus(prev => ({
          ...prev,
          syncing: false,
          lastSync: new Date(),
          eventCount: result.eventCount || 0
        }));
        setAppleReconnectNeeded(null);
        showToast(`✅ ${result.eventCount} Apple events gesynchroniseerd`, 'success');
        await loadData();
      } else {
        throw new Error(result.error || 'Sync failed');
      }
    } catch (error: any) {
      console.error('Error syncing Apple Calendar:', error);
      setAppleCalendarStatus(prev => ({ ...prev, syncing: false }));
      showToast('❌ Fout bij synchroniseren: ' + (error.message || 'Onbekende fout'), 'error');
    }
  };

  const handleAppleCalendarDisconnect = async () => {
    if (!window.confirm('Weet je zeker dat je je Apple Calendar wilt ontkoppelen?')) {
      return;
    }

    try {
      const { disconnectAppleCalendar } = await import('../utils/appleCalendar');
      const result = await disconnectAppleCalendar();

      if (result.success) {
        setAppleCalendarStatus({
          connected: false,
          lastSync: null,
          syncing: false,
          eventCount: 0
        });
        setAppleReconnectNeeded(null);
        showToast('✅ Apple Calendar ontkoppeld', 'success');
        await loadData();
      } else {
        throw new Error(result.error || 'Disconnect failed');
      }
    } catch (error: any) {
      console.error('Error disconnecting Apple Calendar:', error);
      showToast('❌ Fout bij ontkoppelen: ' + (error.message || 'Onbekende fout'), 'error');
    }
  };

  // Load auto-sync preferences
  const loadAutoSyncPreferences = async () => {
    try {
      const functions = getFunctions(undefined, 'europe-west1');
      const getAutoSyncPreferences = httpsCallable(functions, 'getAutoSyncPreferences');
      
      const result = await getAutoSyncPreferences({});
      const data = result.data as { success?: boolean; preferences?: any };
      
      if (data.success && data.preferences) {
        setAutoSyncEnabled(data.preferences.enabled || false);
        setAutoSyncFrequency(data.preferences.frequency || 'weekly');
        setAutoSyncCalendars(data.preferences.calendars || []);
      }
    } catch (error: any) {
      console.error('Error loading auto-sync preferences:', error);
    }
  };

  // Save auto-sync preferences
  const handleSaveAutoSyncPreferences = async () => {
    try {
      const functions = getFunctions(undefined, 'europe-west1');
      const setAutoSyncPreferences = httpsCallable(functions, 'setAutoSyncPreferences');
      
      const result = await setAutoSyncPreferences({
        enabled: autoSyncEnabled,
        frequency: autoSyncFrequency,
        calendars: autoSyncCalendars,
      });
      
      const data = result.data as { success?: boolean; message?: string };
      
      if (data.success) {
        showToast(`✅ Auto-sync ${autoSyncEnabled ? 'ingeschakeld' : 'uitgeschakeld'}`, 'success');
        setShowAutoSyncModal(false);
      }
    } catch (error: any) {
      console.error('Error saving auto-sync preferences:', error);
      showToast('❌ Fout bij opslaan auto-sync instellingen', 'error');
    }
  };

  // Sync ALL connected calendars at once
  const handleSyncAllCalendars = async () => {
    const connectedCalendars: string[] = [];
    
    // Check which calendars are connected
    if (googleCalendarStatus.connected) connectedCalendars.push('Google');
    if (outlookCalendarStatus.connected) connectedCalendars.push('Outlook');
    if (appleCalendarStatus.connected) connectedCalendars.push('Apple');
    
    if (connectedCalendars.length === 0) {
      showToast('ℹ️ Geen calendars verbonden om te synchroniseren', 'info');
      return;
    }

    // Show starting toast
    showToast(`🔄 Synchroniseren van ${connectedCalendars.join(', ')}...`, 'info');

    const syncPromises: Promise<void>[] = [];

    // Sync Google Calendar if connected
    if (googleCalendarStatus.connected && !googleCalendarStatus.syncing) {
      setGoogleCalendarStatus(prev => ({ ...prev, syncing: true }));
      
      syncPromises.push(
        (async () => {
          try {
            const functions = getFunctions(undefined, 'europe-west1');
            const syncGoogleCalendar = httpsCallable(functions, 'syncGoogleCalendar');
            
            const result = await syncGoogleCalendar({ 
              userId: userProfile?.uid,
              syncDirection: 'both'
            });
            
            const data = result.data as { success?: boolean; ok?: boolean; eventCount?: number; error?: string; reason?: string };
            
            if (data.success || data.ok) {
              const eventCount = data.eventCount || 0;
              setGoogleCalendarStatus(prev => ({
                ...prev,
                syncing: false,
                lastSync: new Date(),
                eventCount
              }));
              setGoogleReconnectNeeded(null);
              console.log('✅ Google Calendar synced:', eventCount, 'events');
            } else if (data.reason === 'INVALID_GOOGLE_CLIENT' || data.reason === 'GOOGLE_REFRESH_EXPIRED') {
              setGoogleCalendarStatus(prev => ({ ...prev, syncing: false, connected: false }));
              setGoogleReconnectNeeded(data.reason);
              throw new Error('Google Calendar verbinding verlopen');
            } else {
              throw new Error(data.error || 'Sync mislukt');
            }
          } catch (error: any) {
            console.error('❌ Google Calendar sync error:', error);
            setGoogleCalendarStatus(prev => ({ ...prev, syncing: false }));
            throw error;
          }
        })()
      );
    }

    // Sync Outlook Calendar if connected
    if (outlookCalendarStatus.connected && !outlookCalendarStatus.syncing) {
      setOutlookCalendarStatus(prev => ({ ...prev, syncing: true }));
      
      syncPromises.push(
        (async () => {
          try {
            const functions = getFunctions(undefined, 'europe-west1');
            const syncOutlookCalendar = httpsCallable(functions, 'syncOutlookCalendar');
            
            const result = await syncOutlookCalendar({ 
              userId: userProfile?.uid 
            });
            
            const data = result.data as { success?: boolean; ok?: boolean; eventCount?: number; error?: string; reason?: string };
            
            if (data.success || data.ok) {
              const eventCount = data.eventCount || 0;
              setOutlookCalendarStatus(prev => ({
                ...prev,
                syncing: false,
                lastSync: new Date(),
                eventCount
              }));
              setOutlookReconnectNeeded(null);
              console.log('✅ Outlook Calendar synced:', eventCount, 'events');
            } else if (data.reason === 'INVALID_OUTLOOK_CLIENT' || data.reason === 'OUTLOOK_REFRESH_EXPIRED') {
              setOutlookCalendarStatus(prev => ({ ...prev, syncing: false, connected: false }));
              setOutlookReconnectNeeded(data.reason);
              throw new Error('Outlook Calendar verbinding verlopen');
            } else {
              throw new Error(data.error || 'Sync mislukt');
            }
          } catch (error: any) {
            console.error('❌ Outlook Calendar sync error:', error);
            setOutlookCalendarStatus(prev => ({ ...prev, syncing: false }));
            throw error;
          }
        })()
      );
    }

    // Sync Apple Calendar if connected
    if (appleCalendarStatus.connected && !appleCalendarStatus.syncing) {
      setAppleCalendarStatus(prev => ({ ...prev, syncing: true }));
      
      syncPromises.push(
        (async () => {
          try {
            const { syncAppleCalendar } = await import('../utils/appleCalendar');
            const result = await syncAppleCalendar();
            
            if (result.success) {
              const eventCount = result.eventCount || 0;
              setAppleCalendarStatus(prev => ({
                ...prev,
                syncing: false,
                lastSync: new Date(),
                eventCount
              }));
              setAppleReconnectNeeded(null);
              console.log('✅ Apple Calendar synced:', eventCount, 'events');
            } else {
              throw new Error(result.error || 'Sync mislukt');
            }
          } catch (error: any) {
            console.error('❌ Apple Calendar sync error:', error);
            setAppleCalendarStatus(prev => ({ ...prev, syncing: false }));
            throw error;
          }
        })()
      );
    }

    // Wait for all syncs to complete
    try {
      await Promise.all(syncPromises);
      await loadData(); // Refresh data after all syncs
      showToast(`✅ ${connectedCalendars.join(', ')} gesynchroniseerd!`, 'success');
    } catch (error: any) {
      console.error('Error during sync all:', error);
      showToast('⚠️ Enkele calendars konden niet worden gesynchroniseerd', 'error');
    }
  };

  // Check Google Calendar connection status on mount
  useEffect(() => {
    if (userProfile?.uid) {
      const checkConnectionStatus = async () => {
        try {
          const userDoc = await getDocs(query(
            collection(db, 'userProfiles'),
            where('__name__', '==', userProfile.uid)
          ));
          
          if (!userDoc.empty) {
            const userData = userDoc.docs[0].data();
            
            // Google Calendar status
            const isGoogleConnected = userData.integrations?.googleCalendar || false;
            const googleLastSync = userData.integrations?.googleCalendarLastSync?.toDate() || null;
            const googleEventCount = userData.integrations?.googleCalendarEventCount || 0;
            
            setGoogleCalendarStatus(prev => ({
              ...prev,
              connected: isGoogleConnected,
              lastSync: googleLastSync,
              eventCount: googleEventCount
            }));
            
            // Outlook Calendar status
            const isOutlookConnected = userData.integrations?.outlookCalendar || false;
            const outlookLastSync = userData.integrations?.outlookCalendarLastSync?.toDate() || null;
            const outlookEventCount = userData.integrations?.outlookCalendarEventCount || 0;
            
            setOutlookCalendarStatus(prev => ({
              ...prev,
              connected: isOutlookConnected,
              lastSync: outlookLastSync,
              eventCount: outlookEventCount
            }));

            // Apple Calendar status
            const isAppleConnected = userData.integrations?.appleCalendar || false;
            const appleLastSync = userData.integrations?.appleCalendarLastSync?.toDate() || null;
            const appleEventCount = userData.integrations?.appleCalendarEventCount || 0;
            
            setAppleCalendarStatus(prev => ({
              ...prev,
              connected: isAppleConnected,
              lastSync: appleLastSync,
              eventCount: appleEventCount
            }));
          }
        } catch (error) {
          console.error('Error checking calendar status:', error);
        }
      };
      
      checkConnectionStatus();
      loadAutoSyncPreferences();
    }
  }, [userProfile]);

  const renderSyncView = () => (
    <div className="space-y-3">
      {googleReconnectNeeded && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center justify-between">
          <div className="text-sm">
            {googleReconnectNeeded === 'INVALID_GOOGLE_CLIENT'
              ? 'Google Calendar client configuratie ongeldig. Koppel opnieuw.'
              : 'Google Calendar toegang verlopen. Koppel opnieuw.'}
          </div>
          <button
            onClick={handleGoogleCalendarConnect}
            className="ml-3 px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
          >
            Opnieuw koppelen
          </button>
        </div>
      )}
      <div className="flex justify-between items-center">
                  <div>
          <h3 className="text-sm font-semibold text-gray-900">Agenda Synchronisatie</h3>
          <p className="text-xs text-gray-600">Verbind en synchroniseer externe kalenders</p>
                  </div>
                  <div className="flex items-center space-x-2">
          <button 
            onClick={handleSyncAllCalendars}
            disabled={
              (!googleCalendarStatus.connected && !outlookCalendarStatus.connected && !appleCalendarStatus.connected) ||
              (googleCalendarStatus.syncing || outlookCalendarStatus.syncing || appleCalendarStatus.syncing)
            }
            className={`px-4 py-2 rounded-lg flex items-center space-x-2 ${
              (!googleCalendarStatus.connected && !outlookCalendarStatus.connected && !appleCalendarStatus.connected) ||
              (googleCalendarStatus.syncing || outlookCalendarStatus.syncing || appleCalendarStatus.syncing)
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-stone-500 text-white hover:bg-[#4F6660]'
            }`}
          >
            <RefreshCw size={16} className={
              (googleCalendarStatus.syncing || outlookCalendarStatus.syncing || appleCalendarStatus.syncing) ? 'animate-spin' : ''
            } />
            <span>
              {(googleCalendarStatus.syncing || outlookCalendarStatus.syncing || appleCalendarStatus.syncing) 
                ? 'Synchroniseren...' 
                : 'Synchroniseer Nu'
              }
            </span>
                    </button>
          
          {/* Auto-Sync Settings Button */}
          <button
            onClick={() => setShowAutoSyncModal(true)}
            className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 flex items-center space-x-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>Auto-Sync</span>
            {autoSyncEnabled && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                Aan
              </span>
            )}
          </button>
                  </div>
                </div>

      {/* Sync Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold">Google Calendar</h4>
            <div className={`w-3 h-3 rounded-full ${
              googleCalendarStatus.connected ? 'bg-green-400' : 'bg-red-400'
            }`}></div>
            </div>
          {googleCalendarStatus.connected ? (
            <>
              <p className="text-sm text-gray-600 mb-2">
                Laatste sync: {googleCalendarStatus.lastSync 
                  ? googleCalendarStatus.lastSync.toLocaleString('nl-NL')
                  : 'Nog niet gesynchroniseerd'}
              </p>
              <p className="text-sm text-gray-500 mb-4">{googleCalendarStatus.eventCount} events gesynchroniseerd</p>
              <div className="flex space-x-2">
                <button
                  onClick={handleGoogleCalendarSync}
                  disabled={googleCalendarStatus.syncing}
                  className="flex-1 px-4 py-2 bg-stone-500 text-white rounded-lg hover:bg-[#4F6660] disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {googleCalendarStatus.syncing ? 'Synchroniseren...' : 'Nu Synchroniseren'}
                </button>
                <button
                  onClick={handleGoogleCalendarDisconnect}
                  className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                  title="Loskoppelen"
                >
                  <X size={16} />
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="text-sm text-gray-600 mb-2">Niet verbonden</p>
              <p className="text-sm text-gray-500 mb-4">Verbind je Google Calendar om te synchroniseren</p>
              <button
                onClick={handleGoogleCalendarConnect}
                className="w-full px-4 py-2 text-white rounded-lg transition-colors"
                style={{ backgroundColor: '#8DA8A0' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#7A9488'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#8DA8A0'; }}
              >
                Verbind Google Calendar
              </button>
            </>
          )}
          </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold">Outlook</h4>
            <div className={`w-3 h-3 rounded-full ${
              outlookCalendarStatus.connected ? 'bg-green-400' : 'bg-gray-400'
            }`}></div>
          </div>
          {outlookCalendarStatus.connected ? (
            <>
              <p className="text-sm text-gray-600 mb-2">
                Laatste sync: {outlookCalendarStatus.lastSync 
                  ? outlookCalendarStatus.lastSync.toLocaleString('nl-NL')
                  : 'Nog niet gesynchroniseerd'}
              </p>
              <p className="text-sm text-gray-500 mb-4">{outlookCalendarStatus.eventCount} events gesynchroniseerd</p>
              <div className="flex space-x-2">
                <button
                  onClick={handleOutlookCalendarSync}
                  disabled={outlookCalendarStatus.syncing}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {outlookCalendarStatus.syncing ? 'Synchroniseren...' : 'Nu Synchroniseren'}
                </button>
                <button
                  onClick={handleOutlookCalendarDisconnect}
                  className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                  title="Loskoppelen"
                >
                  <X size={16} />
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="text-sm text-gray-600 mb-2">Niet verbonden</p>
              <p className="text-sm text-gray-500 mb-4">Verbind je Outlook Calendar om te synchroniseren</p>
              <button
                onClick={handleOutlookCalendarConnect}
                className="w-full px-4 py-2 text-white rounded-lg transition-colors"
                style={{ backgroundColor: '#8DA8A0' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#7A9488'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#8DA8A0'; }}
              >
                Verbind Outlook Calendar
              </button>
            </>
          )}
        </div>

        {/* Apple Calendar Card */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold">Apple Calendar</h4>
            <div className={`w-3 h-3 rounded-full ${
              appleCalendarStatus.connected ? 'bg-green-400' : 'bg-red-400'
            }`}></div>
          </div>
          {appleCalendarStatus.connected ? (
            <>
              <p className="text-sm text-gray-600 mb-2">
                Laatste sync: {appleCalendarStatus.lastSync 
                  ? appleCalendarStatus.lastSync.toLocaleString('nl-NL')
                  : 'Nog niet gesynchroniseerd'}
              </p>
              <p className="text-sm text-gray-500 mb-4">{appleCalendarStatus.eventCount} events gesynchroniseerd</p>
              <div className="flex space-x-2">
                <button
                  onClick={handleAppleCalendarSync}
                  disabled={appleCalendarStatus.syncing}
                  className="flex-1 px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {appleCalendarStatus.syncing ? 'Synchroniseren...' : 'Nu Synchroniseren'}
                </button>
                <button
                  onClick={handleAppleCalendarDisconnect}
                  className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                  title="Loskoppelen"
                >
                  <X size={16} />
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="text-sm text-gray-600 mb-2">Niet verbonden</p>
              <p className="text-sm text-gray-500 mb-4">Verbind je Apple Calendar via ICS URL</p>
              
              {!showAppleInstructions ? (
                <button
                  onClick={() => setShowAppleInstructions(true)}
                  className="w-full px-4 py-2 text-white rounded-lg transition-colors"
                  style={{ backgroundColor: '#4F6660' }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#3C4F49'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#4F6660'; }}
                >
                  Verbind Apple Calendar
                </button>
              ) : (
                <div className="space-y-3">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                    <p className="font-semibold mb-2 flex items-center gap-1"><DocumentIcon size={14} className="text-gray-500" /> Instructies:</p>
                    <ol className="list-decimal list-inside space-y-1 text-xs">
                      <li>Open iCloud.com → Calendar</li>
                      <li>Klik op kalender naam → Deel Kalender</li>
                      <li>Maak "Publieke Kalender" aan</li>
                      <li>Kopieer de "webcal://" URL</li>
                      <li>Plak hieronder</li>
                    </ol>
                  </div>
                  
                  <input
                    type="text"
                    placeholder="Kalender naam (optioneel)"
                    value={appleCalendarName}
                    onChange={(e) => setAppleCalendarName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 text-sm"
                  />
                  
                  <input
                    type="text"
                    placeholder="webcal://... of https://..."
                    value={appleIcsUrl}
                    onChange={(e) => setAppleIcsUrl(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 text-sm"
                  />
                  
                  <div className="flex space-x-2">
                    <button
                      onClick={handleAppleCalendarConnect}
                      disabled={!appleIcsUrl.trim()}
                      className="flex-1 px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 disabled:bg-gray-300 disabled:cursor-not-allowed text-sm"
                    >
                      Verbinden
                    </button>
                    <button
                      onClick={() => {
                        setShowAppleInstructions(false);
                        setAppleIcsUrl('');
                      }}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm"
                    >
                      Annuleren
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      </div>
    );

  const renderContent = () => {
    let content: React.ReactNode;
    switch (activeTab) {
      case 'kalender':
        content = renderCalendarView();
        break;
      case 'planning':
        content = renderPlanningView();
        break;
      case 'podcast-sessies':
        content = renderPodcastView();
        break;
      case 'calendar-sync':
        content = renderSyncView();
        break;
      case 'doelstellingen':
        content = <DoelstellingenPage />;
        break;
      case 'purpose':
        content = <IkigaiPage />;
        break;
      case 'team':
        content = <TeamPage />;
        break;
      default:
        content = renderCalendarView();
    }

    return (
      <div className="w-full min-w-0">
        {content}
      </div>
    );
  };

  const renderTaskDetailModal = () => {
    if (!showTaskModal || !selectedTask) return null;

    const task = selectedTask;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4" role="dialog" aria-modal="true">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Taak Details</h3>
            <button
              onClick={() => setShowTaskModal(false)}
              className="text-gray-500 hover:text-gray-700"
            >
              <X size={20} />
            </button>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Taak Naam</label>
              <input
                type="text"
                value={task.name}
                onChange={(e) => updateSelectedTaskFields({ name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stone-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Categorie</label>
              <select
                value={task.category}
                onChange={(e) => updateSelectedTaskFields({ category: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stone-500"
              >
                <option value="Ontdekking">Ontdekking</option>
                <option value="Analyse">Analyse</option>
                <option value="Ontwerp">Ontwerp</option>
                <option value="Implementatie">Implementatie</option>
                <option value="Evaluatie">Evaluatie</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Toegewezen aan</label>
              <input
                type="text"
                value={task.assignee}
                onChange={(e) => updateSelectedTaskFields({ assignee: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stone-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={task.status}
                onChange={(e) => updateSelectedTaskFields({ status: e.target.value as PlanningTask['status'] })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stone-500"
              >
                <option value="todo">Te Doen</option>
                <option value="in-progress">Bezig</option>
                <option value="completed">Voltooid</option>
              </select>
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => {
                  updateTask(task);
                  setShowTaskModal(false);
                }}
                className="px-4 py-2 bg-stone-500 text-white rounded-lg hover:bg-[#4F6660]"
              >
                Opslaan
              </button>
              <button
                onClick={async () => {
                  await deletePlanningTask(task.id);
                  setShowTaskModal(false);
                }}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
              >
                Verwijderen
              </button>
              <button
                onClick={() => setShowTaskModal(false)}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
              >
                Annuleren
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderToastNotification = () => {
    if (!toast) return null;

    return (
      <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg flex items-center space-x-2 animate-fadeIn ${
        toast.type === 'success' ? 'bg-green-500 text-white' :
        toast.type === 'error' ? 'bg-red-500 text-white' :
        'bg-blue-500 text-white'
      }`}>
        <span>{toast.message}</span>
        <button
          onClick={() => setToast(null)}
          className="ml-2 text-white hover:text-gray-200"
        >
          <X size={16} />
        </button>
      </div>
    );
  };

  const findLinkedEvent = (actionId: string) =>
    events.find(
      (event) =>
        event.doelstellingenMeta?.actionId === actionId &&
        event.status !== 'cancelled'
    );

  // Sortable Action Card Component (voor reordering binnen lijst)
  const SortableActionCard = ({ action, goal, milestone }: { action: any; goal: any; milestone?: any }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: action.id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    };

    const isCompleted = Boolean(action.completed);

    return (
      <div ref={setNodeRef} style={style}>
        {renderActionCard(action, goal, milestone, isCompleted ? undefined : { attributes, listeners })}
      </div>
    );
  };

  const renderActionCard = (action: any, goal: any, milestone?: any, sortableHandles?: { attributes: any; listeners: any }) => {
    const originBadge = ACTION_ORIGIN_BADGES[action.origin] || ACTION_ORIGIN_BADGES.intake;
    const isCompleted = Boolean(action.completed);
    const isEditing = editingActionId === action.id;
    const isProcessing = processingActionId === action.id;
    
    // Priority and type badges (NEW)
    const priorityConfig = action.priority ? ACTION_PRIORITY_CONFIG[action.priority as ActionPriority] : null;
    const typeConfig = action.actionType ? ACTION_TYPE_CONFIG[action.actionType as ActionTypeEnum] : null;
    
    const timer = {
      activeTime: action.activeTime || 0,
      timerStartedAt: action.timerStartedAt
        ? action.timerStartedAt.toDate
          ? action.timerStartedAt.toDate()
          : new Date(action.timerStartedAt)
        : null,
      isTimerRunning: action.isTimerRunning || false
    };
    const completionDate =
      action.completedAt
        ? typeof action.completedAt === 'number'
          ? new Date(action.completedAt)
          : action.completedAt.toDate
            ? action.completedAt.toDate()
            : new Date(action.completedAt)
        : null;
    const linkedEvent = findLinkedEvent(action.id);
    const ghostDays = linkedEvent?.doelstellingenMeta?.ghostDates?.length ?? 0;

    return (
      <div
        key={action.id}
        draggable={!isCompleted}
        onDragStart={(e) => {
          if (isCompleted) {
            e.preventDefault();
            return;
          }
          handleActionDragStart(e, action, goal, milestone);
        }}
        className={`group flex flex-col px-2 py-2 mb-1 rounded-lg border transition-all ${
          isCompleted
            ? 'bg-lime-50 border-lime-200 text-lime-900 cursor-not-allowed opacity-90'
            : 'bg-blue-50 border-blue-100 cursor-move hover:bg-blue-100 hover:shadow-sm'
        } ${isEditing ? 'ring-2 ring-stone-200' : ''} ${
          pulsingActionId === action.id ? 'animate-pulse-slow-3x ring-2 ring-[#7A9488]' : ''
        }`}
      >
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-2">
            {/* Grip handle voor reorderen (alleen binnen lijst) */}
            {sortableHandles && !isCompleted && (
              <div
                {...sortableHandles.attributes}
                {...sortableHandles.listeners}
                className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 touch-none"
                title="Versleep om volgorde te wijzigen"
              >
                <svg width="12" height="16" viewBox="0 0 12 16" fill="currentColor">
                  <circle cx="3" cy="4" r="1.5" />
                  <circle cx="9" cy="4" r="1.5" />
                  <circle cx="3" cy="8" r="1.5" />
                  <circle cx="9" cy="8" r="1.5" />
                  <circle cx="3" cy="12" r="1.5" />
                  <circle cx="9" cy="12" r="1.5" />
                </svg>
              </div>
            )}
            <label className={`flex items-center cursor-pointer ${isProcessing ? 'opacity-50 cursor-wait' : ''}`}>
              <input
                type="checkbox"
                checked={isCompleted}
                disabled={isProcessing || isEditing}
                onChange={(e) => {
                  e.stopPropagation();
                  handleCompleteDoelstellingenAction(action);
                }}
                onClick={(e) => e.stopPropagation()}
                className="h-5 w-5 text-stone-500 border-gray-300 rounded focus:ring-2 focus:ring-stone-500 focus:ring-offset-1 cursor-pointer disabled:cursor-not-allowed"
                title={isCompleted ? 'Klik om terug te zetten' : 'Klik om te voltooien'}
              />
              {/* Priority indicator dot */}
              {priorityConfig && !isCompleted && (
                <span 
                  className={`w-2 h-2 rounded-full ml-1.5 ${priorityConfig.dotColor}`}
                  title={`Prioriteit: ${priorityConfig.label}`}
                />
              )}
              {/* Type badge */}
              {typeConfig && !isCompleted && (
                <span 
                  className={`text-[8px] px-1 py-0.5 rounded ml-1 ${typeConfig.bgColor} ${typeConfig.textColor} border ${typeConfig.borderColor}`}
                  title={typeConfig.label}
                >
                  {typeConfig.emoji}
                </span>
              )}
              <span className={`text-[9px] font-semibold ${originBadge.color} ${originBadge.bg} px-1 rounded ml-1`}>
                {originBadge.label}
              </span>
            </label>
          </div>
          <div className="flex items-center space-x-1">
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleDoelstellingenActionTimer(action.id);
              }}
              disabled={isCompleted}
              className={`w-6 h-6 flex items-center justify-center rounded transition-colors text-xs ${
                timer.isTimerRunning
                  ? 'bg-stone-500 text-white hover:bg-[#4F6660]'
                  : 'bg-gray-200 text-gray-600 hover:bg-stone-100 hover:text-[#4F6660]'
              }`}
              title={timer.isTimerRunning ? 'Timer pauzeren' : 'Timer starten'}
            >
              {timer.isTimerRunning ? (
                <span className="text-[10px] font-bold">▌▌</span>
              ) : (
                <span className="text-[10px] ml-0.5">▶</span>
              )}
            </button>
          </div>
        </div>

        {isEditing ? (
          <div className="mt-2 space-y-2">
            <input
              type="text"
              value={editingActionTitle}
              onChange={(e) => setEditingActionTitle(e.target.value)}
              className="w-full text-xs px-2 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stone-500"
            />
            <div className="flex items-center space-x-2">
              <button
                onClick={handleSaveActionTitle}
                disabled={!editingActionTitle.trim() || processingActionId === editingActionId}
                className="px-3 py-1 text-xs rounded bg-stone-500 text-white hover:bg-[#4F6660] disabled:opacity-50"
              >
                Opslaan
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleCancelEditingAction();
                }}
                className="px-3 py-1 text-xs rounded bg-gray-200 text-gray-700 hover:bg-gray-300"
              >
                Annuleren
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-1 text-xs text-gray-800 leading-relaxed line-clamp-2 group-hover:line-clamp-none" title={action.title}>
            {action.title}
          </div>
        )}

        <div className="flex items-center justify-between mt-1.5 text-[9px]">
          <div className="flex items-center space-x-2">
            {milestone && (
              <span
                className="text-blue-600 bg-blue-100/60 px-1.5 py-0.5 rounded truncate max-w-[140px]"
                title={milestone.title}
              >
                → {milestone.title}
              </span>
            )}
            {(timer.activeTime || timer.isTimerRunning) && (
              <span className="text-gray-500 font-mono">
                <TimeIcon size={14} className="inline" /> {formatActiveTime(timer.activeTime || 0, timer.timerStartedAt, timer.isTimerRunning)}
              </span>
            )}
          </div>
          {/* Edit en Delete knoppen rechts onderin */}
          {!isCompleted && !isEditing && (
            <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleStartEditingAction(action);
                }}
                className="w-5 h-5 flex items-center justify-center rounded bg-white text-gray-500 hover:text-gray-800 border border-gray-200 shadow-sm"
                title="Actie bijwerken"
              >
                <Edit3 size={10} />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteDoelstellingenAction(action.id, action.title);
                }}
                className="w-5 h-5 flex items-center justify-center rounded bg-white text-red-500 hover:text-red-700 border border-gray-200 shadow-sm"
                title="Actie verwijderen"
              >
                <Trash2 size={10} />
              </button>
            </div>
          )}
        </div>

        {completionDate && (
          <div className="text-[9px] text-lime-700 mt-1">
            Voltooid op {completionDate.toLocaleDateString('nl-NL')}
          </div>
        )}
        {ghostDays > 0 && (
          <div className="text-[9px] text-[#4F6660] mt-1">
            ⌛ Uitgesteld: {ghostDays} dag{ghostDays === 1 ? '' : 'en'}
          </div>
        )}
      </div>
    );
  };

  // Sortable Task Item Component (NEW)
  const SortableTaskItem: React.FC<{
    task: AgendaTask;
    list: AgendaTaskList;
    event?: CalendarEvent;
  }> = ({ task, list, event }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: task.id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    };

    return (
      <div
        ref={setNodeRef}
        style={style}
        {...attributes}
        className={`flex items-center justify-between px-3 py-2 rounded-lg border ${
          task.completed
            ? 'bg-gray-50 border-gray-200 opacity-60'
            : 'bg-white border-gray-200 hover:border-stone-200'
        } ${!task.completed ? 'cursor-move' : ''}`}
      >
        {/* Drag handle */}
        {!task.completed && (
          <div {...listeners} className="mr-2 text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing">
            ⋮⋮
          </div>
        )}
        
        {/* Task content - verticale layout */}
        <div className="flex-1 flex flex-col space-y-1.5">
          {/* Top row: checkbox + titel */}
          <div className="flex items-start space-x-2">
            {/* Klikbare completion circle */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleTaskCompleted(task.id);
              }}
              disabled={task.completed}
              className={`flex-shrink-0 mt-0.5 transition-all ${
                task.completed 
                  ? 'cursor-default' 
                  : 'hover:scale-110 cursor-pointer'
              }`}
              title={task.completed ? 'Voltooid' : 'Klik om af te vinken'}
            >
              {task.completed ? (
                <CheckCircle size={14} className="text-green-500" />
              ) : (
                <Circle size={14} className="text-gray-400 hover:text-stone-500" />
              )}
            </button>
            <div className={`flex-1 text-xs font-medium leading-relaxed ${
              task.completed ? 'line-through text-gray-500' : 'text-gray-800'
            }`}>
              {task.title}
            </div>
          </div>
          
          {/* Bottom row: tijd, badge, timer - horizontaal naast elkaar */}
          <div className="flex items-center space-x-2 flex-wrap">
            {event && (
              <div className="text-xs text-gray-500 font-medium">
                🕐 {event.startTime.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}
              </div>
            )}
            <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600">
              {list.name}
            </span>
            
            {/* Timer controls (NEW) */}
            {(() => {
              // Check of dit een Doelstellingen actie is
              const isDoelstellingenAction = event?.doelstellingenMeta?.actionId;
              const action = isDoelstellingenAction 
                ? doelstellingenActions.find((a: any) => a.id === isDoelstellingenAction)
                : null;
              
              // Timer status voor normale taken
              const taskTimerStatus = getTaskTimerStatus(task);
              
              // Timer status voor Doelstellingen acties
              const actionTimer = action ? {
                activeTime: action.activeTime || 0,
                timerStartedAt: action.timerStartedAt
                  ? typeof action.timerStartedAt === 'number'
                    ? new Date(action.timerStartedAt)
                    : action.timerStartedAt.toDate
                      ? action.timerStartedAt.toDate()
                      : new Date(action.timerStartedAt)
                  : null,
                isTimerRunning: action.isTimerRunning || false
              } : null;
              
              const timerStatus = isDoelstellingenAction && actionTimer
                ? {
                    isRunning: actionTimer.isTimerRunning,
                    displayTime: formatActiveTime(
                      actionTimer.activeTime || 0,
                      actionTimer.timerStartedAt,
                      actionTimer.isTimerRunning
                    )
                  }
                : taskTimerStatus;
              
              if (!timerStatus) return null;
              
              return (
                <div className="flex items-center space-x-1">
                  {/* Play/Pause button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isDoelstellingenAction && action) {
                        toggleDoelstellingenActionTimer(action.id);
                      } else {
                        toggleTaskTimer(task.id);
                      }
                    }}
                    disabled={task.completed}
                    className={`w-6 h-6 flex items-center justify-center rounded transition-colors text-xs ${
                      timerStatus.isRunning
                        ? 'bg-stone-500 text-white hover:bg-[#4F6660]'
                        : 'bg-gray-200 text-gray-600 hover:bg-stone-100 hover:text-[#4F6660]'
                    } ${task.completed ? 'opacity-50 cursor-not-allowed' : ''}`}
                    title={timerStatus.isRunning ? 'Timer pauzeren' : 'Timer starten'}
                  >
                    {timerStatus.isRunning ? (
                      <span className="text-[10px] font-bold">▌▌</span>
                    ) : (
                      <span className="text-[10px] ml-0.5">▶</span>
                    )}
                  </button>
                  {/* Timer display */}
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    timerStatus.isRunning
                      ? 'bg-stone-100 text-[#4F6660]'
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    <Clock size={12} className="inline mr-1" />
                    {timerStatus.displayTime}
                  </span>
                </div>
              );
            })()}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="mint-sage-theme agenda-mobile-shell w-full scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 hover:scrollbar-thumb-gray-400" style={{ backgroundColor: '#F4F1EA', height: '100%', maxHeight: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
    <div className="agenda-mobile-inner max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-1 flex flex-col" style={{ flex: '1 1 0%', minHeight: 0 }}>
      {/* Header — compact tool mode */}
      <div className="agenda-mobile-page-header hidden md:block" style={{ margin: '0 -1.5rem 0.5rem -1.5rem', padding: '0.5rem 1.5rem 0.35rem 1.5rem', background: 'linear-gradient(180deg, rgba(147,174,163,0.06) 0%, transparent 100%)', borderBottom: '1px solid #ECEAE5', maxHeight: '64px', flexShrink: 0 }}>
        <PageHeader 
          title="Agenda"
          subtitle="Beheer je planning en afspraken"
          icon={<Calendar style={{ color: '#8DA8A0' }} size={24} />}
        />
      </div>

      {/* Expert Status Widgets — compact tool mode */}
      {userProfile?.uid && (
        <div className="mb-1 space-y-1" style={{ flexShrink: 0 }}>
          <ExpertStatusWidget 
            userId={userProfile.uid}
            onPlanConversation={() => {
              window.dispatchEvent(new CustomEvent('openConnectieFlow'));
            }}
          />
          
          <ConnectionSuggestions 
            userId={userProfile.uid}
            onScheduleWith={(connectionId, connectionName) => {
              console.log('Schedule with:', connectionName);
            }}
          />
        </div>
      )}
      
      {/* Action Bar — compact plus actions */}
      <div className="agenda-mobile-actionbar bg-white rounded-lg mb-1" style={{ border: '1px solid #ECEAE5', boxShadow: '0 2px 10px rgba(0,0,0,0.03)', padding: '0.35rem 0.75rem', flexShrink: 0 }}>
        <div className="agenda-mobile-primary-actions flex justify-end space-x-2">
          <button 
            onClick={openNetworkingQuickSlot}
            className="mint-btn-networking h-11 w-11 rounded-full flex items-center justify-center transition-all"
            style={{ background: 'linear-gradient(180deg, #93AEA3 0%, #7A9488 100%)', color: '#FFFFFF', border: 'none', boxShadow: '0 8px 24px rgba(60,79,73,0.18)', fontWeight: 500, letterSpacing: '0.01em' }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'linear-gradient(180deg, #849E94 0%, #6B8579 100%)'; e.currentTarget.style.boxShadow = '0 10px 28px rgba(60,79,73,0.22)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'linear-gradient(180deg, #93AEA3 0%, #7A9488 100%)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(60,79,73,0.18)'; }}
            aria-label="Vrij voor netwerken toevoegen"
            title="Vrij voor netwerken"
          >
            <Plus size={20} />
          </button>
          <button 
            onClick={openNewEventModal}
            className="mint-btn-new-event h-11 w-11 rounded-full flex items-center justify-center transition-colors"
            style={{ backgroundColor: '#FFFFFF', color: '#4F6660', border: '1px solid #E9E7E2', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#F4F4F1'; e.currentTarget.style.borderColor = '#DDDBD5'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.05)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#FFFFFF'; e.currentTarget.style.borderColor = '#E9E7E2'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.03)'; }}
            aria-label="Nieuw agendapunt toevoegen"
            title="Nieuw agendapunt"
          >
            <Plus size={20} />
          </button>
        </div>
                    
        {/* Navigation Tabs - volgorde: Doelstellingen | Purpose | Team | Kalender | Agenda Sync */}
        <div className="agenda-mobile-section-tabs flex flex-wrap space-x-1 mt-1">
          <button
            onClick={() => setActiveTab('doelstellingen')}
            className="flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-colors"
            style={activeTab === 'doelstellingen'
              ? { backgroundColor: 'rgba(141,168,160,0.14)', color: '#3C4F49', borderBottom: '2px solid #7A9488', fontWeight: 500 }
              : { backgroundColor: 'transparent', color: '#5E6462' }}
          >
            <Target size={16} />
            <span>Doelstellingen</span>
          </button>
          <button
            onClick={() => setActiveTab('purpose')}
            className="flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-colors"
            style={activeTab === 'purpose'
              ? { backgroundColor: 'rgba(141,168,160,0.14)', color: '#3C4F49', borderBottom: '2px solid #7A9488', fontWeight: 500 }
              : { backgroundColor: 'transparent', color: '#5E6462' }}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ display: 'inline', flexShrink: 0 }}>
              <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.3" />
              <circle cx="5.5" cy="6.5" r="3" stroke="currentColor" strokeWidth="1" strokeOpacity="0.55" />
              <circle cx="10.5" cy="6.5" r="3" stroke="currentColor" strokeWidth="1" strokeOpacity="0.55" />
              <circle cx="5.5" cy="9.5" r="3" stroke="currentColor" strokeWidth="1" strokeOpacity="0.55" />
              <circle cx="10.5" cy="9.5" r="3" stroke="currentColor" strokeWidth="1" strokeOpacity="0.55" />
            </svg>
            <span>Purpose</span>
          </button>
          <button
            onClick={() => setActiveTab('team')}
            className="flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-colors"
            style={activeTab === 'team'
              ? { backgroundColor: 'rgba(141,168,160,0.14)', color: '#3C4F49', borderBottom: '2px solid #7A9488', fontWeight: 500 }
              : { backgroundColor: 'transparent', color: '#5E6462' }}
          >
            <Users size={16} />
            <span>Team</span>
          </button>
          <button
            onClick={() => setActiveTab('kalender')}
            className="flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-colors"
            style={activeTab === 'kalender'
              ? { backgroundColor: 'rgba(141,168,160,0.14)', color: '#3C4F49', borderBottom: '2px solid #7A9488', fontWeight: 500 }
              : { backgroundColor: 'transparent', color: '#5E6462' }}
          >
            <Calendar size={16} />
            <span>Kalender</span>
          </button>
          {/* Planning- en Podcast-tab blijven in de code bestaan maar zijn tijdelijk verborgen in de UI */}
          <button
            onClick={() => setActiveTab('calendar-sync')}
            className="flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-colors"
            style={activeTab === 'calendar-sync'
              ? { backgroundColor: 'rgba(141,168,160,0.14)', color: '#3C4F49', borderBottom: '2px solid #7A9488', fontWeight: 500 }
              : { backgroundColor: 'transparent', color: '#5E6462' }}
          >
            <RefreshCw size={16} />
            <span>Agenda Sync</span>
          </button>
        </div>
          </div>

      {/* Main Content - Flex Layout voor dynamische sidebar — fills remaining vh */}
      <div className="agenda-mobile-content flex gap-2 w-full" style={{ flex: '1 1 0%', minHeight: 0 }}>
        {/* Main Content Area - groeit dynamisch */}
        <div className="min-w-0 flex flex-col flex-1" style={{ minHeight: 0 }}>
          <div className="agenda-mobile-main-card bg-transparent md:bg-white rounded-lg overflow-y-auto overflow-x-hidden" style={{ border: '1px solid #ECEAE5', boxShadow: '0 2px 10px rgba(0,0,0,0.03)', padding: '0.5rem', flex: '1 1 0%', minHeight: 0 }}>
            {renderContent()}
          </div>
        </div>
          
        {/* Tasks Sidebar - Google Tasks stijl (rechts, fixed width) */}
        <div className={`agenda-mobile-tasks flex-shrink-0 ${mobileTabIndex === 2 ? 'block' : 'hidden'} md:block ${sidebarAnimating ? 'transition-[width] duration-300' : ''} ${
          isMessagesMinimized ? 'w-12' : 'w-72 lg:w-80'
        }`}>
        {isMessagesMinimized ? (
          // Collapsed state - smalle verticale balk met timer indicator
          (() => {
            // Check of er een timer draait
            const runningTask = tasks.find(t => t.isTimerRunning);
            const runningAction = doelstellingenActions.find((a: any) => a.isTimerRunning);
            const hasRunningTimer = runningTask || runningAction;
            const runningItem = runningTask || runningAction;
            
            return (
              <div className={`bg-white rounded-lg shadow-sm border sticky top-0 h-full flex flex-col items-center py-2 transition-colors ${
                hasRunningTimer ? 'border-[#7A9488] bg-stone-50' : 'border-gray-200'
              }`} style={{ borderColor: '#E9E7E2' }}>
                {/* Open button - pijl naar links om sidebar te openen */}
                <button
                  onClick={() => toggleSidebar(false)}
                  className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors mb-2"
                  title="Taken openen"
                >
                  <ChevronLeft size={16} className="text-gray-600" />
                </button>
                
                {/* Timer indicator - alleen als er een timer draait */}
                {hasRunningTimer && runningItem && (
                  <div className="flex flex-col items-center space-y-2 px-1">
                    {/* Pulsing indicator */}
                    <div className="w-3 h-3 bg-stone-500 rounded-full animate-pulse" title="Timer actief" />
                    
                    {/* Timer tijd - verticaal */}
                    <div 
                      className="text-[10px] font-mono text-[#4F6660] font-medium"
                      style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
                    >
                      {formatActiveTime(
                        runningItem.activeTime || 0, 
                        runningItem.timerStartedAt 
                          ? (typeof runningItem.timerStartedAt === 'number' 
                              ? new Date(runningItem.timerStartedAt) 
                              : runningItem.timerStartedAt.toDate 
                                ? runningItem.timerStartedAt.toDate() 
                                : new Date(runningItem.timerStartedAt))
                          : null,
                        true
                      )}
                    </div>
                    
                    {/* Pause button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (runningTask) {
                          toggleTaskTimer(runningTask.id);
                        } else if (runningAction) {
                          toggleDoelstellingenActionTimer(runningAction.id);
                        }
                      }}
                      className="w-7 h-7 flex items-center justify-center rounded-lg bg-stone-500 text-white hover:bg-[#4F6660] transition-colors"
                      title="Pauzeer timer"
                    >
                      <span className="text-[10px] font-bold">▌▌</span>
                    </button>
                  </div>
                )}
                
                {/* Verticaal "Taken" label */}
                <button
                  onClick={() => toggleSidebar(false)}
                  className="flex-1 flex items-center justify-center cursor-pointer group"
                  title="Taken openen"
                >
                  <span 
                    className={`text-sm font-medium transition-colors ${
                      hasRunningTimer ? 'text-[#4F6660]' : 'text-gray-500 group-hover:text-[#4F6660]'
                    }`}
                    style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
                  >
                    Taken
                  </span>
                </button>
                
                {/* Task count badge */}
                <div className={`mt-2 px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                  hasRunningTimer ? 'bg-stone-100 text-[#4F6660]' : 'bg-gray-100 text-gray-600'
                }`}>
                  {tasks.filter(t => !t.completed).length + doelstellingenActions.filter((a: any) => !a.completed).length}
                </div>
              </div>
            );
          })()
        ) : (
          <div
            className="bg-white rounded-lg border border-gray-200 sticky top-0 max-h-full flex flex-col"
            style={{ borderColor: '#F0EEE9', boxShadow: '0 1px 4px rgba(0,0,0,0.018)', opacity: 0.88 }}
          >
            <div className="px-3 py-2 border-b flex items-center justify-between" style={{ borderColor: '#F0EEE9' }}>
              <div className="flex items-center space-x-2">
                <MessageSquare size={16} style={{ color: '#8DA8A0' }} />
                <h3 className="font-medium text-sm" style={{ color: '#5E6E68' }}>Taken</h3>
              </div>
              <div className="flex items-center space-x-1">
                <button
                  onClick={() => setShowTaskListManager(true)}
                  className="text-xs px-2 py-1 rounded-md bg-gray-100 hover:bg-gray-200 text-gray-700 flex items-center space-x-1"
                  title="Lijsten beheren"
                >
                  <EditIcon size={14} />
                  <span>Lijsten</span>
                </button>
                <button
                  onClick={() => toggleSidebar(true)}
                  className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors"
                  title="Sidebar sluiten"
                >
                  <ChevronRight size={16} className="text-gray-600" />
                </button>
              </div>
            </div>

            <div className="p-3 space-y-3 flex-1 flex flex-col overflow-hidden">
              {/* Lijst tabs – meer als elegante tabbladen */}
              <div className="flex flex-wrap gap-2 border-b border-gray-200 pb-1">
                {visibleTaskLists.map(list => (
                  <div
                    key={list.id}
                    className={`inline-flex items-center px-3 py-1.5 rounded-t-lg text-xs font-medium cursor-pointer transition-colors ${
                      activeTaskListId === list.id
                        ? 'bg-white text-[#4F6660] border border-b-white border-stone-500 shadow-sm'
                        : 'bg-transparent text-gray-600 hover:text-gray-800 border border-transparent hover:border-gray-200'
                    } ${
                      list.id === 'done' && shouldPulseKlaar() && activeTaskListId !== 'done'
                        ? 'animate-pulse-gentle'
                        : ''
                    }`}
                    onClick={() => list.id === 'done' ? handleKlaarClick() : setActiveTaskListId(list.id)}
                  >
                    <span>{list.name}</span>
                  </div>
                ))}
              </div>


              {/* Nieuwe taak invoer – direct onder de tabs (niet zichtbaar in Klaar/Alles/Goals/hub) */}
              {activeTaskListId !== 'done' && activeTaskListId !== 'all' && activeTaskListId !== 'goals'
               && activeTaskListId !== 'mastermind-hub' && activeTaskListId !== 'networkclub-hub' && (
                <div className="mt-2 space-y-1.5">
                  <div className="flex items-center space-x-2">
                    <button
                      type="button"
                      onClick={() => void addTaskToActiveList()}
                      className="p-1 rounded-full bg-stone-500 text-white hover:bg-[#4F6660]"
                    >
                      <Plus size={14} />
                    </button>
                    <input
                      type="text"
                      value={newTaskTitle}
                      onChange={e => setNewTaskTitle(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') void addTaskToActiveList();
                      }}
                      className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded-lg focus:ring-1 focus:ring-stone-500"
                      placeholder="Nieuwe taak..."
                    />
                  </div>
                  {userMemberships.length > 0 && (
                    <div className="flex items-center gap-2 pl-8">
                      <label className="text-[10px] text-gray-500 shrink-0 whitespace-nowrap" htmlFor="sk-share-meeting-task">
                        Ook in meeting:
                      </label>
                      <select
                        id="sk-share-meeting-task"
                        value={shareToMeetingEntityId}
                        onChange={e => setShareToMeetingEntityId(e.target.value)}
                        className="flex-1 min-w-0 text-[10px] border border-gray-200 rounded-md px-1.5 py-1 bg-white text-gray-700"
                      >
                        <option value="">Alleen persoonlijke agenda</option>
                        {userMemberships.map(m => (
                          <option key={m.id} value={m.id}>{m.name} ({m.type})</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              )}

              {/* Takenlijst - met scroll */}
              <div className="flex-1 overflow-y-auto mt-2 space-y-2 pr-1" style={{ scrollbarWidth: 'thin', scrollbarColor: '#DDDBD5 #F4F3F0' }}>
                {(activeTaskListId === 'mastermind-hub' || activeTaskListId === 'networkclub-hub') ? (
                  /* ── Groepstaken (Mastermind / Netwerkclub) ── */
                  (() => {
                    const hubType = activeTaskListId === 'mastermind-hub' ? ['mastermind', 'community'] : ['networkclub'];
                    const relevantGroups = userMemberships.filter((m: any) => hubType.includes(m.type));
                    if (!groupTasksLoaded) return <p className="text-xs text-gray-500 text-center py-4">Laden…</p>;
                    if (relevantGroups.length === 0) return (
                      <p className="text-xs text-gray-500 text-center py-4">
                        Je bent nog geen lid van een {activeTaskListId === 'mastermind-hub' ? 'mastermind' : 'netwerkclub'}.
                      </p>
                    );
                    return (
                      <div className="space-y-4">
                        {relevantGroups.map((group: any) => {
                          const gTasks = groupTasks.filter((t: any) => t.entityId === group.id);
                          const openTasks = gTasks.filter((t: any) => t.status !== 'done');
                          // Group by owner
                          const byOwner: Record<string, typeof gTasks> = {};
                          gTasks.forEach((t: any) => {
                            if (!byOwner[t.ownerUid]) byOwner[t.ownerUid] = [];
                            byOwner[t.ownerUid].push(t);
                          });
                          return (
                            <div key={group.id} className="border border-gray-200 rounded-lg overflow-hidden" style={{ borderColor: '#F0EEE9' }}>
                              {/* Groepsnaam header */}
                              <div className="px-3 py-2 bg-stone-50 border-b flex items-center justify-between" style={{ borderColor: '#F0EEE9' }}>
                                <span className="text-xs font-semibold text-stone-700">{group.name}</span>
                                {openTasks.length > 0 && (
                                  <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full font-medium">
                                    {openTasks.length} open
                                  </span>
                                )}
                              </div>
                              {/* Taken per lid */}
                              <div className="p-2 space-y-2">
                                {Object.keys(byOwner).length === 0 && (
                                  <p className="text-[11px] text-gray-400 text-center py-2">Nog geen taken voor deze groep.</p>
                                )}
                                {Object.entries(byOwner).map(([ownerUid, ownerTasks]) => {
                                  const first = (ownerTasks as any[])[0];
                                  const initials = (first.ownerName ?? '?').split(' ').map((w: string) => w[0]).slice(0, 2).join('').toUpperCase();
                                  return (
                                    <div key={ownerUid}>
                                      {/* Lid */}
                                      <div className="flex items-center gap-1.5 mb-1">
                                        {first.ownerAvatar
                                          ? <img src={first.ownerAvatar} alt={first.ownerName} className="w-4 h-4 rounded-full object-cover" />
                                          : <div className="w-4 h-4 rounded-full bg-stone-400 flex items-center justify-center text-[7px] font-bold text-white">{initials}</div>
                                        }
                                        <span className="text-[10px] font-medium text-gray-600">{first.ownerName}</span>
                                      </div>
                                      {(ownerTasks as any[]).map((task: any) => (
                                        <div key={task.id} className="flex items-center gap-2 px-1.5 py-1 rounded hover:bg-gray-50 group">
                                          <button
                                            onClick={async () => {
                                              const next = task.status === 'done' ? 'open' : 'done';
                                              await updateDoc(doc(db, 'mastermindTasks', task.id), { status: next });
                                            }}
                                            className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center transition-colors ${task.status === 'done' ? 'bg-emerald-500 border-emerald-500' : 'border-gray-300 hover:border-emerald-400'}`}
                                          >
                                            {task.status === 'done' && (
                                              <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                              </svg>
                                            )}
                                          </button>
                                          <span className={`text-[11px] flex-1 ${task.status === 'done' ? 'line-through text-gray-400' : 'text-gray-700'}`}>
                                            {task.title}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  );
                                })}
                                <div className="flex gap-1 mt-2 pt-2 border-t border-gray-100" style={{ borderColor: '#F0EEE9' }}>
                                  <input
                                    type="text"
                                    value={hubQuickAddTitle[group.id] || ''}
                                    onChange={e => setHubQuickAddTitle(prev => ({ ...prev, [group.id]: e.target.value }))}
                                    onKeyDown={e => {
                                      if (e.key === 'Enter') void submitHubGroupTask(group);
                                    }}
                                    placeholder={`Nieuwe taak voor ${group.name}…`}
                                    className="flex-1 min-w-0 px-2 py-1 text-[11px] border border-gray-200 rounded-md focus:ring-1 focus:ring-stone-500"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => void submitHubGroupTask(group)}
                                    className="shrink-0 px-2 py-1 rounded-md bg-[#4F6660] text-white text-[11px] font-semibold hover:bg-stone-600"
                                  >
                                    +
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()
                ) : activeTaskListId === 'goals' ? (
                  /* Doelstellingen acties gegroepeerd per doel, gesplitst in Gegenereerd/Handmatig */
                  doelstellingenGoals.length === 0 ? (
                    <p className="text-xs text-gray-500">
                      Geen actieve doelen gevonden.
                    </p>
                  ) : (
                    <>
                      {/* Inline + input zoals bij andere tabs */}
                      <div className="mt-2 flex items-center space-x-2 mb-3">
                        <button
                          onClick={() => {
                            if (newActionTitle.trim()) {
                              setShowNewActionModal(true);
                            }
                          }}
                          className="p-1 rounded-full bg-stone-500 text-white hover:bg-[#4F6660]"
                        >
                          <Plus size={14} />
                        </button>
                        <input
                          type="text"
                          value={newActionTitle}
                          onChange={e => setNewActionTitle(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter' && newActionTitle.trim()) {
                              setShowNewActionModal(true);
                            }
                          }}
                          className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded-lg focus:ring-1 focus:ring-stone-500"
                          placeholder="Nieuwe actie..."
                        />
                      </div>

                      {/* View Mode Switcher */}
                      {(() => {
                        const incompleteActions = doelstellingenActions.filter((a: any) => !a.completed);
                        const totalCount = incompleteActions.length;
                        
                        return (
                          <div className="grid grid-cols-4 gap-1 mb-3 px-2">
                            <button
                              onClick={() => setActiesViewMode('focus')}
                              className={`px-2 py-1.5 text-[10px] font-medium rounded transition-all ${
                                actiesViewMode === 'focus'
                                  ? 'bg-stone-500 text-white shadow-sm'
                                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                            >
                              <GoalIcon size={10} className="inline text-red-500" /> Focus ({incompleteActions.filter((a: any) => a.isFocus === true).length})
                            </button>
                            <button
                              onClick={() => setActiesViewMode('milestones')}
                              className={`px-2 py-1.5 text-[10px] font-medium rounded transition-all ${
                                actiesViewMode === 'milestones'
                                  ? 'bg-blue-500 text-white shadow-sm'
                                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                            >
                              <DocumentIcon size={10} className="inline" /> Mijlpalen
                            </button>
                            <button
                              onClick={() => setActiesViewMode('actueel')}
                              className={`px-2 py-1.5 text-[10px] font-medium rounded transition-all ${
                                actiesViewMode === 'actueel'
                                  ? 'bg-green-500 text-white shadow-sm'
                                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                            >
                              <PlayIcon size={12} className="inline" /> Actueel
                            </button>
                            <button
                              onClick={() => setActiesViewMode('full')}
                              className={`px-2 py-1.5 text-[10px] font-medium rounded transition-all ${
                                actiesViewMode === 'full'
                                  ? 'bg-gray-600 text-white shadow-sm'
                                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                            >
                              <FolderIcon size={12} className="inline" /> Alles ({totalCount})
                            </button>
                          </div>
                        );
                      })()}

                      {/* Acties weergave op basis van view mode */}
                      {(() => {
                        const incompleteActions = doelstellingenActions.filter((a: any) => !a.completed);
                        
                        // Helper: Check if deadline is urgent (< 3 dagen)
                        const isUrgent = (action: any) => {
                          if (!action.targetDate) return false;
                          const targetDate = new Date(action.targetDate);
                          const today = new Date();
                          const diffDays = Math.ceil((targetDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                          return diffDays >= 0 && diffDays <= 3;
                        };
                        
                        // Helper: Check if action is new (< 24 hours)
                        const isNew = (action: any) => {
                          if (!action.createdAt) return false;
                          const createdDate = action.createdAt?.toDate ? action.createdAt.toDate() : new Date(action.createdAt);
                          const now = new Date();
                          const diffHours = (now.getTime() - createdDate.getTime()) / (1000 * 60 * 60);
                          return diffHours < 24;
                        };
                        
                        // Helper: Render smart badges
                        const renderBadges = (action: any) => {
                          const badges = [];
                          if (isUrgent(action)) badges.push(<span key="urgent" className="text-[8px] bg-red-100 text-red-600 px-1 py-0.5 rounded font-bold"><FireIcon size={10} className="inline" /> URGENT</span>);
                          if (isNew(action)) badges.push(<span key="new" className="text-[8px] bg-green-100 text-green-600 px-1 py-0.5 rounded font-bold">🆕 NIEUW</span>);
                          return badges.length > 0 ? <div className="flex gap-1 mt-1">{badges}</div> : null;
                        };
                        
                        // FOCUS VIEW: Acties met isFocus=true
                        if (actiesViewMode === 'focus') {
                          const focusActions = incompleteActions
                            .filter((a: any) => a.isFocus === true)
                            .sort((a: any, b: any) => {
                              // Prioriteit: urgent > recent > order
                              const aUrgent = isUrgent(a);
                              const bUrgent = isUrgent(b);
                              if (aUrgent !== bUrgent) return bUrgent ? 1 : -1;
                              
                              const aRecent = isNew(a);
                              const bRecent = isNew(b);
                              if (aRecent !== bRecent) return bRecent ? 1 : -1;
                              
                              return (a.order ?? a.createdAt) - (b.order ?? b.createdAt);
                            });
                          
                          if (focusActions.length === 0) {
                            return (
                              <div className="text-center py-8 px-2">
                                <GoalIcon size={32} className="text-red-500 mx-auto mb-2" />
                                <p className="text-xs text-gray-600 font-semibold">Geen focus acties</p>
                                <p className="text-[10px] text-gray-500 mt-1">
                                  Vink acties aan in de Mijlpalen tab om ze hier te zien
                                </p>
                              </div>
                            );
                          }
                          
                          return (
                            <div>
                              <div className="flex items-center gap-2 px-2 py-1 bg-gradient-to-r from-stone-50 to-transparent border-l-2 border-[#7A9488] mb-2">
                                <span className="text-[10px] font-bold text-[#4F6660] flex items-center gap-1"><GoalIcon size={10} className="text-red-500" /> FOCUS ACTIES ({focusActions.length})</span>
                              </div>
                              <DndContext
                                sensors={sensors}
                                collisionDetection={closestCenter}
                                onDragEnd={handleDoelActiesReorder}
                              >
                                <SortableContext items={focusActions.map(a => a.id)} strategy={verticalListSortingStrategy}>
                                  {focusActions.map((action: any, index: any) => {
                                    const goal = doelstellingenGoals.find((g: any) => g.id === action.goalId);
                                    const milestone = doelstellingenMilestones.find((m: any) => m.id === action.milestoneId);
                                    return (
                                      <div key={action.id} className="mb-1">
                                        <SortableActionCard action={action} goal={goal} milestone={milestone} />
                                        {renderBadges(action)}
                                      </div>
                                    );
                                  })}
                                </SortableContext>
                              </DndContext>
                            </div>
                          );
                        }
                        
                        // MILESTONES VIEW: Accordion met mijlpalen
                        if (actiesViewMode === 'milestones') {
                          const milestoneGroups = new Map<string, any[]>();
                          const noMilestoneActions: any[] = [];
                          
                          incompleteActions.forEach((action: any) => {
                            if (action.milestoneId) {
                              if (!milestoneGroups.has(action.milestoneId)) {
                                milestoneGroups.set(action.milestoneId, []);
                              }
                              milestoneGroups.get(action.milestoneId)!.push(action);
                            } else {
                              noMilestoneActions.push(action);
                            }
                          });
                          
                          if (milestoneGroups.size === 0 && noMilestoneActions.length === 0) {
                            return <p className="text-xs text-gray-500 px-2">Geen acties</p>;
                          }
                          
                          // Kleurenschema voor mijlpalen
                          const milestoneColors = [
                            { bg: 'bg-blue-50', border: 'border-blue-400', text: 'text-blue-700', hover: 'hover:bg-blue-100' },
                            { bg: 'bg-purple-50', border: 'border-purple-400', text: 'text-purple-700', hover: 'hover:bg-purple-100' },
                            { bg: 'bg-green-50', border: 'border-green-400', text: 'text-green-700', hover: 'hover:bg-green-100' },
                            { bg: 'bg-stone-50', border: 'border-[#7A9488]', text: 'text-[#4F6660]', hover: 'hover:bg-stone-100' },
                            { bg: 'bg-pink-50', border: 'border-pink-400', text: 'text-pink-700', hover: 'hover:bg-pink-100' },
                            { bg: 'bg-teal-50', border: 'border-teal-400', text: 'text-teal-700', hover: 'hover:bg-teal-100' },
                            { bg: 'bg-indigo-50', border: 'border-indigo-400', text: 'text-indigo-700', hover: 'hover:bg-indigo-100' },
                            { bg: 'bg-cyan-50', border: 'border-cyan-400', text: 'text-cyan-700', hover: 'hover:bg-cyan-100' },
                          ];
                          
                          // Helper function to toggle milestone expansion
                          const toggleMilestone = (milestoneId: string) => {
                            setExpandedMilestones(prev => {
                              const newSet = new Set(prev);
                              if (newSet.has(milestoneId)) {
                                newSet.delete(milestoneId);
                              } else {
                                newSet.add(milestoneId);
                              }
                              return newSet;
                            });
                          };
                          
                          return (
                            <div className="space-y-1">
                              {/* Info header */}
                              <div className="px-2 py-1.5 bg-gray-50 border border-gray-200 rounded text-[9px] text-gray-600 mb-2">
                                <TipIcon size={14} className="inline" /> <span className="font-semibold">Kleurcodes:</span> Elke mijlpaal heeft een eigen kleur. Klik om uit/in te klappen.
                              </div>
                              
                              {Array.from(milestoneGroups.entries()).map(([milestoneId, actions]: [string, any[]], index: number) => {
                                const milestone = doelstellingenMilestones.find((m: any) => m.id === milestoneId);
                                const goal = doelstellingenGoals.find((g: any) => g.id === actions[0]?.goalId);
                                const completedCount = actions.filter((a: any) => a.completed).length;
                                const colorScheme = milestoneColors[index % milestoneColors.length];
                                const isExpanded = expandedMilestones.has(milestoneId);
                                
                                return (
                                  <div key={milestoneId} className="mb-1">
                                    {/* ✅ ACCORDION HEADER - Clickable */}
                                    <button
                                      onClick={() => toggleMilestone(milestoneId)}
                                      className={`w-full px-2 py-1.5 ${colorScheme.bg} ${colorScheme.hover} border-l-4 ${colorScheme.border} transition-all rounded text-left flex items-center justify-between ${
                                        pulsingMilestoneId === milestoneId ? 'animate-pulse-slow-3x ring-2 ring-[#7A9488]' : ''
                                      }`}
                                    >
                                      <div className="flex-1">
                                        <div className={`text-[10px] font-bold ${colorScheme.text} flex items-center gap-1`}>
                                          <span className="transform transition-transform" style={{ display: 'inline-block', transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}>
                                            ▶
                                          </span>
                                          📍 {milestone?.title || 'Mijlpaal'}
                                        </div>
                                        <div className="text-[9px] text-gray-500 ml-3">
                                          {goal?.title} • {completedCount}/{actions.length} afgerond
                                        </div>
                                      </div>
                                    </button>
                                    
                                    {/* ✅ ACCORDION CONTENT - Only show when expanded */}
                                    {isExpanded && (
                                      <div className="mt-1 ml-2">
                                        <DndContext
                                          sensors={sensors}
                                          collisionDetection={closestCenter}
                                          onDragEnd={handleDoelActiesReorder}
                                        >
                                          <SortableContext items={actions.map(a => a.id)} strategy={verticalListSortingStrategy}>
                                            {actions.map((action: any) => (
                                              <div key={action.id} className="mb-1 relative">
                                                {/* Kleur stripe */}
                                                <div className={`absolute left-0 top-0 bottom-0 w-1 ${colorScheme.border} rounded-l`} style={{ zIndex: 1 }} />
                                                <SortableActionCard action={action} goal={goal} milestone={milestone} />
                                                <div className="flex items-center gap-1 mt-1 px-2">
                                                  <span className={`text-[8px] font-semibold px-1.5 py-0.5 rounded ${colorScheme.bg} ${colorScheme.text} border ${colorScheme.border}`}>
                                                    📍 {milestone?.title}
                                                  </span>
                                                  {/* Focus checkbox - alleen in milestones view */}
                                                  {!action.completed && (
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleToggleFocusAction(action);
                                                      }}
                                                      disabled={processingActionId === action.id}
                                                      className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[8px] font-medium transition-all ${
                                                        action.isFocus
                                                          ? 'bg-stone-100 text-[#4F6660] border border-stone-300'
                                                          : 'bg-gray-100 text-gray-600 border border-gray-300 hover:bg-gray-200'
                                                      }`}
                                                      title={action.isFocus ? 'Verwijder uit Focus' : 'Voeg toe aan Focus'}
                                                    >
                                                      {action.isFocus ? '✓ ' : ''}<GoalIcon size={10} className="inline text-red-500" /> Focus
                                                    </button>
                                                  )}
                                                  {renderBadges(action)}
                                                </div>
                                              </div>
                                            ))}
                                          </SortableContext>
                                        </DndContext>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                              
                              {noMilestoneActions.length > 0 && (
                                <div className="mt-2">
                                  <div className="px-2 py-1 bg-gray-100 border-l-2 border-gray-400 mb-1 rounded">
                                    <span className="text-[10px] font-bold text-gray-700 flex items-center gap-1"><DocumentIcon size={10} /> Zonder mijlpaal ({noMilestoneActions.length})</span>
                                  </div>
                                  <DndContext
                                    sensors={sensors}
                                    collisionDetection={closestCenter}
                                    onDragEnd={handleDoelActiesReorder}
                                  >
                                    <SortableContext items={noMilestoneActions.map(a => a.id)} strategy={verticalListSortingStrategy}>
                                      {noMilestoneActions.map((action: any) => {
                                        const goal = doelstellingenGoals.find((g: any) => g.id === action.goalId);
                                        return (
                                          <div key={action.id} className="mb-1 ml-2">
                                            <SortableActionCard action={action} goal={goal} milestone={undefined} />
                                            {renderBadges(action)}
                                          </div>
                                        );
                                      })}
                                    </SortableContext>
                                  </DndContext>
                                </div>
                              )}
                            </div>
                          );
                        }
                        
                        // ✅ ACTUEEL VIEW: Alleen acties die op play/pauze staan
                        if (actiesViewMode === 'actueel') {
                          // Filter actions that are currently being worked on (have active timers or are marked as "in progress")
                          const activeActions = incompleteActions.filter((action: any) => {
                            // Check if action has a timer running or is paused
                            return action.timerState === 'playing' || action.timerState === 'paused' || action.status === 'in_progress';
                          });
                          
                          if (activeActions.length === 0) {
                            return (
                              <div className="text-center py-8 px-2">
                                <div className="mb-2"><PauseIcon size={32} /></div>
                                <p className="text-xs text-gray-600 font-semibold">Geen actieve acties</p>
                                <p className="text-[10px] text-gray-500 mt-1">
                                  Druk op <PlayIcon size={14} className="inline" /> bij een actie om deze hier te zien
                                </p>
                              </div>
                            );
                          }
                          
                          return (
                            <div className="space-y-2">
                              {/* Info header */}
                              <div className="px-2 py-1.5 bg-green-50 border border-green-200 rounded text-[9px] text-green-700 mb-2">
                                <PlayIcon size={14} className="inline" /> <span className="font-semibold">Actueel:</span> Alle acties waar je nu mee bezig bent
                              </div>
                              
                              <DndContext
                                sensors={sensors}
                                collisionDetection={closestCenter}
                                onDragEnd={handleDoelActiesReorder}
                              >
                                <SortableContext items={activeActions.map(a => a.id)} strategy={verticalListSortingStrategy}>
                                  {activeActions.map((action: any) => {
                                    const goal = doelstellingenGoals.find((g: any) => g.id === action.goalId);
                                    const milestone = action.milestoneId 
                                      ? doelstellingenMilestones.find((m: any) => m.id === action.milestoneId)
                                      : undefined;
                                    
                                    return (
                                      <div key={action.id} className="mb-1 relative">
                                        {/* Green indicator for active items */}
                                        <div className="absolute left-0 top-0 bottom-0 w-1 bg-green-500 rounded-l" style={{ zIndex: 1 }} />
                                        <SortableActionCard action={action} goal={goal} milestone={milestone} />
                                        <div className="flex items-center gap-1 mt-1 px-2">
                                          {/* Status badge */}
                                          <span className={`text-[8px] font-semibold px-1.5 py-0.5 rounded ${
                                            action.timerState === 'playing' 
                                              ? 'bg-green-100 text-green-700 border border-green-400' 
                                              : 'bg-yellow-100 text-yellow-700 border border-yellow-400'
                                          }`}>
                                            {action.timerState === 'playing' ? <><PlayIcon size={12} className="inline" /> BEZIG</> : <><PauseIcon size={12} className="inline" /> PAUZE</>}
                                          </span>
                                          {milestone && (
                                            <span className="text-[8px] font-semibold px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-400">
                                              📍 {milestone.title}
                                            </span>
                                          )}
                                          {renderBadges(action)}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </SortableContext>
                              </DndContext>
                            </div>
                          );
                        }
                        
                        // FULL VIEW: Alles gegroepeerd per doel
                        if (actiesViewMode === 'full') {
                          const goalGroups = new Map<string, any[]>();
                          
                          incompleteActions.forEach((action: any) => {
                            if (!goalGroups.has(action.goalId)) {
                              goalGroups.set(action.goalId, []);
                            }
                            goalGroups.get(action.goalId)!.push(action);
                          });
                          
                          if (goalGroups.size === 0) {
                            return <p className="text-xs text-gray-500 px-2">Nog geen acties</p>;
                          }
                          
                          return (
                            <div className="space-y-3">
                              {Array.from(goalGroups.entries()).map(([goalId, actions]: [string, any[]]) => {
                                const goal = doelstellingenGoals.find((g: any) => g.id === goalId);
                                const generatedActions = actions.filter((a: any) => a.origin && a.origin !== 'manual');
                                const manualActions = actions.filter((a: any) => !a.origin || a.origin === 'manual');
                                
                                return (
                                  <div key={goalId}>
                                    <div className="text-[10px] font-semibold text-gray-700 mb-1 px-2">
                                      <GoalIcon size={10} className="inline text-red-500" /> {goal?.title} ({actions.length})
                                    </div>
                                    
                                    {generatedActions.length > 0 && (
                                      <div className="mb-2">
                                        <div className="px-2 py-0.5 bg-gradient-to-r from-blue-50 to-transparent border-l-2 border-blue-400 mb-1">
                                          <span className="text-[9px] font-bold text-blue-700">🤖 GEGENEREERD ({generatedActions.length})</span>
                                        </div>
                                        <DndContext
                                          sensors={sensors}
                                          collisionDetection={closestCenter}
                                          onDragEnd={handleDoelActiesReorder}
                                        >
                                          <SortableContext items={generatedActions.map(a => a.id)} strategy={verticalListSortingStrategy}>
                                            {generatedActions.map((action: any) => {
                                              const milestone = doelstellingenMilestones.find((m: any) => m.id === action.milestoneId);
                                              return (
                                                <div key={action.id} className="mb-1">
                                                  <SortableActionCard action={action} goal={goal} milestone={milestone} />
                                                  {renderBadges(action)}
                                                </div>
                                              );
                                            })}
                                          </SortableContext>
                                        </DndContext>
                                      </div>
                                    )}
                                    
                                    {manualActions.length > 0 && (
                                      <div>
                                        <div className="px-2 py-0.5 bg-gradient-to-r from-gray-100 to-transparent border-l-2 border-gray-400 mb-1">
                                          <span className="text-[9px] font-bold text-gray-700 flex items-center gap-1"><EditIcon size={10} /> HANDMATIG ({manualActions.length})</span>
                                        </div>
                                        <DndContext
                                          sensors={sensors}
                                          collisionDetection={closestCenter}
                                          onDragEnd={handleDoelActiesReorder}
                                        >
                                          <SortableContext items={manualActions.map(a => a.id)} strategy={verticalListSortingStrategy}>
                                            {manualActions.map((action: any) => {
                                              const milestone = doelstellingenMilestones.find((m: any) => m.id === action.milestoneId);
                                              return (
                                                <div key={action.id} className="mb-1">
                                                  <SortableActionCard action={action} goal={goal} milestone={milestone} />
                                                  {renderBadges(action)}
                                                </div>
                                              );
                                            })}
                                          </SortableContext>
                                        </DndContext>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          );
                        }
                        
                        return null;
                      })()}
                      
                      {/* Empty state als geen incomplete acties zijn */}
                      {doelstellingenActions.filter((a: any) => !a.completed).length === 0 && (
                        <p className="text-xs text-gray-500 px-2">
                          Nog geen acties. Start een sparring of voeg handmatig acties toe.
                        </p>
                      )}
                    </>
                  )
                ) : activeTaskListId === 'done' ? (
                  <>
                    {/* Klaar filters */}
                    <div className="space-y-2 pb-2 border-b border-gray-200">
                      {/* Categorie filter - dynamisch op basis van bestaande tabbladen */}
                      <div className="flex flex-wrap gap-1">
                        <button
                          onClick={() => setDoneFilterCategory('all')}
                          className={`px-2 py-0.5 text-[10px] rounded ${
                            doneFilterCategory === 'all'
                              ? 'bg-gray-600 text-white'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          Alles
                        </button>
                        {/* Task list filters */}
                        {taskLists
                          .filter(list => list.id !== 'all' && list.id !== 'done')
                          .map(list => (
                            <button
                              key={list.id}
                              onClick={() => setDoneFilterCategory(list.id)}
                              className={`px-2 py-0.5 text-[10px] rounded ${
                                doneFilterCategory === list.id
                                  ? 'bg-gray-600 text-white'
                                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                            >
                              {list.name}
                            </button>
                          ))}
                        {/* Doelen Acties filter - alleen als er doelstellingen acties zijn */}
                        {doelstellingenActions.length > 0 && (
                          <button
                            onClick={() => setDoneFilterCategory('goals')}
                            className={`px-2 py-0.5 text-[10px] rounded ${
                              doneFilterCategory === 'goals'
                                ? 'bg-blue-600 text-white'
                                : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                            }`}
                          >
                            <GoalIcon size={10} className="inline text-red-500" /> Doelen Acties
                          </button>
                        )}
                      </div>
                      {/* Tijdsinterval filter */}
                      <div className="flex gap-1">
                        {[
                          { id: 'week' as const, label: 'Week' },
                          { id: 'month' as const, label: 'Maand' },
                          { id: 'year' as const, label: 'Jaar' }
                        ].map(interval => (
                          <button
                            key={interval.id}
                            onClick={() => setDoneFilterTimeInterval(interval.id)}
                            className={`px-2 py-0.5 text-[10px] rounded ${
                              doneFilterTimeInterval === interval.id
                                ? 'bg-gray-600 text-white'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                          >
                            {interval.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    {/* Gefilterde voltooide taken */}
                    {(() => {
                      const doneTasks = getFilteredDoneTasks();
                      const completedDoelActies = getCompletedDoelstellingenForInterval();

                      if (doneTasks.length === 0 && completedDoelActies.length === 0) {
                        return (
                          <p className="text-xs text-gray-500">
                            Geen voltooide taken in deze periode.
                          </p>
                        );
                      }

                      return (
                        <>
                          {doneTasks.map(task => (
                            <div
                              key={task.id}
                              className="flex items-center justify-between px-2 py-1.5 rounded-lg bg-gray-100 border border-gray-200 group"
                            >
                              <div className="flex items-center space-x-2 flex-1 min-w-0">
                                <CheckCircle
                                  size={14}
                                  className="text-gray-400 flex-shrink-0"
                                />
                                <span className="text-xs text-gray-500 truncate">
                                  {task.title}
                                </span>
                              </div>
                              <div className="flex items-center space-x-2 flex-shrink-0">
                                {task.originalListId && (
                                  <span className="text-[9px] text-gray-400 bg-gray-50 px-1 rounded">
                                    {taskLists.find(l => l.id === task.originalListId)?.name || task.originalListId}
                                  </span>
                                )}
                                {task.createdAt && task.completedAt && (
                                  <span className="text-[9px] text-stone-500 font-medium" title="Doorlooptijd">
                                    {formatTaskDuration(task.createdAt, task.completedAt)}
                                  </span>
                                )}
                                {task.completedAt && (
                                  <span className="text-[10px] text-gray-400">
                                    {task.completedAt.toLocaleDateString('nl-NL', {
                                      day: 'numeric',
                                      month: 'short'
                                    })}
                                  </span>
                                )}
                                <button
                                  onClick={() => restoreTask(task.id)}
                                  className="opacity-0 group-hover:opacity-100 text-[10px] text-gray-400 hover:text-stone-500 transition-opacity"
                                  title="Terugzetten"
                                >
                                  ↩
                                </button>
                              </div>
                            </div>
                          ))}

                          {/* Toon voltooide Doelen Acties alleen bij "Alles" of "🎯 Doelen Acties" filter */}
                          {completedDoelActies.length > 0 && 
                           (doneFilterCategory === 'all' || doneFilterCategory === 'goals') && (
                            <div className="mt-4 space-y-2 border-t border-gray-200 pt-3">
                              <div className="flex items-center text-[10px] font-semibold text-[#4F6660] gap-2 mb-3">
                                <span className="flex items-center gap-1"><GoalIcon size={10} className="text-red-500" /> Doelen Acties</span>
                                <span className="px-1.5 py-0.5 rounded-full bg-stone-50 text-[#4F6660]">
                                  {completedDoelActies.length}
                                </span>
                              </div>
                              
                              {/* Groepeer per doel en mijlpaal met accordion */}
                              {doelstellingenGoals.map((goal: any) => {
                                const goalCompletedActions = completedDoelActies.filter((a: any) => a.goalId === goal.id);
                                if (goalCompletedActions.length === 0) return null;

                                // Groepeer acties per mijlpaal
                                const milestoneGroups = new Map<string, any[]>();
                                const noMilestoneActions: any[] = [];
                                
                                goalCompletedActions.forEach((action: any) => {
                                  if (action.milestoneId) {
                                    if (!milestoneGroups.has(action.milestoneId)) {
                                      milestoneGroups.set(action.milestoneId, []);
                                    }
                                    milestoneGroups.get(action.milestoneId)!.push(action);
                                  } else {
                                    noMilestoneActions.push(action);
                                  }
                                });

                                // Kleurenschema voor mijlpalen (zelfde als in milestones view)
                                const milestoneColors = [
                                  { bg: 'bg-blue-50', border: 'border-blue-400', text: 'text-blue-700', hover: 'hover:bg-blue-100' },
                                  { bg: 'bg-purple-50', border: 'border-purple-400', text: 'text-purple-700', hover: 'hover:bg-purple-100' },
                                  { bg: 'bg-green-50', border: 'border-green-400', text: 'text-green-700', hover: 'hover:bg-green-100' },
                                  { bg: 'bg-stone-50', border: 'border-[#7A9488]', text: 'text-[#4F6660]', hover: 'hover:bg-stone-100' },
                                  { bg: 'bg-pink-50', border: 'border-pink-400', text: 'text-pink-700', hover: 'hover:bg-pink-100' },
                                  { bg: 'bg-teal-50', border: 'border-teal-400', text: 'text-teal-700', hover: 'hover:bg-teal-100' },
                                  { bg: 'bg-indigo-50', border: 'border-indigo-400', text: 'text-indigo-700', hover: 'hover:bg-indigo-100' },
                                  { bg: 'bg-cyan-50', border: 'border-cyan-400', text: 'text-cyan-700', hover: 'hover:bg-cyan-100' },
                                ];

                                return (
                                  <div key={`done-${goal.id}`} className="space-y-2 mb-4">
                                    <div className="text-[10px] font-semibold text-gray-600 px-2 mb-2">
                                      {goal.title}
                                    </div>
                                    
                                    {/* Mijlpalen met accordion */}
                                    {Array.from(milestoneGroups.entries()).map(([milestoneId, actions]: [string, any[]], index: number) => {
                                      const milestone = doelstellingenMilestones.find((m: any) => m.id === milestoneId);
                                      const colorScheme = milestoneColors[index % milestoneColors.length];
                                      const isExpanded = expandedMilestones.has(milestoneId);
                                      const completedCount = actions.length;

                                      return (
                                        <div key={milestoneId} className="mb-1">
                                          {/* Accordion Header */}
                                          <button
                                            onClick={() => {
                                              setExpandedMilestones(prev => {
                                                const newSet = new Set(prev);
                                                if (newSet.has(milestoneId)) {
                                                  newSet.delete(milestoneId);
                                                } else {
                                                  newSet.add(milestoneId);
                                                }
                                                return newSet;
                                              });
                                            }}
                                            className={`w-full px-2 py-1.5 ${colorScheme.bg} ${colorScheme.hover} border-l-4 ${colorScheme.border} transition-all rounded text-left flex items-center justify-between`}
                                          >
                                            <div className="flex-1">
                                              <div className={`text-[10px] font-bold ${colorScheme.text} flex items-center gap-1`}>
                                                <span className="transform transition-transform" style={{ display: 'inline-block', transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}>
                                                  ▶
                                                </span>
                                                <SuccessIcon size={14} className="inline" /> {milestone?.title || 'Mijlpaal'}
                                              </div>
                                              <div className="text-[9px] text-gray-500 ml-3">
                                                {completedCount} {completedCount === 1 ? 'actie' : 'acties'} voltooid
                                              </div>
                                            </div>
                                          </button>
                                          
                                          {/* Accordion Content - Afgevinkte acties */}
                                          {isExpanded && (
                                            <div className="mt-1 ml-2 space-y-1">
                                              {actions.map((action: any) => (
                                                <div key={action.id} className="mb-1 relative">
                                                  <div className={`absolute left-0 top-0 bottom-0 w-1 ${colorScheme.border} rounded-l`} style={{ zIndex: 1 }} />
                                                  {renderActionCard(action, goal, milestone)}
                                                </div>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                    
                                    {/* Acties zonder mijlpaal */}
                                    {noMilestoneActions.length > 0 && (
                                      <div className="mt-2 ml-2">
                                        <div className="px-2 py-1 bg-gray-100 border-l-2 border-gray-400 mb-1 rounded">
                                          <span className="text-[10px] font-bold text-gray-700 flex items-center gap-1"><DocumentIcon size={10} /> Zonder mijlpaal ({noMilestoneActions.length})</span>
                                        </div>
                                        {noMilestoneActions.map((action: any) => (
                                          <div key={action.id} className="mb-1 ml-2">
                                            {renderActionCard(action, goal, undefined)}
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </>
                ) : getTasksForActiveList().length === 0 ? (
                  <p className="text-xs text-gray-500">
                    Nog geen taken in deze lijst.
                  </p>
                ) : (
                  getTasksForActiveList().map(task => (
                    <div
                      key={task.id}
                      draggable={!task.completed}
                      onDragStart={(e) => {
                        console.log('🔵 onDragStart triggered for:', task.title, 'completed:', task.completed);
                        if (!task.completed) {
                          const currentList = taskLists.find(l => l.id === activeTaskListId);
                          console.log('🔵 Current list:', currentList?.name);
                          if (currentList) {
                            handleTaskDragStart(e, task, currentList);
                          } else {
                            console.log('❌ No current list found!');
                          }
                        } else {
                          console.log('❌ Task is completed, cannot drag');
                        }
                      }}
                      className={`flex items-center justify-between px-2 py-1.5 rounded-lg border ${
                        task.isTimerRunning 
                          ? 'bg-stone-50 border-stone-200' 
                          : 'bg-gray-50 border-gray-100'
                      } ${!task.completed ? 'cursor-move hover:bg-blue-50' : ''}`}
                    >
                      <button
                        onClick={() => toggleTaskCompleted(task.id)}
                        className="mr-2"
                      >
                        <Circle
                          size={16}
                          className="text-gray-400 hover:text-green-500"
                        />
                      </button>
                      <span className="flex-1 text-xs text-gray-800 truncate">
                        {task.title}
                      </span>
                      {/* Timer info + toggle */}
                      <div className="flex items-center space-x-1 ml-2">
                        {(task.activeTime || task.isTimerRunning) && (
                          <span className="text-[10px] text-gray-500">
                            {formatActiveTime(task.activeTime || 0, task.timerStartedAt, task.isTimerRunning)}
                          </span>
                        )}
                        <button
                          onClick={() => toggleTaskTimer(task.id)}
                          className={`w-5 h-5 flex items-center justify-center rounded transition-colors ${
                            task.isTimerRunning
                              ? 'bg-stone-500 text-white hover:bg-[#4F6660]'
                              : 'bg-gray-200 text-gray-600 hover:bg-stone-100 hover:text-[#4F6660]'
                          }`}
                          title={task.isTimerRunning ? 'Pauzeer timer' : 'Start timer'}
                        >
                          {task.isTimerRunning ? (
                            <span className="text-[10px] font-bold">▌▌</span>
                          ) : (
                            <span className="text-[10px] ml-0.5">▶</span>
                          )}
                        </button>
                        <button
                          onClick={() => deleteTask(task.id)}
                          className="text-[11px] text-gray-400 hover:text-red-500"
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
        </div>
      </div>

      {/* Task Detail Modal */}
      {showTaskModal && renderTaskDetailModal()}

      {/* Restore Action Bevestigingsmodal */}
      {restoreActionModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setRestoreActionModal(null)}
        >
          <div 
            className="bg-white rounded-lg shadow-xl max-w-sm w-full p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-base font-semibold mb-3 text-gray-800">
              Actie terugzetten?
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Wil je deze actie terugzetten naar niet-voltooid?
            </p>
            <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-xs text-gray-700 font-medium line-clamp-2">
                {restoreActionModal.actionTitle}
              </p>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setRestoreActionModal(null);
                }}
                className="px-5 py-2.5 text-sm font-medium rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 active:bg-gray-300 transition-colors cursor-pointer min-w-[80px]"
                type="button"
              >
                Nee
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleRestoreDoelstellingenAction();
                }}
                disabled={processingActionId === restoreActionModal.actionId}
                className="px-5 py-2.5 text-sm font-medium rounded-lg bg-stone-500 text-white hover:bg-[#4F6660] active:bg-[#4F6660] disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer min-w-[80px]"
                type="button"
              >
                {processingActionId === restoreActionModal.actionId ? 'Bezig...' : 'Ja'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Compacte modal voor doel selectie */}
      {showNewActionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => {
          setShowNewActionModal(false);
          setNewActionGoalId('');
          setNewActionMilestoneId('');
        }}>
          <div className="bg-white rounded-lg p-5 w-full max-w-sm mx-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-base font-semibold mb-3 text-gray-800">Koppel aan doel</h3>
            
            {/* Toon de ingevoerde titel */}
            <div className="mb-3 p-2 bg-gray-50 rounded text-sm text-gray-700 border border-gray-200">
              <DocumentIcon size={14} className="inline text-gray-500" /> {newActionTitle}
            </div>
            
            <div className="space-y-3">
              {/* Doel selectie */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Doel *</label>
                <select
                  value={newActionGoalId}
                  onChange={(e) => {
                    setNewActionGoalId(e.target.value);
                    setNewActionMilestoneId(''); // Reset milestone bij doel wijziging
                  }}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stone-500 text-xs"
                  autoFocus
                >
                  <option value="">Selecteer een doel...</option>
                  {doelstellingenGoals.map((goal: any) => (
                    <option key={goal.id} value={goal.id}>
                      {goal.title}
                    </option>
                  ))}
                </select>
              </div>

              {/* Milestone selectie (optioneel) */}
              {newActionGoalId && doelstellingenMilestones.filter((m: any) => m.goalId === newActionGoalId).length > 0 && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Mijlpaal (optioneel)</label>
                  <select
                    value={newActionMilestoneId}
                    onChange={(e) => setNewActionMilestoneId(e.target.value)}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stone-500 text-xs"
                  >
                    <option value="">Geen specifieke mijlpaal</option>
                    {doelstellingenMilestones
                      .filter((m: any) => m.goalId === newActionGoalId)
                      .map((milestone: any) => (
                        <option key={milestone.id} value={milestone.id}>
                          {milestone.title}
                        </option>
                      ))}
                  </select>
                </div>
              )}
            </div>

            {/* Buttons */}
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => {
                  setShowNewActionModal(false);
                  setNewActionGoalId('');
                  setNewActionMilestoneId('');
                }}
                className="flex-1 px-3 py-1.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-xs font-medium"
              >
                Annuleren
              </button>
              <button
                onClick={handleAddManualDoelAction}
                disabled={!newActionGoalId}
                className="flex-1 px-3 py-1.5 bg-stone-500 text-white rounded-lg hover:bg-[#4F6660] disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors text-xs font-medium"
              >
                Toevoegen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Event Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Afspraak Bewerken</h3>
                
                <div className="space-y-4">
                    <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Titel</label>
                <input
                  type="text"
                  value={editForm.title}
                  onChange={(e) => setEditForm({...editForm, title: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Afspraak titel"
                />
                    </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Beschrijving</label>
                <textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder="Beschrijving van de afspraak"
                />
                  </div>
                  
                    <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Datum</label>
                <input
                  type="date"
                  value={editForm.startDate}
                  onChange={(e) => setEditForm({...editForm, startDate: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                    </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Starttijd</label>
                  <input
                    type="time"
                    value={editForm.startTime}
                    onChange={(e) => setEditForm({...editForm, startTime: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                      </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Eindtijd</label>
                  <input
                    type="time"
                    value={editForm.endTime}
                    onChange={(e) => setEditForm({...editForm, endTime: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                      </div>
                  </div>
                  
                    <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select
                  value={editForm.type}
                  onChange={(e) => setEditForm({...editForm, type: e.target.value as CalendarEvent['type']})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="google">Google Calendar</option>
                  <option value="planning">Planning</option>
                  <option value="podcast">Podcast</option>
                  <option value="meeting">Meeting</option>
                  <option value="call">Call</option>
                  <option value="personal">Personal</option>
                </select>
                    </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Kleur</label>
                <div className="grid grid-cols-6 gap-2">
                  {[
                    { name: 'Blauw', value: '#3B82F6' },
                    { name: 'Groen', value: '#10B981' },
                    { name: 'Oranje', value: '#F59E0B' },
                    { name: 'Rood', value: '#EF4444' },
                    { name: 'Paars', value: '#8B5CF6' },
                    { name: 'Roze', value: '#EC4899' }
                  ].map((color) => (
                    <button
                      key={color.value}
                      onClick={() => setEditForm({...editForm, color: color.value})}
                      className={`w-8 h-8 rounded-full border-2 ${
                        editForm.color === color.value ? 'border-gray-800' : 'border-gray-300'
                      }`}
                      style={{ backgroundColor: color.value }}
                      title={color.name}
                    />
                      ))}
                    </div>
                  </div>
                </div>

            <div className="flex justify-end space-x-3 mt-6">
                  <button
                onClick={handleEditCancel}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                      >
                Annuleren
                      </button>
              <button
                onClick={handleEditSave}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                Opslaan
                  </button>
                </div>
              </div>
        </div>
      )}

      {/* Task list manager modal */}
      {showTaskListManager && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Taaklijsten beheren</h3>
              <button
                onClick={() => setShowTaskListManager(false)}
                className="text-gray-400 hover:text-gray-600 text-xl leading-none"
              >
                ✕
              </button>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              Pas de namen van je lijsten aan of verwijder lijsten die je niet meer nodig hebt.
              De lijst <strong>Klaar</strong> blijft altijd bestaan als archief.
            </p>

            {/* Header row */}
            <div className="flex items-center space-x-2 mb-2 pb-2 border-b border-gray-200">
              <span className="flex-1 text-xs font-medium text-gray-500 uppercase tracking-wide">Naam</span>
              <span className="w-[140px] text-xs font-medium text-gray-500 uppercase tracking-wide" title="Koppel aan een levensbalans categorie voor tijdsanalyse">
                Levensbalans
                <span className="ml-1 text-gray-400 cursor-help">ⓘ</span>
              </span>
              <span className="w-[80px] text-xs font-medium text-gray-500 uppercase tracking-wide text-center">Volgorde</span>
              <span className="w-[80px]"></span>
            </div>

            <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
              {taskLists
                .filter(list => !list.isSystem || list.id === 'done')
                .map((list, index, filteredLists) => (
                <div
                  key={list.id}
                  className="flex items-center space-x-2"
                >
                  <div className="flex flex-col space-y-1 flex-1">
                    <input
                      type="text"
                      value={list.name}
                      onChange={e => {
                        const name = e.target.value;
                        setTaskLists(prev =>
                          prev.map(l => (l.id === list.id ? { ...l, name } : l))
                        );
                      }}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-stone-500"
                      disabled={list.isSystem}
                    />
                    {list.isSystem && (
                      <span className="text-[11px] text-gray-400 italic">
                        systeemlijst
                      </span>
                    )}
                  </div>
                  {/* Categorie dropdown */}
                  {!list.isSystem && (
                    <select
                      value={list.balanceCategory || 'none'}
                      onChange={e => {
                        const balanceCategory = e.target.value as BalanceCategory;
                        setTaskLists(prev =>
                          prev.map(l => (l.id === list.id ? { ...l, balanceCategory } : l))
                        );
                      }}
                      className="text-xs px-2 py-1 border border-gray-300 rounded-md focus:ring-1 focus:ring-stone-500 bg-white"
                      title="Levensbalans categorie"
                    >
                      {Object.entries(balanceCategoryLabels).map(([key, { label, emoji }]) => (
                        <option key={key} value={key}>{emoji} {label}</option>
                      ))}
                    </select>
                  )}
                  <div className="flex flex-col space-y-1">
                    <button
                      onClick={() => moveTaskList(list.id, 'up')}
                      className="text-xs px-2 py-1 rounded-md bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200 disabled:opacity-40"
                      disabled={index === 0}
                      title="Omhoog verplaatsen"
                    >
                      ↑
                    </button>
                    <button
                      onClick={() => moveTaskList(list.id, 'down')}
                      className="text-xs px-2 py-1 rounded-md bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200 disabled:opacity-40"
                      disabled={index === filteredLists.length - 1}
                      title="Omlaag verplaatsen"
                    >
                      ↓
                    </button>
                  </div>
                  {!list.isSystem && (
                    <button
                      onClick={() => deleteTaskList(list.id)}
                      className="text-xs px-2 py-1 rounded-md bg-red-50 text-red-600 hover:bg-red-100 border border-red-200"
                    >
                      Verwijderen
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* Nieuwe lijst toevoegen */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={newListName}
                  onChange={e => setNewListName(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && newListName.trim()) addTaskList();
                  }}
                  className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-stone-500 focus:outline-none"
                  placeholder="Nieuwe lijst..."
                />
                <select
                  value={newListCategory}
                  onChange={e => setNewListCategory(e.target.value as BalanceCategory)}
                  className="text-xs px-2 py-1.5 border border-gray-300 rounded-md focus:ring-1 focus:ring-stone-500 bg-white"
                  title="Levensbalans categorie"
                >
                  {Object.entries(balanceCategoryLabels).map(([key, { label, emoji }]) => (
                    <option key={key} value={key}>{emoji} {label}</option>
                  ))}
                </select>
                <button
                  onClick={addTaskList}
                  disabled={!newListName.trim()}
                  className="text-xs px-3 py-1.5 rounded-md bg-stone-500 text-white hover:bg-[#4F6660] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  +
                </button>
              </div>
            </div>

            <div className="flex justify-end mt-5 space-x-2">
              <button
                onClick={() => setShowTaskListManager(false)}
                className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
              >
                Sluiten
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Event Modal */}
      {showEventModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Nieuw agendapunt</h3>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Categorie</label>
                  <select
                    value={newEventForm.category}
                    onChange={(e) => setNewEventForm({...newEventForm, category: e.target.value as CalendarEvent['category']})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-stone-500"
                  >
                    <option value="work">Werk (zakelijk)</option>
                    <option value="friends">Vrienden</option>
                    <option value="family">Familie</option>
                    <option value="activity">Activiteit</option>
                    <option value="action">Actie</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  {isNetworkingTemplate ? (
                    <input
                      type="text"
                      value="Vrij voor netwerken"
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-700"
                    />
                  ) : (
                    <select
                      value={newEventForm.type}
                      onChange={(e) =>
                        setNewEventForm({
                          ...newEventForm,
                          type: e.target.value as CalendarEvent['type']
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-stone-500"
                    >
                      <option value="google">Google Calendar</option>
                      <option value="planning">Planning</option>
                      <option value="podcast">Podcast</option>
                      <option value="meeting">Meeting</option>
                      <option value="call">Call</option>
                      <option value="personal">Personal</option>
                      <option value="networking">Vrij voor netwerken</option>
                    </select>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Titel *</label>
                <input
                  type="text"
                  value={newEventForm.title}
                  onChange={(e) => setNewEventForm({...newEventForm, title: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-stone-500"
                  placeholder="Afspraak titel"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Beschrijving</label>
                <textarea
                  value={newEventForm.description}
                  onChange={(e) => setNewEventForm({...newEventForm, description: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-stone-500"
                  rows={3}
                  placeholder="Beschrijving van de afspraak"
                />
              </div>
              
                <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Datum</label>
                <input
                  type="date"
                  value={newEventForm.startDate}
                  onChange={(e) => setNewEventForm({...newEventForm, startDate: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-stone-500"
                />
                    </div>
              
              <div className="grid grid-cols-2 gap-4">
                        <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Starttijd</label>
                  <input
                    type="time"
                    value={newEventForm.startTime}
                    onChange={(e) => setNewEventForm({...newEventForm, startTime: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-stone-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Eindtijd</label>
                  <input
                    type="time"
                    value={newEventForm.endTime}
                    onChange={(e) => setNewEventForm({...newEventForm, endTime: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-stone-500"
                  />
                </div>
              </div>

              {/* Herhaling / terugkerende afspraken */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Herhaling</label>
                  <select
                    value={newEventForm.repeat}
                    onChange={(e) => setNewEventForm({ ...newEventForm, repeat: e.target.value as 'none' | 'daily' | 'weekly' | 'monthly' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-stone-500"
                  >
                    <option value="none">Geen</option>
                    <option value="daily">Dagelijks</option>
                    <option value="weekly">Wekelijks</option>
                    <option value="monthly">Maandelijks</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Einddatum herhaling</label>
                  <input
                    type="date"
                    value={newEventForm.repeatEndDate}
                    onChange={(e) => setNewEventForm({ ...newEventForm, repeatEndDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-stone-500"
                    disabled={newEventForm.repeat === 'none'}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Laat leeg voor <strong>doorlopend</strong>.
                  </p>
                </div>
              </div>

              {/* Herinnering instellingen */}
              <div className="grid grid-cols-2 gap-4 items-end">
                <div className="flex items-center space-x-2">
                  <input
                    id="reminder-enabled"
                    type="checkbox"
                    checked={newEventForm.reminderEnabled}
                    onChange={(e) =>
                      setNewEventForm({
                        ...newEventForm,
                        reminderEnabled: e.target.checked
                      })
                    }
                    className="h-4 w-4 text-stone-500 border-gray-300 rounded"
                  />
                  <label htmlFor="reminder-enabled" className="text-sm font-medium text-gray-700">
                    Herinnering aanzetten
                  </label>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Hoelang van tevoren
                  </label>
                  <select
                    value={newEventForm.reminderMinutesBefore}
                    onChange={(e) =>
                      setNewEventForm({
                        ...newEventForm,
                        reminderMinutesBefore: parseInt(e.target.value, 10)
                      })
                    }
                    disabled={!newEventForm.reminderEnabled}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-stone-500 disabled:bg-gray-100"
                  >
                    <option value={5}>5 minuten</option>
                    <option value={15}>15 minuten</option>
                    <option value={30}>30 minuten</option>
                    <option value={60}>1 uur</option>
                    <option value={120}>2 uur</option>
                    <option value={1440}>1 dag</option>
                    <option value={10080}>1 week</option>
                  </select>
                </div>
              </div>

              <p className="text-xs text-gray-500">
                Gebruik <strong>Werk</strong> voor ondernemersafspraken; de anderen zijn voor privé en reviews.
              </p>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Kleur</label>
                <div className="grid grid-cols-6 gap-2">
                  {[
                    { name: 'Blauw', value: '#3B82F6' },
                    { name: 'Groen', value: '#10B981' },
                    { name: 'Oranje', value: '#F59E0B' },
                    { name: 'Rood', value: '#EF4444' },
                    { name: 'Paars', value: '#8B5CF6' },
                    { name: 'Roze', value: '#EC4899' }
                  ].map((color) => (
              <button
                      key={color.value}
                      onClick={() => setNewEventForm({...newEventForm, color: color.value})}
                      className={`w-8 h-8 rounded-full border-2 ${
                        newEventForm.color === color.value ? 'border-gray-800' : 'border-gray-300'
                      }`}
                      style={{ backgroundColor: color.value }}
                      title={color.name}
                    />
                  ))}
                </div>
                </div>
              </div>
              
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={handleNewEventCancel}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
              >
                Annuleren
              </button>
              <button
                onClick={handleNewEventSave}
                className="px-4 py-2 bg-stone-500 text-white rounded-lg hover:bg-[#4F6660] transition-colors"
              >
                Afspraak Maken
              </button>
            </div>
                  </div>
                </div>
      )}

      {/* New Task Modal */}
      {showTaskModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Nieuwe Taak</h3>
                
                <div className="space-y-4">
                    <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Titel *</label>
                <input
                  type="text"
                  value={newTaskForm.title}
                  onChange={(e) => setNewTaskForm({...newTaskForm, title: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-stone-500"
                  placeholder="Taak titel"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Beschrijving</label>
                <textarea
                  value={newTaskForm.description}
                  onChange={(e) => setNewTaskForm({...newTaskForm, description: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-stone-500"
                  rows={3}
                  placeholder="Beschrijving van de taak"
                />
              </div>
                  
                    <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Deadline</label>
                <input
                  type="date"
                  value={newTaskForm.deadline}
                  onChange={(e) => setNewTaskForm({...newTaskForm, deadline: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-stone-500"
                />
            </div>

                    <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Prioriteit</label>
                <select
                  value={newTaskForm.priority}
                  onChange={(e) => setNewTaskForm({...newTaskForm, priority: e.target.value as PlanningTask['priority']})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-stone-500"
                >
                  <option value="low">Laag</option>
                  <option value="medium">Gemiddeld</option>
                  <option value="high">Hoog</option>
                  <option value="urgent">Urgent</option>
                </select>
                  </div>
                </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={handleNewTaskCancel}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
              >
                Annuleren
              </button>
              <button
                onClick={handleNewTaskSave}
                className="px-4 py-2 bg-stone-500 text-white rounded-lg hover:bg-[#4F6660] transition-colors"
              >
                Taak Maken
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Podcast Planning Modal */}
      {showPodcastModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Plan Podcast Sessie</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Titel *</label>
                <input
                  type="text"
                  value={podcastForm.title}
                  onChange={(e) => setPodcastForm({...podcastForm, title: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Podcast titel"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Onderwerp</label>
                  <input
                  type="text"
                  value={podcastForm.topic}
                  onChange={(e) => setPodcastForm({...podcastForm, topic: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Hoofdonderwerp van de podcast"
                  />
                </div>

                <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Beschrijving</label>
                <textarea
                  value={podcastForm.description}
                  onChange={(e) => setPodcastForm({...podcastForm, description: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  rows={3}
                  placeholder="Beschrijving van de podcast sessie"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
              <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Datum *</label>
                <input
                    type="date"
                    value={podcastForm.date}
                    onChange={(e) => {
                      setPodcastForm({...podcastForm, date: e.target.value, time: ''});
                    }}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                      podcastAvailability.blockedDates.includes(podcastForm.date) 
                        ? 'border-red-300 bg-red-50' 
                        : 'border-gray-300'
                    }`}
                    min={new Date().toISOString().split('T')[0]}
                  />
                  {podcastAvailability.blockedDates.includes(podcastForm.date) && (
                    <p className="text-red-500 text-xs mt-1">⚠️ Deze datum is geblokkeerd</p>
                  )}
                  {podcastForm.date && !podcastAvailability.blockedDates.includes(podcastForm.date) && (
                    <p className="text-green-600 text-xs mt-1">✅ Deze datum is beschikbaar</p>
                  )}
              </div>
              <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tijd *</label>
                <select
                    value={podcastForm.time}
                    onChange={(e) => setPodcastForm({...podcastForm, time: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    disabled={!podcastForm.date || podcastAvailability.blockedDates.includes(podcastForm.date)}
                  >
                    <option value="">Selecteer tijd</option>
                    {podcastForm.date && getAvailableTimesForDate(podcastForm.date).map(time => (
                      <option key={time} value={time}>{time}</option>
                  ))}
                </select>
                  {podcastForm.date && !podcastAvailability.blockedDates.includes(podcastForm.date) && (
                    <p className="text-xs text-gray-500 mt-1">
                      {getAvailableTimesForDate(podcastForm.date).length} tijden beschikbaar
                    </p>
                  )}
              </div>
              </div>
              
              {/* Live Availability Display */}
              {podcastForm.date && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Beschikbaarheid voor {new Date(podcastForm.date).toLocaleDateString('nl-NL')}</h4>
                  <div className="grid grid-cols-3 gap-2">
                    {podcastAvailability.availableTimes.map(time => {
                      const isAvailable = isDateTimeAvailable(podcastForm.date, time);
                      return (
                        <div
                          key={time}
                          className={`text-xs p-2 rounded text-center ${
                            isAvailable 
                              ? 'bg-green-100 text-green-700 border border-green-300' 
                              : 'bg-red-100 text-red-700 border border-red-300'
                          }`}
                        >
                          {time}
                          <br />
                          {isAvailable ? '✅' : '❌'}
            </div>
                      );
                    })}
          </div>
        </div>
      )}

              <div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                  <div className="flex items-start space-x-2">
                    <div className="text-blue-500 mt-0.5">ℹ️</div>
                    <div className="text-sm text-blue-700">
                      <p className="font-medium mb-1">Planning Informatie:</p>
                      <p>Houd rekening met aankomst- en vertrektijd. Plan hier maximaal <strong>0,5 uur (30 minuten)</strong> extra voor in.</p>
                      <p className="text-xs mt-1 text-blue-600">Bijvoorbeeld: 1 uur podcast = 1,5 uur totale tijd inclusief voorbereiding</p>
                    </div>
                  </div>
                </div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Duur (minuten)</label>
                <select
                  value={podcastForm.duration}
                  onChange={(e) => setPodcastForm({...podcastForm, duration: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="30">30 minuten</option>
                  <option value="45">45 minuten</option>
                  <option value="60">1 uur</option>
                  <option value="90">1,5 uur</option>
                  <option value="120">2 uur</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Totale tijd inclusief voorbereiding: {parseInt(podcastForm.duration) + 30} minuten
                </p>
              </div>
              
              <div className="border-t pt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Host & Gasten</h4>
                
                {/* Host */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Host *</label>
                <input
                    type="text"
                    value={podcastForm.mainSpeaker}
                    onChange={(e) => setPodcastForm({...podcastForm, mainSpeaker: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="Naam van de host"
                    required
                  />
                </div>

                {/* Gasten */}
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <label className="text-sm font-medium text-gray-700">
                      Gasten (Max 5)
                </label>
                    <button
                      type="button"
                      onClick={addGuest}
                      disabled={podcastForm.guests.length >= 5}
                      className={`px-3 py-1 text-xs rounded ${
                        podcastForm.guests.length >= 5
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                      }`}
                    >
                      + Gast Toevoegen
                    </button>
              </div>
              
                  {podcastForm.guests.length === 0 && (
                    <p className="text-sm text-gray-500 italic">Nog geen gasten toegevoegd</p>
                  )}
                  
                  <div className="space-y-3">
                    {podcastForm.guests.map((guest, index) => (
                      <div key={index} className="bg-gray-50 rounded-lg p-3">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm font-medium text-gray-700">Gast {index + 1}</span>
                          <button
                            type="button"
                            onClick={() => removeGuest(index)}
                            className="text-red-500 hover:text-red-700 text-sm"
                          >
                            Verwijderen
                          </button>
                        </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Naam</label>
                    <input
                              type="text"
                              value={guest.name}
                              onChange={(e) => updateGuest(index, 'name', e.target.value)}
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-purple-500"
                              placeholder="Gast naam"
                    />
                  </div>
                  <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
                    <input
                              type="email"
                              value={guest.email}
                              onChange={(e) => updateGuest(index, 'email', e.target.value)}
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-purple-500"
                              placeholder="email@gast.com"
                    />
                  </div>
                </div>
                      </div>
                    ))}
                  </div>
                  
                  {podcastForm.guests.length >= 5 && (
                    <p className="text-xs text-gray-500 mt-2">Maximum aantal gasten bereikt (5)</p>
                  )}
                </div>
              </div>
            </div>

            {/* Simple Admin Controls */}
            {podcastAvailability.adminMode && (
              <div className="border-t pt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Admin Tools - Simpel & Snel</h4>
                
                {/* Operation Mode Selection */}
                <div className="mb-4">
                  <h5 className="text-xs font-medium text-gray-600 mb-2">Wat wil je doen?</h5>
                  <div className="grid grid-cols-3 gap-2">
              <button
                      onClick={() => setAdminCalendarView('month')}
                      className={`px-3 py-2 text-xs rounded font-medium ${
                        adminCalendarView === 'month' 
                          ? 'bg-purple-500 text-white' 
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      📅 Maand
              </button>
              <button
                      onClick={() => setAdminCalendarView('week')}
                      className={`px-3 py-2 text-xs rounded font-medium ${
                        adminCalendarView === 'week' 
                          ? 'bg-purple-500 text-white' 
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      📆 Week
                    </button>
                    <button
                      onClick={() => {/* Single day mode */}}
                      className="px-3 py-2 text-xs rounded font-medium bg-gray-100 text-gray-700 hover:bg-gray-200"
                    >
                      📍 Dag
                    </button>
                  </div>
                </div>

                {/* Simple Action Buttons */}
                <div className="space-y-3">
                  <div>
                    <h5 className="text-xs font-medium text-gray-600 mb-2">Snelle Acties:</h5>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => {
                          const today = new Date().toISOString().split('T')[0];
                          blockDate(today);
                        }}
                        className="px-3 py-2 bg-red-100 text-red-700 text-xs rounded hover:bg-red-200 font-medium"
                      >
                        🚫 Blokkeer Vandaag
                      </button>
                      <button
                        onClick={() => {
                          const today = new Date().toISOString().split('T')[0];
                          unblockDate(today);
                        }}
                        className="px-3 py-2 bg-green-100 text-green-700 text-xs rounded hover:bg-green-200 font-medium"
                      >
                        ✅ Open Vandaag
                      </button>
                    </div>
                  </div>

                  <div>
                    <h5 className="text-xs font-medium text-gray-600 mb-2">Week Acties:</h5>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => blockWeek(adminCurrentDate.toISOString().split('T')[0])}
                        className="px-3 py-2 bg-red-100 text-red-700 text-xs rounded hover:bg-red-200 font-medium"
                      >
                        🚫 Blokkeer Hele Week
                      </button>
                      <button
                        onClick={() => unblockWeek(adminCurrentDate.toISOString().split('T')[0])}
                        className="px-3 py-2 bg-green-100 text-green-700 text-xs rounded hover:bg-green-200 font-medium"
                      >
                        ✅ Open Hele Week
                      </button>
                    </div>
                  </div>

                  <div>
                    <h5 className="text-xs font-medium text-gray-600 mb-2">Maand Acties:</h5>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => {
                          const startOfMonth = new Date(adminCurrentDate.getFullYear(), adminCurrentDate.getMonth(), 1);
                          const endOfMonth = new Date(adminCurrentDate.getFullYear(), adminCurrentDate.getMonth() + 1, 0);
                          const monthDates: string[] = [];
                          
                          for (let d = new Date(startOfMonth); d <= endOfMonth; d.setDate(d.getDate() + 1)) {
                            monthDates.push(d.toISOString().split('T')[0]);
                          }
                          
                          setPodcastAvailability(prev => ({
                            ...prev,
                            blockedDates: [...prev.blockedDates, ...monthDates]
                          }));
                        }}
                        className="px-3 py-2 bg-red-100 text-red-700 text-xs rounded hover:bg-red-200 font-medium"
                      >
                        🚫 Blokkeer Hele Maand
                      </button>
                      <button
                        onClick={() => {
                          const startOfMonth = new Date(adminCurrentDate.getFullYear(), adminCurrentDate.getMonth(), 1);
                          const endOfMonth = new Date(adminCurrentDate.getFullYear(), adminCurrentDate.getMonth() + 1, 0);
                          const monthDates: string[] = [];
                          
                          for (let d = new Date(startOfMonth); d <= endOfMonth; d.setDate(d.getDate() + 1)) {
                            monthDates.push(d.toISOString().split('T')[0]);
                          }
                          
                          setPodcastAvailability(prev => ({
                            ...prev,
                            blockedDates: prev.blockedDates.filter(date => !monthDates.includes(date))
                          }));
                        }}
                        className="px-3 py-2 bg-green-100 text-green-700 text-xs rounded hover:bg-green-200 font-medium"
                      >
                        ✅ Open Hele Maand
                      </button>
                    </div>
                  </div>
                </div>

                {/* Manual Date Input */}
                <div className="mt-4 pt-3 border-t">
                  <h5 className="text-xs font-medium text-gray-600 mb-2">Specifieke Datum:</h5>
                  <div className="flex space-x-2">
                    <input
                      type="date"
                      id="manualDate"
                      className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded"
                    />
                    <button
                      onClick={() => {
                        const input = document.getElementById('manualDate') as HTMLInputElement;
                        if (input.value) {
                          blockDate(input.value);
                          input.value = '';
                        }
                      }}
                      className="px-3 py-1 bg-red-100 text-red-700 text-xs rounded hover:bg-red-200 font-medium"
                    >
                      🚫 Blokkeer
                    </button>
                    <button
                      onClick={() => {
                        const input = document.getElementById('manualDate') as HTMLInputElement;
                        if (input.value) {
                          unblockDate(input.value);
                          input.value = '';
                        }
                      }}
                      className="px-3 py-1 bg-green-100 text-green-700 text-xs rounded hover:bg-green-200 font-medium"
                    >
                      ✅ Open
              </button>
            </div>
          </div>
        </div>
            )}

            <div className="flex justify-between items-center mt-6">
              <div className="flex items-center space-x-2">
                <button
                  onClick={toggleAdminMode}
                  className={`px-3 py-1 text-xs rounded-full ${
                    podcastAvailability.adminMode 
                      ? 'bg-red-100 text-red-700 border border-red-300' 
                      : 'bg-gray-100 text-gray-700 border border-gray-300'
                  }`}
                >
                  {podcastAvailability.adminMode ? 'Admin Mode' : 'Admin'}
                </button>
                {podcastAvailability.adminMode && (
                  <span className="text-xs text-gray-500">Beheer beschikbaarheid</span>
      )}
        </div>
              <div className="flex space-x-3">
                <button
                  onClick={handlePodcastCancel}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                >
                  Annuleren
                </button>
                <button
                  onClick={submitPodcastRequest}
                  className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors"
                >
                  Aanvraag Indienen
                </button>
      </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Reschedule Modal */}
      {showRescheduleModal && rescheduleEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">📅 Meeting Verzetten</h3>
              <button
                onClick={() => {
                  setShowRescheduleModal(false);
                  setRescheduleEvent(null);
                  setRescheduleForm({ newStartDate: '', newStartTime: '', newEndTime: '', reason: '' });
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nieuwe Datum
                </label>
                <input
                  type="date"
                  value={rescheduleForm.newStartDate}
                  onChange={(e) => setRescheduleForm(prev => ({ ...prev, newStartDate: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stone-500 focus:border-stone-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Starttijd
                  </label>
                  <input
                    type="time"
                    value={rescheduleForm.newStartTime}
                    onChange={(e) => setRescheduleForm(prev => ({ ...prev, newStartTime: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stone-500 focus:border-stone-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Eindtijd
                  </label>
                  <input
                    type="time"
                    value={rescheduleForm.newEndTime}
                    onChange={(e) => setRescheduleForm(prev => ({ ...prev, newEndTime: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stone-500 focus:border-stone-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reden voor wijziging
                </label>
                <textarea
                  value={rescheduleForm.reason}
                  onChange={(e) => setRescheduleForm(prev => ({ ...prev, reason: e.target.value }))}
                  placeholder="Leg uit waarom je de meeting wilt verzetten..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stone-500 focus:border-stone-500"
                />
              </div>

              <div className="flex space-x-3 pt-2">
                <button
                  onClick={async () => {
                    if (!rescheduleForm.newStartDate || !rescheduleForm.newStartTime || !rescheduleForm.newEndTime) {
                      showToast('⚠️ Vul alle velden in', 'error');
                      return;
                    }

                    try {
                      if (!rescheduleEvent) {
                        showToast('❌ Geen afspraak geselecteerd', 'error');
                        return;
                      }
                      const newStartDateTime = new Date(`${rescheduleForm.newStartDate}T${rescheduleForm.newStartTime}`);
                      const newEndDateTime = new Date(`${rescheduleForm.newStartDate}T${rescheduleForm.newEndTime}`);
                      
                      // Get other participant
                      const otherUserId = rescheduleEvent.participants.find((p: string) => p !== userProfile?.uid);
                      if (!otherUserId) {
                        showToast('❌ Kon andere deelnemer niet vinden', 'error');
                        return;
                      }

                      // Create change request ID
                      const changeRequestId = `change_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

                      // Update event with pending change request
                      await updateDoc(doc(db, 'calendarEvents', rescheduleEvent.id), {
                        pendingChangeRequest: {
                          id: changeRequestId,
                          fromUserId: userProfile?.uid,
                          newStartTime: newStartDateTime,
                          newEndTime: newEndDateTime,
                          reason: rescheduleForm.reason || 'Geen reden opgegeven',
                          status: 'pending'
                        },
                        updatedAt: new Date()
                      });

                      // Create inbox notification for other participant
                      await addDoc(collection(db, 'inboxItems'), {
                        userId: otherUserId,
                        type: 'notification',
                        category: 'meeting',
                        title: '📅 Meeting wijzigingsverzoek',
                        message: `${userProfile?.displayName || 'Iemand'} wil de meeting "${rescheduleEvent.title}" verzetten naar ${newStartDateTime.toLocaleDateString('nl-NL')} om ${newStartDateTime.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}.`,
                        isRead: false,
                        priority: 'high',
                        createdAt: new Date(),
                        actionData: {
                          actionType: 'accept_invitation',
                          meetingId: rescheduleEvent.id,
                          changeRequestId: changeRequestId
                        },
                        metadata: {
                          eventDate: newStartDateTime,
                          senderName: userProfile?.displayName || '',
                          reason: rescheduleForm.reason || 'Geen reden opgegeven',
                          oldStartTime: rescheduleEvent.startTime,
                          newStartTime: newStartDateTime
                        }
                      });

                      showToast('✅ Wijzigingsverzoek verzonden!', 'success');
                      setShowRescheduleModal(false);
                      setRescheduleEvent(null);
                      setRescheduleForm({ newStartDate: '', newStartTime: '', newEndTime: '', reason: '' });
                    } catch (error) {
                      console.error('Error sending change request:', error);
                      showToast('❌ Fout bij verzenden wijzigingsverzoek', 'error');
                    }
                  }}
                  className="flex-1 px-4 py-2 bg-stone-500 text-white rounded-lg hover:bg-[#4F6660] transition-colors font-medium"
                >
                  📤 Verzenden
                </button>
                <button
                  onClick={() => {
                    setShowRescheduleModal(false);
                    setRescheduleEvent(null);
                    setRescheduleForm({ newStartDate: '', newStartTime: '', newEndTime: '', reason: '' });
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Annuleren
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Recurring Event Modal */}
      {showDeleteModal && eventToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">🗑️ Afspraak Verwijderen</h3>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setEventToDelete(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              {eventToDelete?.recurring ? (
                <>
                  <p className="text-gray-700">
                    Dit is een herhalende afspraak. Wat wil je verwijderen?
                  </p>
                  <div className="space-y-3">
                    <button
                      type="button"
                      onClick={async (e) => {
                        e.preventDefault();
                        if (eventToDelete) {
                          await deleteEvent(eventToDelete.id, 'single', eventToDelete.startTime);
                          setShowDeleteModal(false);
                          setEventToDelete(null);
                        }
                      }}
                      className="w-full px-4 py-3 bg-stone-500 text-white rounded-lg hover:bg-[#4F6660] transition-colors font-medium text-left"
                    >
                      <div className="font-semibold">Alleen deze afspraak</div>
                      <div className="text-sm opacity-90">Verwijdert alleen dit specifieke moment</div>
                    </button>
                    <button
                      type="button"
                      onClick={async (e) => {
                        e.preventDefault();
                        if (eventToDelete) {
                          await deleteEvent(eventToDelete.id, 'series');
                          setShowDeleteModal(false);
                          setEventToDelete(null);
                        }
                      }}
                      className="w-full px-4 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors font-medium text-left"
                    >
                      <div className="font-semibold">Hele reeks verwijderen</div>
                      <div className="text-sm opacity-90">Verwijdert alle herhaalde afspraken</div>
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-gray-700">
                    Weet je zeker dat je <strong>{eventToDelete?.title}</strong> wilt verwijderen?
                  </p>
                  <button
                    type="button"
                    onClick={async (e) => {
                      e.preventDefault();
                      if (eventToDelete) {
                        await deleteEvent(eventToDelete.id, 'series');
                        setShowDeleteModal(false);
                        setEventToDelete(null);
                      }
                    }}
                    className="w-full px-4 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors font-medium"
                  >
                    Afspraak verwijderen
                  </button>
                </>
              )}

              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  setShowDeleteModal(false);
                  setEventToDelete(null);
                }}
                className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Annuleren
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {renderToastNotification()}

      {/* Loading Overlay */}
      {(loading.events || loading.planningTasks || loading.agendaTasks || loading.meetingRequests || loading.podcastSessions) && (
        <div className="fixed inset-0 bg-black bg-opacity-20 flex items-center justify-center z-40">
          <div className="bg-white rounded-lg p-6 flex flex-col items-center space-y-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-stone-500"></div>
            <p className="text-gray-700">Laden...</p>
          </div>
        </div>
      )}

      {/* Scroll Indicator */}
      <ScrollIndicator />

      {/* Auto-Sync Settings Modal */}
      {showAutoSyncModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-gray-900">Auto-Sync Instellingen</h3>
              <button
                onClick={() => setShowAutoSyncModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              {/* Enable/Disable Toggle */}
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">Auto-Sync Inschakelen</label>
                <button
                  onClick={() => setAutoSyncEnabled(!autoSyncEnabled)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    autoSyncEnabled ? 'bg-green-500' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      autoSyncEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {autoSyncEnabled && (
                <>
                  {/* Frequency Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Frequentie</label>
                    <div className="space-y-2">
                      {[
                        { value: 'daily' as const, label: 'Dagelijks', description: 'Elke dag om 02:00 uur' },
                        { value: 'weekly' as const, label: 'Wekelijks', description: 'Elke week op maandag' },
                        { value: 'monthly' as const, label: 'Maandelijks', description: 'Elke maand op de 1e' },
                      ].map((freq) => (
                        <button
                          key={freq.value}
                          onClick={() => setAutoSyncFrequency(freq.value)}
                          className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all ${
                            autoSyncFrequency === freq.value
                              ? 'border-stone-500 bg-stone-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="font-medium text-gray-900">{freq.label}</div>
                              <div className="text-sm text-gray-500">{freq.description}</div>
                            </div>
                            {autoSyncFrequency === freq.value && (
                              <svg className="w-5 h-5 text-stone-500" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Calendar Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Calendars om te synchroniseren
                    </label>
                    <div className="space-y-2">
                      {/* Google Calendar */}
                      {googleCalendarStatus.connected && (
                        <label className="flex items-center space-x-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={autoSyncCalendars.includes('google')}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setAutoSyncCalendars([...autoSyncCalendars, 'google']);
                              } else {
                                setAutoSyncCalendars(autoSyncCalendars.filter((c) => c !== 'google'));
                              }
                            }}
                            className="h-4 w-4 text-stone-500 focus:ring-stone-500 border-gray-300 rounded"
                          />
                          <span className="flex-1 text-sm font-medium text-gray-700">Google Calendar</span>
                        </label>
                      )}

                      {/* Outlook Calendar */}
                      {outlookCalendarStatus.connected && (
                        <label className="flex items-center space-x-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={autoSyncCalendars.includes('outlook')}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setAutoSyncCalendars([...autoSyncCalendars, 'outlook']);
                              } else {
                                setAutoSyncCalendars(autoSyncCalendars.filter((c) => c !== 'outlook'));
                              }
                            }}
                            className="h-4 w-4 text-stone-500 focus:ring-stone-500 border-gray-300 rounded"
                          />
                          <span className="flex-1 text-sm font-medium text-gray-700">Outlook Calendar</span>
                        </label>
                      )}

                      {/* Apple Calendar */}
                      {appleCalendarStatus.connected && (
                        <label className="flex items-center space-x-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={autoSyncCalendars.includes('apple')}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setAutoSyncCalendars([...autoSyncCalendars, 'apple']);
                              } else {
                                setAutoSyncCalendars(autoSyncCalendars.filter((c) => c !== 'apple'));
                              }
                            }}
                            className="h-4 w-4 text-stone-500 focus:ring-stone-500 border-gray-300 rounded"
                          />
                          <span className="flex-1 text-sm font-medium text-gray-700">Apple Calendar</span>
                        </label>
                      )}
                    </div>

                    {autoSyncCalendars.length === 0 && (
                      <p className="text-sm text-red-600 mt-2">⚠️ Selecteer minimaal één calendar</p>
                    )}
                  </div>
                </>
              )}

              {/* Action Buttons */}
              <div className="flex space-x-3 pt-4">
                <button
                  onClick={() => setShowAutoSyncModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Annuleren
                </button>
                <button
                  onClick={handleSaveAutoSyncPreferences}
                  disabled={autoSyncEnabled && autoSyncCalendars.length === 0}
                  className={`flex-1 px-4 py-2 rounded-lg text-white ${
                    autoSyncEnabled && autoSyncCalendars.length === 0
                      ? 'bg-gray-300 cursor-not-allowed'
                      : 'bg-stone-500 hover:bg-[#4F6660]'
                  }`}
                >
                  Opslaan
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    </div>
  );
};

export default AgendaPage;